import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plus, Users, Trash2, Check, X, 
  ArrowDownLeft, ArrowUpRight, Receipt, Utensils, Car, 
  Home, Dumbbell, MoreHorizontal, UserPlus, RefreshCw, Play, Pause, Calendar, BarChart3,
  Pencil, Link2, Copy, Share2, MessageCircle, Send, Wine, ShoppingBag,
  Stethoscope, Zap, CalendarDays, Wallet, AlertTriangle, Settings,
  ClipboardCheck, UserCheck, UserX, CheckSquare, Square, BookOpen, ScrollText, BellRing, Download, Bot, ChevronRight, FileCheck, Flag, Trophy
} from 'lucide-react';
import PlayTab from './tabs/PlayTab';
import GroupLogoUploader from '../components/GroupLogoUploader';
import CourtModeSheet from './../components/play/CourtModeSheet';
import { isPlayEnabled, canScore as canScorePlay } from '../lib/play';
import { toast } from 'sonner';
import { runWithUndo } from '../lib/undo';
import { format } from 'date-fns';
import api from '../lib/api';
import { sendSystemWaInvite } from '../lib/systemWaInvite';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../context/RealtimeContext';
import CommentThread from '../components/CommentThread';
import NeoPayBadge from '../components/NeoPayBadge';
import AIChatPanel from '../components/AIChatPanel';
import NXCIcon from '../components/NXCIcon';
import FineExplainerPopover from '../components/FineExplainerPopover';
import PairLedgerSheet from '../components/PairLedgerSheet';
import VenueManagerDialog from '../components/VenueManagerDialog';
import PaymentRotationDialog from '../components/PaymentRotationDialog';
import ContributionRequestDialog from '../components/ContributionRequestDialog';
import CashOutRequestDialog from '../components/CashOutRequestDialog';

// Tab components — extracted to keep the page maintainable (iter90 refactor phase 1)
import RecurringTab from './tabs/RecurringTab';
import BalancesTab from './tabs/BalancesTab';
import MembersTab from './tabs/MembersTab';
// Phase 2 (iter91) — Expenses, Attendance, Fund tabs extracted
import ExpensesTab from './tabs/ExpensesTab';
import AttendanceTab from './tabs/AttendanceTab';
import FundTab from './tabs/FundTab';
// Wave 2 (iter95) — Information architecture: 7 tabs collapsed to 3 sections
import OverviewTab from './tabs/OverviewTab';
import LedgerTab from './tabs/LedgerTab';
import PeopleTab from './tabs/PeopleTab';
import { categoryIcons, categoryColors } from './tabs/categoryMaps';
import Avatar from '../components/ui/avatar-initials';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../components/ui/drawer';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import EmptyState from '../components/EmptyState';
import {
  EmptyExpenses,
  EmptyRecurring,
  EmptySessions,
  EmptyFund,
  AllSettled,
  EmptyComments,
} from '../components/illustrations';
import StatusBadge from '../components/ui/status-badge';
import useDraftAutoSave from '../hooks/useDraftAutoSave';
import useGroupPrefetch from '../hooks/useGroupPrefetch';
import useSyncReload from '../hooks/useSyncReload';
import useSilentGuestMatches from '../hooks/useSilentGuestMatches';
import { formatNXC, formatCoins, CURRENCY_SYMBOLS } from '../lib/formatNXC';
import GroupContextHeader from '../components/GroupContextHeader';
import GroupEmojiPicker from '../components/GroupEmojiPicker';
import PendingTreasuryBanner from '../components/PendingTreasuryBanner';
import ShareInviteSheet from '../components/ShareInviteSheet';
import useGroupInvite from '../lib/useGroupInvite';
import FundHeroCard from '../components/FundHeroCard';
import AdminActionsFab from '../components/AdminActionsFab';
import MembershipFeeDialog from '../components/MembershipFeeDialog';
import PrimaryActionBar from '../components/PrimaryActionBar';
import SetTripRateModal from '../components/SetTripRateModal';
import EndTripModal from '../components/EndTripModal';
import ArchivedTripBanner from '../components/ArchivedTripBanner';
import GuestLinkSheet from '../components/GuestLinkSheet';
import AddGuestModal from '../components/AddGuestModal';
import SilentGuestMatchesBanner from '../components/SilentGuestMatchesBanner';
import AIExpenseCapture from '../components/AIExpenseCapture';
import useGroupTypeConfig from '../hooks/useGroupTypeConfig';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// iter311 — <input type="datetime-local"> <-> ISO helpers (local timezone).
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Tiny pulsing primary-tinted dot used on tab labels for ~3s after a relevant SSE event
// lands. Pure CSS; no setInterval. Positioned absolute-top-right of the tab trigger.
const LiveDot = ({ testId }) => (
  <span
    aria-hidden="true"
    data-testid={testId}
    className="absolute top-0.5 right-1 sm:top-1 sm:right-1.5 flex h-2 w-2"
  >
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
  </span>
);

// iter166 — Sessions tab live badge.
//   - "Live now" (red, pulsing) if any session is currently between starts_at and ends_at.
//   - "Closes in Xh" / "Xm" (amber) if any session has status='rsvp_open' and closes within 2h.
//   - null otherwise.
// Computed from the local `sessions` array; no extra fetches. Sessions are
// kept fresh by SSE + fetchSessions on group view, so the badge auto-refreshes.
function deriveSessionsBadge(sessions, nowMs) {
  if (!Array.isArray(sessions) || sessions.length === 0) return null;
  const TWO_HOURS = 2 * 60 * 60 * 1000;
  // 1) Anything currently live wins.
  for (const s of sessions) {
    if (s.status === 'cancelled' || s.status === 'completed') continue;
    const sa = s.starts_at ? new Date(s.starts_at).getTime() : NaN;
    const ea = s.ends_at ? new Date(s.ends_at).getTime() : NaN;
    if (Number.isFinite(sa) && Number.isFinite(ea) && nowMs >= sa && nowMs <= ea) {
      return { kind: 'live', label: 'Live now' };
    }
  }
  // 2) Earliest "RSVP closing in <=2h".
  let bestMs = Infinity;
  for (const s of sessions) {
    if (s.status !== 'rsvp_open' || !s.rsvp_closes_at) continue;
    const t = new Date(s.rsvp_closes_at).getTime();
    if (!Number.isFinite(t)) continue;
    const diff = t - nowMs;
    if (diff > 0 && diff <= TWO_HOURS && diff < bestMs) bestMs = diff;
  }
  if (Number.isFinite(bestMs)) {
    const mins = Math.round(bestMs / 60000);
    const label = mins >= 60 ? `Closes in ${Math.round(mins / 60)}h` : `Closes in ${Math.max(1, mins)}m`;
    return { kind: 'closing', label };
  }
  return null;
}

// Visual badge for the Sessions tab trigger. Wraps text + dot.
const SessionsBadge = ({ badge }) => {
  if (!badge) return null;
  const isLive = badge.kind === 'live';
  return (
    <span
      data-testid={`sessions-tab-badge-${badge.kind}`}
      className={`hidden sm:inline-flex ml-1.5 items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-bold tracking-tight ${
        isLive
          ? 'bg-red-500/15 text-red-600 dark:text-red-400'
          : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
      }`}
    >
      <span className={`relative flex h-1.5 w-1.5 ${isLive ? '' : ''}`}>
        {isLive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />}
        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${isLive ? 'bg-red-500' : 'bg-amber-500'}`} />
      </span>
      {badge.label}
    </span>
  );
};

export default function GroupDetailPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { onEvent } = useRealtime();

  // iter201 — Offline-first: warm the SW cache with expenses, fund txns,
  // sessions and members for THIS group as soon as the page mounts.
  // Subsequent reads (incl. offline) hit cache.
  useGroupPrefetch(groupId);

  const [group, setGroup] = useState(null);
  // iter189 — derive type-aware config once per render; null-safe (returns
  // safe defaults when group hasn't loaded yet).
  const groupTypeConfig = useGroupTypeConfig(group);
  const [recurringExpenses, setRecurringExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  // Read deep-link params synchronously on first render (?tab=sessions&focus={id}).
  // Use React Router's location.search (not window.location) so the value is fresh on first mount.
  const _initialQuery = (() => {
    const p = new URLSearchParams(location.search || '');
    return {
      tab: p.get('tab'),
      filter: p.get('filter'),
      focus: p.get('focus'),
    };
  })();
  const [activeTab, setActiveTab] = useState(
    ['overview', 'ledger', 'sessions', 'people', 'play'].includes(_initialQuery.tab) ? _initialQuery.tab : 'overview'
  );
  const playEnabled = isPlayEnabled(group);
  const [peopleInitialFilter, setPeopleInitialFilter] = useState(_initialQuery.filter || null);
  const [focusedSessionId, setFocusedSessionId] = useState(_initialQuery.focus || null);

  // After we've consumed the deep-link params, auto-clear the focus highlight
  // after a short window. We intentionally do NOT clear the URL — React
  // StrictMode's double-mount would lose the initial state if we did.
  useEffect(() => {
    if (_initialQuery.focus) {
      const t = setTimeout(() => setFocusedSessionId(null), 4000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter116 — re-sync deep-link state when location.search CHANGES after
  // mount. Without this, clicking a Sessions Rail card while already on the
  // group page only updates the URL — activeTab / peopleInitialFilter /
  // focusedSessionId remain stuck at whatever the first render set them to,
  // so the user "doesn't go anywhere".
  useEffect(() => {
    const p = new URLSearchParams(location.search || '');
    const t = p.get('tab');
    const f = p.get('filter');
    const fo = p.get('focus');
    if (['overview', 'ledger', 'sessions', 'people'].includes(t)) {
      setActiveTab(t);
    }
    if (f) setPeopleInitialFilter(f);
    if (fo) {
      setFocusedSessionId(fo);
      // Re-arm the 4s auto-clear so the highlight reappears on the new focus.
      const timer = setTimeout(() => setFocusedSessionId(null), 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.search]);
  // Live-activity ping per tab — { expenses: ts, fund: ts, attendance: ts, recurring: ts }
  // A tab gets a pulsing dot for 3s after a relevant SSE event lands.
  const [liveActivity, setLiveActivity] = useState({});
  const pingTab = useCallback((tabKey) => {
    if (activeTab === tabKey) return; // never show the dot on the tab the user is currently on
    setLiveActivity((cur) => ({ ...cur, [tabKey]: Date.now() }));
    setTimeout(() => {
      setLiveActivity((cur) => {
        // Only clear if no newer ping arrived in the meantime
        if (!cur[tabKey] || Date.now() - cur[tabKey] >= 2900) {
          const { [tabKey]: _, ...rest } = cur;
          return rest;
        }
        return cur;
      });
    }, 3100);
  }, [activeTab]);

  // iter166 — Tick every 60s so "Closes in Xm" labels stay fresh without
  // requiring a session refetch. Also re-triggers "Live now" detection when
  // the start_at moment passes. Cheap; only re-renders once per minute.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  
  // Modal states
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAdminSheet, setShowAdminSheet] = useState(false); // iter216 — replaces the "+" FAB with a toolbar-triggered sheet  // iter191 — Trip multi-currency: lazy-open the SetTripRateModal.
  const [showSetTripRate, setShowSetTripRate] = useState(false);
  const [courtModeSessionId, setCourtModeSessionId] = useState(null);
  // iter193 — Trip lifecycle: end-trip modal.
  const [showEndTrip, setShowEndTrip] = useState(false);
  const [showTripViewLinkSheet, setShowTripViewLinkSheet] = useState(false);
  const [addGuestSessionId, setAddGuestSessionId] = useState(null);
  const [addGuestSessionTitle, setAddGuestSessionTitle] = useState('');
  // iter182 — Bulk Add modal
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [bulkData, setBulkData] = useState({
    category: 'transport',
    payment_mode: 'direct', // 'direct' | 'single'
    payer: '',
    reimburse_from_fund: false,
    advance_from_fund: false,
    shared_description: '',
    rows: [], // [{user_id, amount, description, included}]
  });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  // iter255 — bulk add: quick-pick registered users + comma-separated numbers
  const [quickPickUsers, setQuickPickUsers] = useState([]);
  const [selectedQuickIds, setSelectedQuickIds] = useState(new Set());
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showEditExpense, setShowEditExpense] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showComments, setShowComments] = useState(null); // expense id
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showFundModal, setShowFundModal] = useState(false);
  const [showFineModal, setShowFineModal] = useState(false);
  const [fundData, setFundData] = useState(null);
  const [fundSnapshot, setFundSnapshot] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [fundAmount, setFundAmount] = useState('');
  const [fundNote, setFundNote] = useState('');
  const [fundContributorId, setFundContributorId] = useState('split'); // 'split' | 'sponsor' | <user_id>
  const [fundStatus, setFundStatus] = useState('received'); // 'received' | 'pledged'
  // iter191 — Trip multi-currency: which currency the top-up is being paid in.
  // Seeded on modal-open from group default + localStorage last-used.
  const [fundEntryCurrency, setFundEntryCurrency] = useState('');
  // Stage-2 Loan support — only applies when contributor is a single member (not split / sponsor)
  // and status='received'. Tags the contribution as a loan with optional due date.
  const [fundIsLoan, setFundIsLoan] = useState(false);
  const [fundLoanDueDate, setFundLoanDueDate] = useState('');
  // Group-to-member loan modal
  const [showLoanToMemberModal, setShowLoanToMemberModal] = useState(false);
  const [loanToMemberForm, setLoanToMemberForm] = useState({ member_id: '', amount: '', expected_repayment_date: '', note: '' });
  // Guest contribution modal state
  const [showGuestContribModal, setShowGuestContribModal] = useState(false);
  const [guestContribForm, setGuestContribForm] = useState({ guest_name: '', amount: '', note: '', status: 'received' });
  const [fineData, setFineData] = useState({ user_id: '', amount: '', fine_type: 'late', reason: '' });
  const [showFineDefaultsModal, setShowFineDefaultsModal] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [fineDefaults, setFineDefaults] = useState({ late_10: 100, late_above_10: 200, cancellation: 300, no_show: 500, late_threshold_minutes: 10, session_fee: 0, late_cancel_hours: 15, late_cancel: 300 });
  const [balancePolicy, setBalancePolicy] = useState({ allow_negative: true, min_balance: null, warning_threshold: -500, alert_method: 'in_app' });
  const [showBalancePolicyModal, setShowBalancePolicyModal] = useState(false);
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  const [bulkReversing, setBulkReversing] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [showAllotCoins, setShowAllotCoins] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  // 'send' = current user pays recipient · 'request_peer' = ask peer to pay me · 'request_fund' = ask fund to reimburse me
  const [transferMode, setTransferMode] = useState('send');
  const [transferData, setTransferData] = useState({ to_user_id: '', amount: '', note: '' });
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [expandedTransferId, setExpandedTransferId] = useState(null);
  const [showQuickSettle, setShowQuickSettle] = useState(false);
  const [quickSettle, setQuickSettle] = useState(null);
  const [loadingQuickSettle, setLoadingQuickSettle] = useState(false);
  const [allotData, setAllotData] = useState({ user_id: '', amount: '', note: '' });
  const [sessions, setSessions] = useState([]);
  const [importPreview, setImportPreview] = useState(null); // { preview_id, mapping, summary, members, warning, fund_before, fund_after }
  const [importLoading, setImportLoading] = useState(false); // 'preview' | 'confirm' | false
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [newSession, setNewSession] = useState({
    title: '',
    date: '',
    num_courts: 2,
    start_time: '07:30',
    rsvp_enabled: true,
    rsvp_preset: 'open_now_close_1h_before', // morning-friendly default
    rsvp_opens_at: '',
    rsvp_closes_at: '',
  });
  const [activeSession, setActiveSession] = useState(null);
  const [presentIds, setPresentIds] = useState([]);
  const [lateUnderIds, setLateUnderIds] = useState([]); // iter48: late ≤threshold
  const [lateAboveIds, setLateAboveIds] = useState([]); // iter48: late >threshold
  const [lateCancelIds, setLateCancelIds] = useState([]); // iter210: late cancel (₹300, not no-show)
  // iter209 — waitlist promotion / demotion dialog state
  const [attendanceTouched, setAttendanceTouched] = useState(false);
  const [replacementBusy, setReplacementBusy] = useState(null);
  const [replacementGuest, setReplacementGuest] = useState('');
  const [promotePrompt, setPromotePrompt] = useState(null); // {absentee_id, absentee_name, waitlist_user_id, waitlist_user_name}
  const [demotePrompt, setDemotePrompt] = useState(null);   // {promoted_user_id, promoted_user_name, absentee_name}
  const [promoting, setPromoting] = useState(false);

  // Member ledger + Audit logs drawers (P1.4 + P1.5)
  const [ledgerForMember, setLedgerForMember] = useState(null); // {id, name} or null
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Session defaults (iter35) + Duplicate
  const [sessionDefaults, setSessionDefaults] = useState({
    start_time: '07:30', num_courts: 2, days_of_week: [], title_template: 'Pickle — {day}', auto_create_enabled: false, rsvp_open_hours_before: 12, rsvp_close_hours_before: 1, duration_minutes: 120, live_indicator_style: 'red_pulse',
  });
  const [showSessionDefaults, setShowSessionDefaults] = useState(false);
  const [savingSessionDefaults, setSavingSessionDefaults] = useState(false);
  // iter226 — Venue library dialog
  const [showVenueManager, setShowVenueManager] = useState(false);
  // iter228 — Payment rotation dialog
  const [showPaymentRotation, setShowPaymentRotation] = useState(false);
  // iter231 — Contribution Request dialog (Model 1 — non-super-admins route via Kalpesh)
  const [showContribRequest, setShowContribRequest] = useState(false);
  const [showCashOutRequest, setShowCashOutRequest] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null); // session object to clone
  const [duplicateDate, setDuplicateDate] = useState('');
  const [editSession, setEditSession] = useState(null); // session to edit
  const [editForm, setEditForm] = useState({ title: '', date: '', start_time: '', num_courts: 1, max_players: 6, min_players: 5 });
  const [editBusy, setEditBusy] = useState(false);

  // Group settings modal (iter37)
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [groupSettings, setGroupSettings] = useState({ name: '', description: '', currency: 'INR', sport: '', currency_name: '', emoji: '', treasurer_id: '', ledger_pdf_freq: 'off', ledger_pdf_day: 1, allow_external_donations: false, expense_approval_policy: 'admin' });
  const [savingGroupSettings, setSavingGroupSettings] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  // iter223 — sport catalog for the group settings dropdown
  const [sportsCatalog, setSportsCatalog] = useState([]);

  // iter223 — load sports catalog once on mount for use across settings + session editor
  useEffect(() => {
    api.get('/sports').then((r) => setSportsCatalog(r.data?.rows || [])).catch(() => {});
  }, []);
  // Customise Features (iter97) — admin overrides on top of type defaults.
  const [savingFeatures, setSavingFeatures] = useState(false);

  // iter169 — Trip-only party composition + per-head kid weight.
  const [partyDraft, setPartyDraft] = useState({}); // { user_id: {adults, kids} }
  const [kidWeightDraft, setKidWeightDraft] = useState(1);
  const [savingParty, setSavingParty] = useState(false);

  // iter181 — Trip-only Smart Splitting state
  const [drinkerDraft, setDrinkerDraft] = useState({}); // { user_id: bool }
  const [roomsDraft, setRoomsDraft] = useState([]); // [{id, label, occupant_user_ids}]
  const [savingRooms, setSavingRooms] = useState(false);

  // iter171 — Trip Budget draft state. Each item: {key, label, amount, expense_categories: []}.
  const TRIP_BUDGET_DEFAULTS = [
    { key: 'rooms',     label: 'Rooms / Accommodation', amount: 0, expense_categories: ['accommodation'] },
    { key: 'transport', label: 'Transport & Flights',   amount: 0, expense_categories: ['transport'] },
    { key: 'food',      label: 'Food & Restaurants',    amount: 0, expense_categories: ['food', 'restaurant', 'groceries'] },
    { key: 'activities',label: 'Activities & Entertainment', amount: 0, expense_categories: ['entertainment', 'sports', 'nightlife'] },
    { key: 'misc',      label: 'Miscellaneous',         amount: 0, expense_categories: ['miscellaneous', 'shopping'] },
  ];
  const [budgetDraft, setBudgetDraft] = useState([]);
  const [savingBudget, setSavingBudget] = useState(false);

  // AI Supervisor observations (iter37)
  const [aiObservations, setAiObservations] = useState([]);
  const [aiActiveCount, setAiActiveCount] = useState(0);
  const [aiSeverity, setAiSeverity] = useState({ low: 0, medium: 0, high: 0 });
  const [showAiObsDrawer, setShowAiObsDrawer] = useState(false);
  const [aiObsLoading, setAiObsLoading] = useState(false);

  // AI Co-Admin (iter38)
  const [coAdminSettings, setCoAdminSettings] = useState({
    enabled: false, auto_approve_expenses: true, auto_approve_max_amount: 500,
    auto_approve_requires_clean: true, auto_fine_late_cancel: true, veto_window_hours: 24,
  });
  const [coAdminActions, setCoAdminActions] = useState([]);
  const [coAdminActiveCount, setCoAdminActiveCount] = useState(0);
  const [showCoAdminDrawer, setShowCoAdminDrawer] = useState(false);
  const [savingCoAdmin, setSavingCoAdmin] = useState(false);
  const [vetoingIds, setVetoingIds] = useState(new Set());

  // Trash drawer (iter47)
  const [showTrashDrawer, setShowTrashDrawer] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  // iter94 — sticky group header sheets (AI assistant + group menu)
  const [showAiSheet, setShowAiSheet] = useState(false);
  const [showGroupMenu, setShowGroupMenu] = useState(false);

  // iter170 — Duplicate Trip modal state + handler
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateForm, setDuplicateForm] = useState({
    name: '',
    trip_start_date: '',
    trip_end_date: '',
    included_member_ids: [],
  });
  const [duplicateBusy, setDuplicateBusy] = useState(false);

  const openDuplicateModal = () => {
    setDuplicateForm({
      name: '',
      trip_start_date: '',
      trip_end_date: '',
      // Pre-select every current member; admin can uncheck.
      included_member_ids: (group?.members || []).map((m) => m.id),
    });
    setShowDuplicateModal(true);
  };

  const handleDuplicateGroup = async (e) => {
    if (e) e.preventDefault();
    if (!duplicateForm.name.trim()) {
      toast.error('Please enter a name for the new trip');
      return;
    }
    if (duplicateForm.included_member_ids.length === 0) {
      toast.error('Pick at least one member to carry forward');
      return;
    }
    setDuplicateBusy(true);
    try {
      const res = await api.post(`/groups/${groupId}/duplicate`, {
        name: duplicateForm.name.trim(),
        trip_start_date: duplicateForm.trip_start_date || null,
        trip_end_date: duplicateForm.trip_end_date || null,
        member_ids: duplicateForm.included_member_ids,
      });
      toast.success(`Created "${duplicateForm.name.trim()}"`);
      setShowDuplicateModal(false);
      const newId = res.data?.id || res.data?.group?.id;
      if (newId) navigate(`/groups/${newId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate');
    } finally {
      setDuplicateBusy(false);
    }
  };
  const invite = useGroupInvite(); // iter127 — shared share-invite sheet
  
  // Form states
  // iter181 — Smart Trip Splitting: category-aware presets.
  // When the user picks a category in Add Expense, we auto-set split_type and
  // recompute split_among based on these rules. Trip-only rules wait for the
  // Trip context check at runtime.
  // Special hooks: 'hotel' → may use room_id picker; 'liquor' → excludes
  // non-drinkers (member_tags[uid].drinker===false).
  const TRIP_CATEGORY_PRESETS = useMemo(() => ({
    accommodation:  { split_type: 'per_head',  scope: 'all' },
    hotel:          { split_type: 'per_head',  scope: 'all' },
    lodging:        { split_type: 'per_head',  scope: 'all' },
    food:           { split_type: 'per_head',  scope: 'all' },
    restaurant:     { split_type: 'per_head',  scope: 'all' },
    groceries:      { split_type: 'per_head',  scope: 'all' },
    sports:         { split_type: 'per_head',  scope: 'all' },
    entertainment:  { split_type: 'per_head',  scope: 'all' },
    court_booking:  { split_type: 'equal',     scope: 'all' },
    transport:      { split_type: 'equal',     scope: 'all' },
    transportation: { split_type: 'equal',     scope: 'all' },
    utilities:      { split_type: 'equal',     scope: 'all' },
    nightlife:      { split_type: 'equal',     scope: 'drinkers' },
    liquor:         { split_type: 'equal',     scope: 'drinkers' },
    bar:            { split_type: 'equal',     scope: 'drinkers' },
    shopping:       { split_type: 'custom',    scope: 'all' },
    medical:        { split_type: 'custom',    scope: 'all' },
    flight:         { split_type: 'custom',    scope: 'all' },
  }), []);

  const [newExpense, setNewExpense] = useState({
    amount: '',
    description: '',
    category: 'miscellaneous',
    paid_by: '',
    split_type: 'equal',
    split_among: [],
    custom_splits: {},
    percent_splits: {},
    reimburse_from_fund: false,
    advance_from_fund: false,
    fund_cap_enabled: false,
    fund_cap: '',
    // iter181 — Smart Trip Splitting fields
    room_id: '',
    direct_payment: false,
    kid_participation: {},
    // iter183 — Variable stay-nights weighting
    weight_by_nights: false,
    // iter191 — Trip multi-currency: which currency the user typed in.
    // Set when the AddExpense modal opens (group default + last-used localStorage).
    entry_currency: '',
  });

  // iter191 — Seed entry_currency for the AddExpense + Fund-Setup modals when
  // either opens. Defaults to the user's localStorage last-used pick, falling
  // back to the admin-set group default. No-ops for non-multi-currency groups.
  // Placed AFTER all relevant useState declarations (showAddExpense:266,
  // showFundModal:?, fundEntryCurrency:305, newExpense:509) to avoid TDZ refs.
  useEffect(() => {
    if (!showAddExpense) return;
    if (!group?.trip_currency || !group?.trip_rate) return;
    const home = group.currency || 'INR';
    const trip = group.trip_currency;
    let last = null;
    try { last = localStorage.getItem(`lastEntryCur_${groupId}`); } catch { /* localStorage blocked */ }
    const fallback = group.default_entry_currency === 'home' ? home : trip;
    const seed = (last === home || last === trip) ? last : fallback;
    setNewExpense((prev) => prev.entry_currency === seed ? prev : { ...prev, entry_currency: seed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddExpense, group?.trip_currency, group?.trip_rate, group?.currency, group?.default_entry_currency, groupId]);
  useEffect(() => {
    if (!showFundModal) return;
    if (!group?.trip_currency || !group?.trip_rate) return;
    const home = group.currency || 'INR';
    const trip = group.trip_currency;
    let last = null;
    try { last = localStorage.getItem(`lastEntryCur_${groupId}`); } catch { /* localStorage blocked */ }
    const fallback = group.default_entry_currency === 'home' ? home : trip;
    const seed = (last === home || last === trip) ? last : fallback;
    setFundEntryCurrency(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFundModal, group?.trip_currency, group?.trip_rate, group?.currency, group?.default_entry_currency, groupId]);

  // iter183 — Categories where stay-nights weighting auto-applies (Hotel + Food family)
  const NIGHT_SENSITIVE_CATEGORIES = useMemo(() => new Set([
    'hotel', 'lodging', 'accommodation',
    'food', 'restaurant', 'groceries',
  ]), []);
  const [newRecurring, setNewRecurring] = useState({
    amount: '',
    description: '',
    category: 'sports',
    paid_by: '',
    split_type: 'equal',
    split_among: [],
    frequency: 'weekly',
    days_of_week: [],
    custom_dates: [],
  });
  // iter132 — pending treasury actions (top-ups / allots awaiting co-approval)
  const [pendingTreasury, setPendingTreasury] = useState([]);

  // iter181 — Smart category change: apply Trip preset rules.
  // - Looks up TRIP_CATEGORY_PRESETS[category]
  // - Sets split_type per preset
  // - Re-selects split_among based on `scope` (all | drinkers)
  // - Clears room_id when category leaves the Hotel family
  // - Turns OFF reimburse_from_fund (so the split UI becomes visible — the
  //   whole point of the smart preset is the SPLIT, not a fund refund).
  // No-op for non-Trip groups (category just updates).
  const handleCategoryChange = (newCategory) => {
    setNewExpense((prev) => {
      const next = { ...prev, category: newCategory };
      // iter281 — prefill the remembered cap for this category (when cap on & field empty)
      if (prev.paid_by === 'group_fund' && prev.fund_cap_enabled && prev.fund_cap === '') {
        const saved = group?.fund_cap_defaults?.[(newCategory || 'miscellaneous').replace(/[.$]/g, '_')];
        if (saved != null) next.fund_cap = String(saved);
      }
      const isTrip = group?.group_type === 'trip';
      if (!isTrip) return next;
      const preset = TRIP_CATEGORY_PRESETS[newCategory];
      if (!preset) {
        // Unknown category — leave split_type alone but clear room if not hotel
        if (newCategory !== 'hotel' && newCategory !== 'lodging' && newCategory !== 'accommodation') next.room_id = '';
        return next;
      }
      next.split_type = preset.split_type;
      next.reimburse_from_fund = false; // preset wants a real split, not a fund refund
      const allMemberIds = (group?.members || []).map((m) => m.id);
      if (preset.scope === 'drinkers') {
        const tags = group?.member_tags || {};
        // Default drinker=true; only exclude when explicit false
        next.split_among = allMemberIds.filter((uid) => tags[uid]?.drinker !== false);
      } else {
        next.split_among = allMemberIds;
      }
      // Clear room_id unless still in hotel family
      if (newCategory !== 'hotel' && newCategory !== 'lodging' && newCategory !== 'accommodation') next.room_id = '';
      // iter183 — Auto-enable nights weighting for Hotel/Food categories
      // when any member's nights differ from the max (someone arrives late
      // or leaves early). User can still toggle off via the visible switch.
      if (NIGHT_SENSITIVE_CATEGORIES.has(newCategory)) {
        const party = group?.party_composition || {};
        const nights = next.split_among.map((uid) => (party[uid] || {}).nights || 0);
        const validNights = nights.filter((n) => n > 0);
        const anyDiffer = validNights.length > 1 && Math.max(...validNights) !== Math.min(...validNights);
        next.weight_by_nights = anyDiffer;
      } else {
        next.weight_by_nights = false;
      }
      return next;
    });
  };

  // iter181 — When the admin picks a Hotel room, force the split to the room's
  // occupants and split_type=equal (the backend also enforces this on POST).
  const handleRoomPick = (roomId) => {
    setNewExpense((prev) => {
      const rooms = group?.trip_rooms || [];
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return { ...prev, room_id: '' };
      return {
        ...prev,
        room_id: roomId,
        split_type: 'equal',
        split_among: room.occupant_user_ids || [],
      };
    });
  };

  // iter182 — Open Bulk Add: seed rows from group members (one per member, included by default)
  useEffect(() => {
    if (!showBulkAdd) return;
    const members = group?.members || [];
    setBulkData((prev) => ({
      ...prev,
      payer: prev.payer || user?.id || '',
      rows: members.map((m) => ({
        user_id: m.id,
        amount: '',
        description: '',
        included: true,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBulkAdd, group?.id]);

  const handleBulkSubmit = async () => {
    if (!groupId) return;
    const validRows = (bulkData.rows || []).filter((r) => r.included && parseFloat(r.amount) > 0);
    if (validRows.length === 0) {
      toast.error('Add at least one row with an amount');
      return;
    }
    if (bulkData.payment_mode === 'single' && !bulkData.payer) {
      toast.error('Pick the payer');
      return;
    }
    if (!bulkData.shared_description.trim()) {
      toast.error('Add a description (used as fallback for rows without one)');
      return;
    }
    setBulkSubmitting(true);
    try {
      const payload = {
        group_id: groupId,
        category: bulkData.category,
        payment_mode: bulkData.payment_mode,
        payer: bulkData.payment_mode === 'single' ? bulkData.payer : null,
        reimburse_from_fund: bulkData.payment_mode === 'single' ? !!bulkData.reimburse_from_fund : false,
        shared_description: bulkData.shared_description.trim(),
        rows: validRows.map((r) => ({
          user_id: r.user_id,
          amount: parseFloat(r.amount),
          description: (r.description || '').trim() || null,
        })),
      };
      const r = await api.post('/expenses/bulk', payload);
      const d = r.data || {};
      const approved = d.approved_count || 0;
      const pending = d.pending_count || 0;
      toast.success(`${d.created_count || validRows.length} expense${validRows.length === 1 ? '' : 's'} added${approved ? ` (${approved} auto-approved)` : ''}${pending ? ` (${pending} pending)` : ''}`);
      setShowBulkAdd(false);
      // Reset state for next open
      setBulkData({
        category: 'transport',
        payment_mode: 'direct',
        payer: user?.id || '',
        reimburse_from_fund: false,
    advance_from_fund: false,
        shared_description: '',
        rows: [],
      });
      // Refresh group + ledger (fetchGroup already pulls expenses)
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add bulk expenses');
    } finally {
      setBulkSubmitting(false);
    }
  };

  // iter182 helpers — quick-fill in single Add Expense (Option A)
  const distributeEvenlyCustom = () => {
    const total = parseFloat(newExpense.amount);
    if (!total || total <= 0 || (newExpense.split_among || []).length === 0) {
      toast.error('Set a total amount and pick members first');
      return;
    }
    const per = Math.round((total / newExpense.split_among.length) * 100) / 100;
    const splits = {};
    newExpense.split_among.forEach((uid, idx) => {
      // Reconcile rounding remainder on last
      if (idx === newExpense.split_among.length - 1) {
        splits[uid] = Math.round((total - per * (newExpense.split_among.length - 1)) * 100) / 100;
      } else {
        splits[uid] = per;
      }
    });
    setNewExpense({ ...newExpense, custom_splits: splits });
    toast.success(`Distributed ${formatCurrency(total)} evenly across ${newExpense.split_among.length} members`);
  };

  const applyAmountToAllCustom = (amountStr) => {
    const v = parseFloat(amountStr);
    if (!v || v <= 0 || (newExpense.split_among || []).length === 0) return;
    const splits = {};
    newExpense.split_among.forEach((uid) => { splits[uid] = v; });
    setNewExpense({ ...newExpense, custom_splits: splits, amount: String(v * newExpense.split_among.length) });
  };

  const fetchPendingTreasury = useCallback(async () => {
    if (!groupId) return;
    try {
      const r = await api.get(`/groups/${groupId}/treasury/pending`);
      setPendingTreasury(r.data?.pending || []);
    } catch {
      // silently no-op
    }
  }, [groupId]);

  useEffect(() => { fetchPendingTreasury(); }, [fetchPendingTreasury]);

  const handleTreasuryApprove = async (txnId) => {
    try {
      await api.post(`/groups/${groupId}/treasury/${txnId}/approve`);
      toast.success('Approved — money moved.');
      fetchPendingTreasury();
      fetchFund();
      fetchGroup();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  // iter284 — bulk approve selected treasury actions in one go
  const handleTreasuryBulkApprove = async (txnIds) => {
    let ok = 0; let failed = 0; let firstErr = '';
    for (const id of txnIds) {
      try {
        await api.post(`/groups/${groupId}/treasury/${id}/approve`);
        ok += 1;
      } catch (e) {
        failed += 1;
        if (!firstErr) firstErr = e.response?.data?.detail || '';
      }
    }
    if (ok) toast.success(`Approved ${ok} action${ok > 1 ? 's' : ''} — money moved.`);
    if (failed) toast.error(`${failed} failed${firstErr ? ` — ${firstErr}` : ''}`);
    fetchPendingTreasury();
    fetchFund();
    fetchGroup();
    fetchSessions();
  };

  const handleTreasuryReject = async (txnId) => {
    const reason = window.prompt('Reason for rejection (optional):') || '';
    try {
      await api.post(`/groups/${groupId}/treasury/${txnId}/reject`, { reason });
      toast.success('Rejected.');
      fetchPendingTreasury();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
  };

  const handleTreasuryUndo = async (txnId) => {
    if (!window.confirm('Undo this treasury action? A counter-entry will be written to the NeoPay ledger.')) return;
    try {
      const r = await api.post(`/groups/${groupId}/treasury/${txnId}/undo`, { reason: '' });
      toast.success(`Undone (${r.data?.reversed_via || 'reversed'}). Counter-entry on ledger.`);
      fetchFund();
      fetchGroup();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    }
  };

  const [newMemberPhone, setNewMemberPhone] = useState('');

  // iter128 — live phone validation
  const [phoneLookup, setPhoneLookup] = useState({ status: 'idle', data: null });
  // iter129 — ghost / shadow member name (only used when phone is not_found)
  const [shadowName, setShadowName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null); // null, 'all', or user_id
  const [sendingGroupDigest, setSendingGroupDigest] = useState(false);

  // ---- Draft auto-save (durability: never lose what user typed) ----
  const { clearDraft: clearAddExpenseDraft } = useDraftAutoSave({
    formKey: `addExpense:${groupId}`,
    value: newExpense,
    onRestore: (saved) => setNewExpense((prev) => ({ ...prev, ...saved })),
    enabled: showAddExpense,
  });
  const { clearDraft: clearRecurringDraft } = useDraftAutoSave({
    formKey: `addRecurring:${groupId}`,
    value: newRecurring,
    onRestore: (saved) => setNewRecurring((prev) => ({ ...prev, ...saved })),
    enabled: showRecurringModal,
  });
  const { clearDraft: clearFundDraft } = useDraftAutoSave({
    formKey: `fundTopUp:${groupId}`,
    value: { fundAmount, fundNote, fundContributorId, fundStatus, fundIsLoan, fundLoanDueDate },
    onRestore: (saved) => {
      if (saved.fundAmount != null) setFundAmount(saved.fundAmount);
      if (saved.fundNote != null) setFundNote(saved.fundNote);
      if (saved.fundContributorId != null) setFundContributorId(saved.fundContributorId);
      if (saved.fundStatus != null) setFundStatus(saved.fundStatus);
      if (saved.fundIsLoan != null) setFundIsLoan(saved.fundIsLoan);
      if (saved.fundLoanDueDate != null) setFundLoanDueDate(saved.fundLoanDueDate);
    },
    enabled: showFundModal,
  });
  const { clearDraft: clearFineDraft } = useDraftAutoSave({
    formKey: `addFine:${groupId}`,
    value: fineData,
    onRestore: (saved) => setFineData((prev) => ({ ...prev, ...saved })),
    enabled: showFineModal,
  });
  const { clearDraft: clearAllotDraft } = useDraftAutoSave({
    formKey: `allotCoins:${groupId}`,
    value: allotData,
    onRestore: (saved) => setAllotData((prev) => ({ ...prev, ...saved })),
    enabled: showAllotCoins,
  });
  const { clearDraft: clearTransferDraft } = useDraftAutoSave({
    formKey: `coinTransfer:${groupId}`,
    value: transferData,
    onRestore: (saved) => setTransferData((prev) => ({ ...prev, ...saved })),
    enabled: showTransferModal,
  });
  const { clearDraft: clearSessionDraft } = useDraftAutoSave({
    formKey: `createSession:${groupId}`,
    value: newSession,
    onRestore: (saved) => setNewSession((prev) => ({ ...prev, ...saved })),
    enabled: showCreateSession,
  });

  const fetchGroup = useCallback(async () => {
    try {
      const [groupRes, recurringRes] = await Promise.all([
        api.get(`/groups/${groupId}`),
        api.get(`/recurring-expenses/${groupId}`).catch(() => ({ data: [] }))
      ]);
      setGroup(groupRes.data);
      setRecurringExpenses(recurringRes.data || []);
      
      // iter176 — Default to "I paid on behalf of the group, group will reimburse me"
      // for ALL group types (Trip / Sports / Daily / Event). Members opt out
      // (untick reimburse OR pick a different payer) when the default doesn't apply.
      if (!newExpense.paid_by && user) {
        setNewExpense(prev => ({
          ...prev,
          paid_by: user.id,
          reimburse_from_fund: true,
          split_among: groupRes.data.member_ids || [],
        }));
        setNewRecurring(prev => ({
          ...prev,
          paid_by: user.id,
          split_among: groupRes.data.member_ids || [],
        }));
      }
    } catch (error) {
      toast.error('Failed to load group');
      navigate('/groups');
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate, user, newExpense.paid_by]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  // iter201 — auto-refresh the group view whenever the durable write
  // queue drains (i.e. an offline write just made it to the server).
  useSyncReload(fetchGroup);

  const formatCurrency = (amount) => formatNXC(amount, group?.currency);
  // iter234 — coin-denominated amounts wear the group's coin name.
  const formatCoinAmount = (amount) => formatCoins(amount, group?.currency_name);

  const isAdmin = group?.admin_ids?.includes(user?.id);

  // iter203 — silent-guest → real-member match surface (admin-only).
  const silentGuestMatches = useSilentGuestMatches(groupId, { enabled: !!isAdmin });

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!newExpense.amount || !newExpense.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (newExpense.paid_by !== 'group_fund' && !newExpense.reimburse_from_fund && newExpense.split_among.length === 0) {
      toast.error('Please select at least one member to split with');
      return;
    }

    setSubmitting(true);
    try {
      const useRefund = !!newExpense.reimburse_from_fund && newExpense.paid_by !== 'group_fund';
      const useAdvance = !!newExpense.advance_from_fund && !useRefund && newExpense.paid_by !== 'group_fund';
      const isDirect = !!newExpense.direct_payment && newExpense.paid_by !== 'group_fund';
      // iter258 — percentage split converts to custom amounts client-side
      let effSplitType = newExpense.split_type;
      let effCustomSplits = newExpense.custom_splits;
      if (effSplitType === 'percentage' && !useRefund && newExpense.paid_by !== 'group_fund') {
        const ps = newExpense.percent_splits || {};
        const totalPct = newExpense.split_among.reduce((s, id) => s + (parseFloat(ps[id]) || 0), 0);
        if (Math.abs(totalPct - 100) > 0.5) {
          toast.error(`Percentages must add up to 100% (currently ${Math.round(totalPct * 100) / 100}%)`);
          setSubmitting(false);
          return;
        }
        const amt = parseFloat(newExpense.amount);
        effCustomSplits = {};
        let acc = 0;
        newExpense.split_among.forEach((id, i) => {
          if (i === newExpense.split_among.length - 1) {
            effCustomSplits[id] = Math.round((amt - acc) * 100) / 100;
          } else {
            const v = Math.round(amt * (parseFloat(ps[id]) || 0)) / 100;
            effCustomSplits[id] = v;
            acc += v;
          }
        });
        effSplitType = 'custom';
      }
      const payload = {
        group_id: groupId,
        amount: parseFloat(newExpense.amount),
        description: newExpense.description,
        category: newExpense.category,
        paid_by: newExpense.paid_by,
        split_type: (newExpense.paid_by === 'group_fund' || useRefund) ? 'equal' : effSplitType,
        split_among: newExpense.paid_by === 'group_fund'
          ? (newExpense.fund_cap_enabled && parseFloat(newExpense.fund_cap || 0) < parseFloat(newExpense.amount || 0) ? newExpense.split_among : [])
          : (useRefund ? [newExpense.paid_by] : newExpense.split_among),
        custom_splits: (effSplitType === 'custom' && !useRefund) ? effCustomSplits : null,
        reimburse_from_fund: useRefund,
        advance_from_fund: useAdvance, // iter217
        room_id: newExpense.room_id || null,
        direct_payment: isDirect,
        kid_participation: (newExpense.split_type === 'per_head' && Object.keys(newExpense.kid_participation || {}).length > 0)
          ? newExpense.kid_participation
          : null,
        weight_by_nights: !useRefund && !useAdvance && !isDirect ? !!newExpense.weight_by_nights : false,
      };
      // iter280 — Fund Cap Split
      if (newExpense.paid_by === 'group_fund' && newExpense.fund_cap_enabled && newExpense.fund_cap !== '') {
        payload.fund_cap = parseFloat(newExpense.fund_cap);
      }
      // iter191 — Trip multi-currency: when the group has a frozen rate, send the
      // typed amount + currency so backend can convert at the locked rate.
      if (group?.trip_currency && group?.trip_rate && newExpense.entry_currency) {
        payload.entry_currency = newExpense.entry_currency;
        payload.entry_amount = parseFloat(newExpense.amount);
        // Backend will overwrite payload.amount with the converted home value.
        // Persist last-used currency for this user + group so the next AddExpense
        // opens with the same default.
        try { localStorage.setItem(`lastEntryCur_${groupId}`, newExpense.entry_currency); } catch { /* localStorage blocked */ }
      }
      const expenseRes = await api.post('/expenses', payload);
      
      // Upload receipt if attached
      if (receiptFile && expenseRes.data?.id) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        formData.append('expense_id', expenseRes.data.id);
        formData.append('group_id', groupId);
        try {
          await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch { /* receipt upload failure is non-blocking */ }
      }

      const created = expenseRes.data || {};
      toast.success(created.queued
        ? 'Saved offline — will sync automatically.'
        : created.status === 'approved'
          ? 'Expense added and approved!'
          : created.approval_type === 'members'
            ? `Expense added! Waiting for member approvals (${created.approvals_needed || ''} needed).`
            : created.approval_type === 'peer'
              ? 'Expense added! Waiting for the other member to approve.'
              : 'Expense added! Waiting for admin approval.');
      setShowAddExpense(false);
      setReceiptFile(null);
      setNewExpense({
        amount: '',
        description: '',
        category: 'miscellaneous',
        paid_by: user?.id || '',
        split_type: 'equal',
        split_among: group?.member_ids || [],
        custom_splits: {},
        reimburse_from_fund: true,
        room_id: '',
        direct_payment: false,
        kid_participation: {},
        weight_by_nights: false,
      });
      clearAddExpenseDraft();
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRecurring = async (e) => {
    e.preventDefault();
    if (!newRecurring.amount || !newRecurring.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (newRecurring.split_among.length === 0) {
      toast.error('Please select at least one member');
      return;
    }
    if (newRecurring.frequency === 'weekly' && newRecurring.days_of_week.length === 0) {
      toast.error('Please select at least one day');
      return;
    }
    if (newRecurring.frequency === 'custom_dates' && newRecurring.custom_dates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/recurring-expenses', {
        group_id: groupId,
        amount: parseFloat(newRecurring.amount),
        description: newRecurring.description,
        category: newRecurring.category,
        paid_by: newRecurring.paid_by,
        split_type: newRecurring.split_type,
        split_among: newRecurring.split_among,
        frequency: newRecurring.frequency,
        days_of_week: newRecurring.days_of_week,
        custom_dates: newRecurring.custom_dates.map(d => format(d, 'yyyy-MM-dd')),
        is_active: true,
      });
      
      toast.success('Recurring expense created!');
      setShowRecurringModal(false);
      setNewRecurring({
        amount: '',
        description: '',
        category: 'sports',
        paid_by: user?.id || '',
        split_type: 'equal',
        split_among: group?.member_ids || [],
        frequency: 'weekly',
        days_of_week: [],
        custom_dates: [],
      });
      clearRecurringDraft();
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create recurring expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTriggerRecurring = async (recurringId) => {
    try {
      await api.post(`/recurring-expenses/${recurringId}/trigger`);
      toast.success('Expense created from recurring!');
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger');
    }
  };

  const handleToggleRecurring = async (recurringId) => {
    // Optimistic — flip the enabled flag on the local copy
    const prev = group;
    setGroup((g) => g ? {
      ...g,
      recurring_expenses: (g.recurring_expenses || []).map((r) =>
        r.id === recurringId ? { ...r, enabled: !r.enabled } : r
      ),
    } : g);
    try {
      const response = await api.put(`/recurring-expenses/${recurringId}/toggle`);
      toast.success(response.data.message);
      fetchGroup();
    } catch (error) {
      setGroup(prev);
      toast.error(error.response?.data?.detail || 'Failed to toggle');
    }
  };

  const handleDeleteRecurring = async (recurringId) => {
    const prev = group;
    await runWithUndo({
      applyOptimistic: () => setGroup((g) => g ? {
        ...g,
        recurring_expenses: (g.recurring_expenses || []).filter((r) => r.id !== recurringId),
      } : g),
      restore: () => setGroup(prev),
      commit: async () => {
        await api.delete(`/recurring-expenses/${recurringId}`);
        fetchGroup();
      },
      toastMsg: 'Recurring expense deleted',
      undoMsg: 'Recurring expense restored',
      onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete'),
    });
  };

  const handleAddMember = async () => {
    // iter255 — supports multiple: quick-picked users + comma/space-separated numbers
    const typed = (newMemberPhone || '').split(/[,;\s]+/).map((p) => p.trim()).filter((p) => p.length >= 4);
    const picked = quickPickUsers.filter((u) => selectedQuickIds.has(u.id)).map((u) => u.phone);
    const phones = [...new Set([...picked, ...typed])];
    if (phones.length === 0) {
      toast.error('Enter a phone number or pick members above');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.put(`/groups/${groupId}`, {
        member_phones: phones,
      });
      const added = res.data?.added_users || [];
      const notFound = res.data?.not_found_phones || [];
      if (added.length > 0) {
        toast.success(`Added ${added.map((u) => u.name).join(', ')}`);
      }
      if (notFound.length > 0 && phones.length === 1 && added.length === 0) {
        // iter128 — single-number silent-failure with one-tap invite fallback
        toast.error(
          `No SplitEasy account for ${notFound[0]}. Send them an invite link instead.`,
          {
            action: {
              label: 'Send invite',
              onClick: () => {
                setShowAddMember(false);
                setNewMemberPhone('');
                setPhoneLookup({ status: 'idle', data: null });
                invite.openShareMenu(group);
              },
            },
            duration: 8000,
          }
        );
        setSubmitting(false);
        return;
      }
      if (notFound.length > 0) {
        toast.warning(
          `${notFound.length} number${notFound.length === 1 ? '' : 's'} not on SplitEasy yet — tracked under Pending invites (Members tab) and auto-reminded on WhatsApp`,
          { duration: 8000 }
        );
      }
      if (added.length === 0 && notFound.length === 0) {
        toast.info('They are already in this group.');
      }
      setShowAddMember(false);
      setNewMemberPhone('');
      setSelectedQuickIds(new Set());
      setPhoneLookup({ status: 'idle', data: null });
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add members');
    } finally {
      setSubmitting(false);
    }
  };

  // iter255 — load quick-pick candidates (registered users not yet in the group)
  useEffect(() => {
    if (!showAddMember) {
      setSelectedQuickIds(new Set());
      return;
    }
    api.get('/users/search?name=')
      .then((res) => {
        const memberIds = new Set(group?.member_ids || (group?.members || []).map((m) => m.id));
        setQuickPickUsers((res.data || []).filter((u) => u.phone && !memberIds.has(u.id)));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddMember]);

  // iter128 — debounced phone lookup so the admin sees a live preview
  // ("✓ Shrenik will be added" / "✗ Not registered — send invite") before clicking.
  useEffect(() => {
    if (!showAddMember) return;
    const raw = (newMemberPhone || '').trim();
    if (raw.length < 4 || /[,;\s]/.test(raw)) {
      // bulk mode (multiple numbers) — skip the single-number live preview
      setPhoneLookup({ status: 'idle', data: null });
      return;
    }
    let cancelled = false;
    setPhoneLookup({ status: 'loading', data: null });
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/users/lookup-by-phone', { params: { phone: raw } });
        if (cancelled) return;
        const data = res.data || {};
        // Already a member?
        const memberIds = new Set((group?.member_ids) || []);
        const alreadyMember = data.found && data.user && memberIds.has(data.user.id);
        setPhoneLookup({
          status: alreadyMember ? 'already_member' : (data.found ? 'found' : 'not_found'),
          data,
        });
      } catch {
        if (cancelled) return;
        setPhoneLookup({ status: 'idle', data: null });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [newMemberPhone, showAddMember, group?.member_ids]);

  // iter129 — add as ghost / shadow member (no account required)
  const handleAddShadowMember = async () => {
    const name = shadowName.trim();
    if (!name) {
      toast.error('Enter a name for the ghost member');
      return;
    }
    if (!newMemberPhone.trim()) {
      toast.error('Phone is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/groups/${groupId}/shadow-member`, {
        name,
        phone: newMemberPhone,
      });
      toast.success(
        res.data?.is_new_shadow
          ? `${name} added as a ghost — they'll auto-merge when they sign up.`
          : `${name} added to the group.`,
        { duration: 5500 },
      );
      setShowAddMember(false);
      setNewMemberPhone('');
      setShadowName('');
      setPhoneLookup({ status: 'idle', data: null });
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add ghost member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReminder = async (userId = null) => {
    const key = userId || 'all';
    setSendingReminder(key);
    try {
      const res = await api.post(`/whatsapp/send-reminder/${groupId}`, {
        user_id: userId,
      });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleSendGroupDigest = async (ledgerOption = 'none') => {
    setSendingGroupDigest(true);
    try {
      const res = await api.post(`/whatsapp/send-digest/${groupId}`, {
        include_ledger: ledgerOption === 'week' || ledgerOption === 'month',
        ledger_period: ledgerOption === 'month' ? 'month' : 'week',
      });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingGroupDigest(false);
    }
  };

  const handleToggleGroupAdmin = async (memberId, isCurrentlyAdmin) => {
    try {
      if (isCurrentlyAdmin) {
        await api.put(`/groups/${groupId}/members/${memberId}/remove-admin`);
        toast.success('Removed from group admins');
      } else {
        await api.put(`/groups/${groupId}/members/${memberId}/make-admin`);
        toast.success('Promoted to group admin!');
      }
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    const prevMembers = group?.members;
    await runWithUndo({
      applyOptimistic: () => setGroup((g) => (g ? { ...g, members: (g.members || []).filter((m) => m.id !== memberId) } : g)),
      restore: () => setGroup((g) => (g ? { ...g, members: prevMembers } : g)),
      commit: async () => {
        await api.put(`/groups/${groupId}/members/${memberId}/remove`);
        fetchGroup();
      },
      toastMsg: `${memberName} removed from group`,
      undoMsg: `${memberName} is back in the group`,
      onError: (error) => toast.error(error.response?.data?.detail || 'Failed to remove member'),
    });
  };

  const handleSettle = async () => {
    if (!selectedSettlement) return;

    setSubmitting(true);
    try {
      await api.post('/settlements', {
        group_id: groupId,
        from_user: selectedSettlement.from_user,
        to_user: selectedSettlement.to_user,
        amount: selectedSettlement.amount,
      });
      toast.success('Settlement recorded!');
      setShowSettleModal(false);
      setSelectedSettlement(null);
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record settlement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await api.delete(`/groups/${groupId}`);
      toast.success('Group deleted');
      navigate('/groups');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleEditExpense = async (e) => {
    e.preventDefault();
    if (!editingExpense) return;
    
    setSubmitting(true);
    try {
      await api.patch(`/expenses/${editingExpense.id}`, {
        amount: parseFloat(editingExpense.amount),
        description: editingExpense.description,
        category: editingExpense.category,
        paid_by: editingExpense.paid_by,
        split_type: editingExpense.split_type,
        split_among: editingExpense.split_among,
      });
      toast.success('Expense updated!');
      setShowEditExpense(false);
      setEditingExpense(null);
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditExpense = (expense) => {
    setEditingExpense({
      id: expense.id,
      amount: expense.amount,
      description: expense.description,
      category: expense.category,
      paid_by: expense.paid_by,
      split_type: expense.split_type,
      split_among: expense.split_among || [],
    });
    setShowEditExpense(true);
  };

  const handleGenerateInvite = async () => {
    setInviteLoading(true);
    try {
      const response = await api.post(`/groups/${groupId}/invite`);
      setInviteCode(response.data.code);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/join/${inviteCode}`;
    navigator.clipboard.writeText(link).then(() => {
      toast.success('Invite link copied!');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  // Rich-preview share: uses /api/i/<code> wrapper which serves OG meta tags
  // so WhatsApp / FB / LinkedIn unfurl into a card image.
  const shareInviteRich = async () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const richUrl = `${apiBase}/api/i/${inviteCode}`;
    const text = `You're invited to join ${group?.name || 'our group'} on ${process.env.REACT_APP_BRAND_NAME || 'SplitEasy'}.`;

    if (navigator.share) {
      try {
        await navigator.share({ title: group?.name, text, url: richUrl });
        toast.success('Shared!');
        return;
      } catch (e) {
        // user cancelled — silent
        if (e?.name === 'AbortError') return;
      }
    }
    // Fallback: copy the rich-preview URL
    navigator.clipboard.writeText(richUrl).then(() => {
      toast.success('Rich-preview link copied! Paste into WhatsApp.');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const fetchFund = useCallback(async () => {
    try {
      const [fundRes, policyRes, statsRes, snapshotRes] = await Promise.all([
        api.get(`/groups/${groupId}/fund`),
        api.get(`/groups/${groupId}/balance-policy`).catch(() => ({ data: null })),
        api.get(`/groups/${groupId}/fund/weekly-stats`).catch(() => ({ data: null })),
        api.get(`/groups/${groupId}/fund/snapshot`).catch(() => ({ data: null })),
      ]);
      setFundData(fundRes.data);
      if (fundRes.data.fine_defaults) {
        setFineDefaults(fundRes.data.fine_defaults);
      }
      if (policyRes.data) {
        setBalancePolicy(policyRes.data);
      }
      setWeeklyStats(statsRes.data);
      setFundSnapshot(snapshotRes.data);
    } catch (error) {
      // Fund not setup yet
    }
  }, [groupId]);

  useEffect(() => {
    if (group) fetchFund();
  }, [group, fetchFund]);

  const handleLoanToMember = async (e) => {
    e.preventDefault();
    const amt = parseFloat(loanToMemberForm.amount);
    if (!loanToMemberForm.member_id || !amt || amt <= 0) {
      toast.error('Pick a member and enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/groups/${groupId}/fund/loan-to-member`, {
        member_id: loanToMemberForm.member_id,
        amount: amt,
        expected_repayment_date: loanToMemberForm.expected_repayment_date || null,
        note: loanToMemberForm.note || undefined,
      });
      // iter132/133 — distinguish pending vs approved
      if (res.data?.approval_status === 'pending') {
        toast.warning(res.data.message, {
          description: res.data.requires_super_admin
            ? 'Above ceiling — only super-admin can approve.'
            : 'Another admin must approve before fund disburses.',
          duration: 7000,
        });
      } else {
        toast.success(res.data.message);
      }
      setShowLoanToMemberModal(false);
      setLoanToMemberForm({ member_id: '', amount: '', expected_repayment_date: '', note: '' });
      fetchFund();
      fetchGroup();
      fetchPendingTreasury();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue loan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRepayLoan = async (loanId, principal, repaidSoFar, name) => {
    const outstanding = Math.max(0, Math.round(principal - (repaidSoFar || 0)));
    const input = window.prompt(
      `Repay how much? (Outstanding: ${formatNXC(outstanding)} from ${name || 'loan'})\n` +
        `Leave blank or type "${outstanding}" to repay full.`,
      String(outstanding)
    );
    if (input === null) return;
    const amt = input.trim() === '' ? outstanding : parseFloat(input);
    if (Number.isNaN(amt) || amt <= 0) {
      toast.error('Enter a valid positive amount');
      return;
    }
    try {
      const body = amt < outstanding ? { amount: amt } : {};
      const res = await api.post(`/groups/${groupId}/fund/loans/${loanId}/repay`, body);
      toast.success(res.data.message);
      fetchFund();
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record repayment');
    }
  };

  const handleAddGuestContribution = async (e) => {
    e.preventDefault();
    const amt = parseFloat(guestContribForm.amount);
    if (!guestContribForm.guest_name.trim() || !amt || amt <= 0) {
      toast.error('Enter a guest name and a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/groups/${groupId}/fund/guest-contribution`, {
        guest_name: guestContribForm.guest_name.trim(),
        amount: amt,
        note: guestContribForm.note || undefined,
        status: guestContribForm.status,
      });
      toast.success(res.data.message);
      setShowGuestContribModal(false);
      setGuestContribForm({ guest_name: '', amount: '', note: '', status: 'received' });
      fetchFund();
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add guest contribution');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPledgeReceived = async (txnId, amount, name) => {
    if (!window.confirm(`Mark ${formatNXC(amount)} payment-pending from ${name || 'contributor'} as received?`)) return;
    try {
      await api.post(`/groups/${groupId}/fund/contributions/${txnId}/mark-received`);
      toast.success(`Payment of ${formatNXC(amount)} marked received`);
      fetchFund();
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark received');
    }
  };

  const handleSetupFund = async (e) => {    e.preventDefault();
    if (!fundAmount || parseFloat(fundAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const isLoanEligible = fundIsLoan && fundContributorId !== 'sponsor' && fundContributorId !== 'split' && fundStatus === 'received';
      const body = {
        total_amount: parseFloat(fundAmount),
        note: fundNote || undefined,
        contributor_id: fundContributorId,
        status: fundContributorId === 'sponsor' ? 'received' : fundStatus,
        is_loan: isLoanEligible,
        expected_repayment_date: isLoanEligible && fundLoanDueDate ? fundLoanDueDate : null,
      };
      // iter191 — Trip multi-currency: when the group has a frozen rate, send
      // the typed amount + currency so backend can convert to home currency.
      if (group?.trip_currency && group?.trip_rate && fundEntryCurrency) {
        body.entry_currency = fundEntryCurrency;
        body.entry_amount = parseFloat(fundAmount);
        try { localStorage.setItem(`lastEntryCur_${groupId}`, fundEntryCurrency); } catch { /* localStorage blocked */ }
      }
      const response = await api.post(`/groups/${groupId}/fund/setup`, body);
      toast.success(response.data.message);
      // iter132 — warn if sponsor top-up is pending
      if (response.data?.approval_status === 'pending') {
        toast.warning('Pending co-approval', {
          description: response.data.requires_super_admin
            ? 'Above ceiling — only super-admin can approve.'
            : 'Another admin must approve before money moves to the fund.',
          duration: 7000,
        });
      }
      setShowFundModal(false);
      setFundAmount('');
      setFundNote('');
      setFundContributorId('split');
      setFundStatus('received');
      setFundIsLoan(false);
      setFundLoanDueDate('');
      clearFundDraft();
      fetchFund();
      fetchGroup();
      fetchPendingTreasury();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup fund');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIssueFine = async (e) => {
    e.preventDefault();
    if (!fineData.user_id || !fineData.amount || parseFloat(fineData.amount) <= 0) {
      toast.error('Select a member and enter amount');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post(`/groups/${groupId}/fines`, {
        user_id: fineData.user_id,
        amount: parseFloat(fineData.amount),
        fine_type: fineData.fine_type,
        reason: fineData.reason || undefined,
      });
      // iter133 — distinguish AI auto-approve (success) vs flagged-pending (warning)
      if (response.data?.approval_status === 'pending') {
        toast.warning(response.data.message, {
          description: (response.data.anomaly_flags || []).length
            ? `Flagged: ${response.data.anomaly_flags.join(', ')} — needs second admin.`
            : 'Above ceiling — needs second admin.',
          duration: 8000,
        });
      } else {
        toast.success(response.data.message);
      }
      setShowFineModal(false);
      setFineData({ user_id: '', amount: '', fine_type: 'late', reason: '' });
      clearFineDraft();
      fetchFund();
      fetchPendingTreasury();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue fine');
    } finally {
      setSubmitting(false);
    }
  };

  const openFineWithType = (type) => {
    const amount = fineDefaults[type] || '';
    setFineData({ user_id: '', amount: String(amount), fine_type: type, reason: '' });
    setShowFineModal(true);
  };

  const handleSaveFineDefaults = async (e) => {
    e.preventDefault();
    // iter288 — Fee Save Guard: a blank/0 session fee silently removes the
    // "Charge fee" button on every session. Require explicit confirmation.
    const feeVal = parseFloat(fineDefaults.session_fee) || 0;
    if (feeVal <= 0) {
      const prevFee = parseFloat(group?.fine_defaults?.session_fee) || 0;
      const ok = window.confirm(
        prevFee > 0
          ? `⚠️ Session fee is blank/0 (it was ₹${prevFee}). The "Charge fee" button will disappear on ALL sessions of this group. Save anyway?`
          : '⚠️ Session fee is blank/0 — the "Charge fee" button will not appear on any session of this group. Save anyway?'
      );
      if (!ok) return;
    }
    try {
      await api.put(`/groups/${groupId}/fine-defaults`, fineDefaults);
      toast.success('Fine defaults saved');
      setShowFineDefaultsModal(false);
      fetchFund();
    } catch (error) {
      toast.error('Failed to save defaults');
    }
  };

  const handleSaveBalancePolicy = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/groups/${groupId}/balance-policy`, balancePolicy);
      toast.success('Balance policy saved');
      setShowBalancePolicyModal(false);
      fetchFund();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    }
  };

  const handleReverseExpense = async (expenseId) => {
    if (!window.confirm('Reverse this expense? money will be refunded and fund restored.')) return;
    const prev = group;
    await runWithUndo({
      applyOptimistic: () => setGroup((g) => g ? {
        ...g,
        expenses: (g.expenses || []).map((e) =>
          e.id === expenseId ? { ...e, reversed: true, status: 'reversed' } : e
        ),
      } : g),
      restore: () => setGroup(prev),
      commit: async () => {
        await api.post(`/expenses/${expenseId}/reverse`);
        fetchGroup();
        fetchFund();
      },
      toastMsg: 'Expense reversed',
      undoMsg: 'Expense restored',
      onError: (err) => toast.error(err.response?.data?.detail || 'Failed to reverse expense'),
    });
  };

  const handleResubmitExpense = async (expenseId) => {
    const prev = group;
    setGroup((g) => g ? {
      ...g,
      expenses: (g.expenses || []).map((e) =>
        e.id === expenseId ? { ...e, status: 'pending' } : e
      ),
    } : g);
    try {
      const res = await api.post(`/expenses/${expenseId}/resubmit`);
      toast.success(res.data.message);
      fetchGroup();
    } catch (error) {
      setGroup(prev);
      toast.error(error.response?.data?.detail || 'Failed to resubmit');
    }
  };

  const handleReverseTransaction = async (txnId) => {
    if (!window.confirm('Reverse this transaction? money will be returned.')) return;
    // Optimistic — mark txn reversed in the fund list
    const prev = fundData;
    setFundData((fd) => fd ? {
      ...fd,
      transactions: (fd.transactions || []).map((t) =>
        t.id === txnId ? { ...t, reversed: true } : t
      ),
    } : fd);
    try {
      const res = await api.post(`/fund-transactions/${txnId}/reverse`);
      toast.success(res.data.message);
      fetchFund();
    } catch (error) {
      setFundData(prev);
      toast.error(error.response?.data?.detail || 'Failed to reverse');
    }
  };

  const toggleTxnSelect = (txnId) => {
    const next = new Set(selectedTxns);
    if (next.has(txnId)) next.delete(txnId); else next.add(txnId);
    setSelectedTxns(next);
  };

  const toggleSelectAllTxns = () => {
    const reversible = (fundData?.transactions || []).filter(t => ['fine', 'coin_allot', 'contribution', 'session_fee', 'membership_fee'].includes(t.type) && !t.reversed);
    if (selectedTxns.size === reversible.length && reversible.length > 0) {
      setSelectedTxns(new Set());
    } else {
      setSelectedTxns(new Set(reversible.map(t => t.id)));
    }
  };

  const handleBulkReverse = async () => {
    if (selectedTxns.size === 0) return;
    if (!window.confirm(`Reverse ${selectedTxns.size} transaction(s)? money will be returned.`)) return;
    setBulkReversing(true);
    // Optimistic — mark all selected as reversed
    const prev = fundData;
    const selectedSet = selectedTxns;
    setFundData((fd) => fd ? {
      ...fd,
      transactions: (fd.transactions || []).map((t) =>
        selectedSet.has(t.id) ? { ...t, reversed: true } : t
      ),
    } : fd);
    setSelectedTxns(new Set());
    try {
      const res = await api.post('/fund-transactions/bulk-reverse', { transaction_ids: [...selectedSet] });
      toast.success(res.data.message);
      fetchFund();
    } catch (error) {
      setFundData(prev);
      toast.error(error.response?.data?.detail || 'Bulk reverse failed');
    } finally { setBulkReversing(false); }
  };

  const handleAllotCoins = async (e) => {
    e.preventDefault();
    const amt = parseFloat(allotData.amount);
    if (!allotData.user_id) {
      toast.error('Select a member');
      return;
    }
    if (!allotData.amount || isNaN(amt) || amt === 0) {
      toast.error('Enter a non-zero amount (negative values allowed to deduct)');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post(`/groups/${groupId}/coins/allot`, {
        user_id: allotData.user_id,
        amount: amt,
        note: allotData.note || undefined,
      });
      // iter132 — handle pending vs approved outcomes distinctly
      const status = response.data?.approval_status;
      if (status === 'pending') {
        toast.warning(response.data.message || 'Pending co-approval', {
          description: response.data.requires_super_admin
            ? 'Above ceiling — only super-admin can approve.'
            : 'Another admin must approve before money moves.',
          duration: 7000,
        });
      } else {
        toast.success(response.data.message);
      }
      setShowAllotCoins(false);
      setAllotData({ user_id: '', amount: '', note: '' });
      fetchFund();
      fetchGroup();
      fetchPendingTreasury();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allot money');
    } finally {
      setSubmitting(false);
    }
  };

  // Coin Transfer ---------------------------------------------------------
  const fetchPendingTransfers = useCallback(async () => {
    try {
      const res = await api.get('/coin-transfers/pending');
      setPendingTransfers(res.data.filter((t) => t.group_id === groupId));
    } catch {
      setPendingTransfers([]);
    }
  }, [groupId]);

  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    const amt = parseFloat(transferData.amount);
    if (!transferData.to_user_id) return toast.error(transferMode === 'send' ? 'Pick a recipient' : 'Pick who to request from');
    if (!transferData.amount || isNaN(amt) || amt <= 0) return toast.error('Enter a positive amount');
    setSubmitting(true);
    try {
      if (transferMode === 'send') {
        await api.post('/coin-transfers', {
          group_id: groupId,
          to_user_id: transferData.to_user_id,
          amount: amt,
          note: transferData.note || undefined,
        });
        toast.success('Transfer request sent. Waiting for recipient approval.');
      } else {
        // request_peer or request_fund — selector value is the from_user_id (or 'group_fund')
        await api.post('/coin-transfers/request', {
          group_id: groupId,
          from_user_id: transferData.to_user_id,
          amount: amt,
          note: transferData.note || undefined,
        });
        const target = transferData.to_user_id === 'group_fund'
          ? 'group admins'
          : (group?.members?.find((m) => m.id === transferData.to_user_id)?.name || 'them');
        toast.success(`Request sent to ${target}.`);
      }
      setShowTransferModal(false);
      setTransferData({ to_user_id: '', amount: '', note: '' });
      setTransferMode('send');
      clearTransferDraft();
      fetchPendingTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveTransfer = async (transferId) => {
    // Optimistic — remove from pending list
    const prev = pendingTransfers;
    setPendingTransfers((cur) => cur.filter((t) => t.id !== transferId));
    try {
      await api.post(`/coin-transfers/${transferId}/approve`);
      toast.success('Transfer approved');
      fetchPendingTransfers();
      fetchFund();
      fetchGroup();
    } catch (error) {
      setPendingTransfers(prev);
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectTransfer = async (transferId) => {
    const prev = pendingTransfers;
    setPendingTransfers((cur) => cur.filter((t) => t.id !== transferId));
    try {
      await api.post(`/coin-transfers/${transferId}/reject`);
      toast.success('Transfer rejected');
      fetchPendingTransfers();
    } catch (error) {
      setPendingTransfers(prev);
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleCancelTransfer = async (transferId) => {
    const prev = pendingTransfers;
    setPendingTransfers((cur) => cur.filter((t) => t.id !== transferId));
    try {
      await api.post(`/coin-transfers/${transferId}/cancel`);
      toast.success('Transfer cancelled');
      fetchPendingTransfers();
    } catch (error) {
      setPendingTransfers(prev);
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleReverseTransfer = async (transferId) => {
    if (!window.confirm('Reverse this transfer? This flips the money change back and logs it in audit.')) return;
    try {
      await api.post(`/coin-transfers/${transferId}/reverse`);
      toast.success('Transfer reversed');
      fetchFund();
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse');
    }
  };

  useEffect(() => { if (groupId) fetchPendingTransfers(); }, [groupId, fetchPendingTransfers]);

  // Real-time: refresh relevant data on SSE events
  useEffect(() => {
    if (!groupId) return undefined;
    return onEvent(
      [
        'coin_transfer_request',
        'coin_transfer_approved',
        'coin_transfer_rejected',
        'coin_transfer_reversed',
        'fine_issued',
        'coins_allotted',
        'expense_pending',
        'expense_approved',
        'expense_rejected',
        'expense_reversed',
        'expense_deleted',
        'pledge_received',
        'loan_disbursed',
        'loan_repaid',
        'recurring_expense',
      ],
      (type, payload) => {
        if (payload?.group_id && payload.group_id !== groupId) return;
        fetchPendingTransfers();
        fetchFund();
        fetchGroup(); // also re-pulls expenses + recurring_expenses
        // Live activity dot routing — different events ping different tabs
        if (type.startsWith('expense_')) pingTab('expenses');
        else if (type === 'recurring_expense') pingTab('recurring');
        else if (
          type === 'fine_issued' ||
          type === 'coins_allotted' ||
          type === 'pledge_received' ||
          type === 'loan_disbursed' ||
          type === 'loan_repaid' ||
          type.startsWith('coin_transfer_')
        ) pingTab('fund');
      }
    );
  }, [groupId, onEvent, fetchPendingTransfers, fetchFund, fetchGroup, pingTab]);

  // Real-time: refetch sessions list on session-level events (creation, edits, cancellations,
  // fee charges). Distinct from rsvp_changed which patches an individual session in place.
  // (placed below fetchSessions to avoid TDZ)

  // Real-time: live RSVP updates (someone joined / dropped / admin acted / session edited)
  useEffect(() => {
    if (!groupId) return undefined;
    return onEvent(['rsvp_changed'], (_type, payload) => {
      if (!payload || payload.group_id !== groupId) return;
      // Patch the local sessions list so confirmed/waitlist counts and chips refresh instantly
      setSessions((cur) => cur.map((s) => {
        if (s.id !== payload.session_id) return s;
        const myUid = user?.id;
        let my_rsvp = null;
        let my_position = null;
        let my_position_total = 0;
        if (myUid && payload.confirmed_ids?.includes(myUid)) {
          my_rsvp = 'confirmed';
          my_position = payload.confirmed_ids.indexOf(myUid) + 1;
          my_position_total = payload.confirmed_ids.length;
        } else if (myUid && payload.waitlist_ids?.includes(myUid)) {
          my_rsvp = 'waitlist';
          my_position = payload.waitlist_ids.indexOf(myUid) + 1;
          my_position_total = payload.waitlist_ids.length;
        }
        // iter118 — also patch the parallel name + timestamp arrays so the
        // roster chips refresh in lockstep with the IDs. Backend now ships
        // these fields on the SSE payload; the fallback resolves any UID
        // missing from the payload via the locally-loaded group members
        // (handles legacy clients receiving fresh payloads and vice-versa).
        const localNameMap = Object.fromEntries(
          (group?.members || []).map((m) => [m.id, m.name])
        );
        const resolveName = (uid, fallbackIdx, fallbackList) => {
          // Prefer fresh name from payload; else local member_map; else stale
          // name at the same index from the previous render; else "Unknown".
          if (!uid) return 'Unknown';
          return localNameMap[uid] || fallbackList?.[fallbackIdx] || 'Unknown';
        };
        const newConfirmedIds = payload.confirmed_ids ?? s.confirmed_ids ?? [];
        const newWaitlistIds = payload.waitlist_ids ?? s.waitlist_ids ?? [];
        // iter172 — keep rsvp_out_ids in sync from SSE so the Live RSVP hero
        // bucket counts update without a full refetch.
        const newOutIds = payload.out_ids ?? s.rsvp_out_ids ?? [];
        const confirmed_names = payload.confirmed_names
          ?? newConfirmedIds.map((uid, i) => resolveName(uid, i, s.confirmed_names));
        const waitlist_names = payload.waitlist_names
          ?? newWaitlistIds.map((uid, i) => resolveName(uid, i, s.waitlist_names));
        const out_names = payload.out_names
          ?? newOutIds.map((uid, i) => resolveName(uid, i, s.rsvp_out_names));
        const confirmed_rsvp_ats = payload.confirmed_rsvp_ats ?? s.confirmed_rsvp_ats ?? [];
        const waitlist_rsvp_ats = payload.waitlist_rsvp_ats ?? s.waitlist_rsvp_ats ?? [];
        return {
          ...s,
          status: payload.status ?? s.status,
          rsvp_locked: payload.rsvp_locked ?? s.rsvp_locked,
          confirmed_ids: newConfirmedIds,
          waitlist_ids: newWaitlistIds,
          rsvp_out_ids: newOutIds,
          confirmed_names,
          waitlist_names,
          rsvp_out_names: out_names,
          confirmed_rsvp_ats,
          waitlist_rsvp_ats,
          max_players: payload.max_players ?? s.max_players,
          min_players: payload.min_players ?? s.min_players,
          my_rsvp,
          my_position,
          my_position_total,
        };
      }));
      // Show a non-intrusive toast when someone else acts on the same session you're watching
      if (payload.actor_user_id && payload.actor_user_id !== user?.id && payload.action) {
        // Ping the Sessions tab to draw a live dot when user is on another tab
        pingTab('attendance');
        const verbs = {
          in: `${payload.actor_name || 'Someone'} joined`,
          out: `${payload.actor_name || 'Someone'} dropped out of`,
          admin_in: `${payload.actor_name || 'Admin'} added a member to`,
          admin_out: `${payload.actor_name || 'Admin'} removed a member from`,
          guest_added: `${payload.actor_name || 'Admin'} added a guest to`,
          guest_removed: `${payload.actor_name || 'Admin'} removed a guest from`,
          lock: `${payload.actor_name || 'Admin'} locked RSVP for`,
          cancel: `${payload.actor_name || 'Admin'} cancelled`,
          edited: `${payload.actor_name || 'Admin'} edited`,
        };
        const verb = verbs[payload.action];
        if (verb) toast.message(`${verb} '${payload.title || 'a session'}'`, { duration: 2500 });
      }
      // Reconcile with server occasionally — if the user's status changed, fetchSessions re-derives my_position
      // (Patch above is optimistic; this guarantees consistency if payload misses fields)
    });
  }, [groupId, onEvent, user?.id, pingTab, group?.members]);

  // Quick Settle ----------------------------------------------------------
  const openQuickSettle = async () => {
    setShowQuickSettle(true);
    setLoadingQuickSettle(true);
    try {
      const res = await api.get(`/groups/${groupId}/quick-settle/suggestions`);
      setQuickSettle(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to compute suggestions');
    } finally {
      setLoadingQuickSettle(false);
    }
  };

  const handleQuickSettleNudge = async (from_user_id, to_user_id, amount) => {
    try {
      const res = await api.post(`/groups/${groupId}/quick-settle/notify`, { from_user_id, to_user_id, amount });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send nudge');
    }
  };

  const fetchSessions = useCallback(async () => {
    try {
      const response = await api.get(`/groups/${groupId}/sessions`);
      setSessions(response.data);
    } catch (error) { /* no sessions yet */ }
  }, [groupId]);

  const fetchSessionDefaults = useCallback(async () => {
    try {
      const res = await api.get(`/groups/${groupId}/session-defaults`);
      setSessionDefaults({
        start_time: res.data.start_time || '07:30',
        num_courts: res.data.num_courts || 2,
        days_of_week: res.data.days_of_week || [],
        title_template: res.data.title_template || 'Pickle — {day}',
        auto_create_enabled: !!res.data.auto_create_enabled,
        rsvp_open_hours_before: res.data.rsvp_open_hours_before ?? 12,
        rsvp_close_hours_before: res.data.rsvp_close_hours_before ?? 1,
        duration_minutes: res.data.duration_minutes ?? 120,
        live_indicator_style: res.data.live_indicator_style || 'red_pulse',
      });
    } catch { /* ignore */ }
  }, [groupId]);

  useEffect(() => {
    if (group) {
      fetchSessions();
      fetchSessionDefaults();
    }
  }, [group, fetchSessions, fetchSessionDefaults]);

  // Real-time: refetch sessions list on session-level events (creation, edits, cancellations,
  // fee charges). Distinct from rsvp_changed which patches an individual session in place.
  useEffect(() => {
    if (!groupId) return undefined;
    return onEvent(
      [
        'session_created',
        'session_cancelled',
        'session_updated',
        'session_fee_charged',
        'rsvp_admin_added',
        'rsvp_admin_removed',
        'rsvp_locked',
        'rsvp_promoted',
        'rsvp_demoted',
        'session_needs_guests',
      ],
      (type, payload) => {
        if (payload?.group_id && payload.group_id !== groupId) return;
        fetchSessions();
        pingTab('attendance');
      }
    );
  }, [groupId, onEvent, fetchSessions, pingTab]);

  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (!newSession.title || !newSession.date) {
      toast.error('Enter title and date');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: newSession.title,
        date: newSession.date,
        num_courts: newSession.num_courts,
        start_time: newSession.start_time || sessionDefaults.start_time || '07:30',
        session_fee: newSession.session_fee === '' || newSession.session_fee == null
          ? null : Number(newSession.session_fee),
        venue_payer: newSession.venue_payer || null,
      };
      if (newSession.rsvp_enabled) {
        // Compute open/close based on preset, using the session's actual start_time
        const [hh, mm] = String(payload.start_time).split(':').map(x => parseInt(x) || 0);
        const [yy, mo, dd] = newSession.date.split('-').map(x => parseInt(x) || 0);
        const sessionDtIst = new Date(Date.UTC(yy, mo - 1, dd, hh - 5, mm - 30)); // IST → UTC
        let opens, closes;
        const preset = newSession.rsvp_preset;
        if (preset === 'open_now_close_when_full') {
          opens = new Date();
          closes = sessionDtIst;
        } else if (preset === 'open_now_close_1h_before') {
          opens = new Date();
          closes = new Date(sessionDtIst.getTime() - 60 * 60 * 1000);
        } else {
          if (!newSession.rsvp_opens_at || !newSession.rsvp_closes_at) {
            toast.error('Pick open and close times');
            setSubmitting(false);
            return;
          }
          opens = new Date(newSession.rsvp_opens_at);
          closes = new Date(newSession.rsvp_closes_at);
        }
        payload.rsvp_opens_at = opens.toISOString();
        payload.rsvp_closes_at = closes.toISOString();
      }
      await api.post(`/groups/${groupId}/sessions`, payload);
      toast.success('Session created');
      setShowCreateSession(false);
      setNewSession({ title: '', date: '', num_courts: sessionDefaults.num_courts || 2, start_time: sessionDefaults.start_time || '07:30', rsvp_enabled: true, rsvp_preset: 'open_now_close_1h_before', rsvp_opens_at: '', rsvp_closes_at: '' });
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create session');
    } finally {
      setSubmitting(false);
    }
  };

  // Pre-fill New Session form with a template
  const applySessionTemplate = (tmpl) => {
    const todayStr = new Date().toISOString().split('T')[0];
    if (tmpl === 'defaults_morning_730') {
      setNewSession({
        title: `Pickle — ${sessionDefaults.start_time || '07:30'}`,
        date: todayStr,
        num_courts: sessionDefaults.num_courts || 2,
        start_time: sessionDefaults.start_time || '07:30',
        rsvp_enabled: true,
        rsvp_preset: 'open_now_close_1h_before',
        rsvp_opens_at: '',
        rsvp_closes_at: '',
      });
    } else if (tmpl === 'morning_7') {
      setNewSession({
        title: 'Pickle — 7:00 AM',
        date: todayStr,
        num_courts: sessionDefaults.num_courts || 2,
        start_time: '07:00',
        rsvp_enabled: true,
        rsvp_preset: 'open_now_close_1h_before',
        rsvp_opens_at: '',
        rsvp_closes_at: '',
      });
    } else if (tmpl === 'saturday_random') {
      setNewSession({
        title: 'Saturday pickle',
        date: '',
        num_courts: sessionDefaults.num_courts || 2,
        start_time: sessionDefaults.start_time || '07:30',
        rsvp_enabled: true,
        rsvp_preset: 'open_now_close_when_full',
        rsvp_opens_at: '',
        rsvp_closes_at: '',
      });
    } else if (tmpl === 'clear') {
      setNewSession({ title: '', date: '', num_courts: sessionDefaults.num_courts || 2, start_time: sessionDefaults.start_time || '07:30', rsvp_enabled: true, rsvp_preset: 'open_now_close_1h_before', rsvp_opens_at: '', rsvp_closes_at: '' });
    }
  };

  // When session defaults load, sync the New Session form's baseline
  useEffect(() => {
    setNewSession((prev) => ({
      ...prev,
      num_courts: prev.num_courts || sessionDefaults.num_courts || 2,
      start_time: prev.start_time || sessionDefaults.start_time || '07:30',
    }));
  }, [sessionDefaults.start_time, sessionDefaults.num_courts]);

  const openNewSessionWithDefaults = () => {
    applySessionTemplate('defaults_morning_730');
    setShowCreateSession(true);
  };

  const fetchSessionDefaults2 = fetchSessionDefaults; // alias used below in JSX if needed

  const handleSaveSessionDefaults = async (e) => {
    e.preventDefault();
    // iter289 — same Fee Save Guard as Fine Defaults (fee is editable here too)
    const feeVal = parseFloat(fineDefaults.session_fee) || 0;
    const prevFee = parseFloat(group?.fine_defaults?.session_fee) || 0;
    if (feeVal <= 0 && prevFee > 0) {
      const ok = window.confirm(`⚠️ Session fee is blank/0 (it was ₹${prevFee}). The "Charge fee" button will disappear on ALL sessions of this group. Save anyway?`);
      if (!ok) return;
    }
    setSavingSessionDefaults(true);
    try {
      await api.put(`/groups/${groupId}/session-defaults`, sessionDefaults);
      await api.put(`/groups/${groupId}/fine-defaults`, fineDefaults);
      toast.success('Session defaults saved');
      setShowSessionDefaults(false);
      fetchFund();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSessionDefaults(false);
    }
  };

  const openDuplicateSession = (session) => {
    setDuplicateSource(session);
    // Default to +7 days from source
    try {
      const [yy, mo, dd] = session.date.split('-').map(n => parseInt(n) || 0);
      const d = new Date(Date.UTC(yy, mo - 1, dd + 7));
      setDuplicateDate(d.toISOString().split('T')[0]);
    } catch {
      setDuplicateDate('');
    }
  };

  const handleDuplicateSession = async (e) => {
    e.preventDefault();
    if (!duplicateSource || !duplicateDate) return;
    setSubmitting(true);
    try {
      await api.post(`/sessions/${duplicateSource.id}/duplicate`, { date: duplicateDate });
      toast.success(`Cloned to ${duplicateDate}`);
      setDuplicateSource(null);
      setDuplicateDate('');
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditSession = (session) => {
    setEditSession(session);
    setEditForm({
      title: session.title || '',
      date: session.date || '',
      start_time: session.start_time || '07:30',
      num_courts: session.num_courts || 1,
      max_players: session.max_players || 6,
      min_players: session.min_players || 5,
      // iter175 — session notes / venue info
      notes: session.notes || '',
      court_info: session.court_info || '',
      location_url: session.location_url || '',
      // iter309 — per-session fee (blank = group default)
      session_fee: session.session_fee ?? '',
      // iter311 — explicit RSVP window (datetime-local strings, local tz)
      rsvp_opens_at: isoToLocalInput(session.rsvp_opens_at),
      rsvp_closes_at: isoToLocalInput(session.rsvp_closes_at),
    });
  };

  const handleSaveEditSession = async (e) => {
    e.preventDefault();
    if (!editSession) return;
    setEditBusy(true);
    try {
      const payload = {
        ...editForm,
        session_fee: editForm.session_fee === '' || editForm.session_fee == null ? -1 : Number(editForm.session_fee),
      };
      // iter311 — only send the RSVP window when the admin actually changed it.
      const origOpens = isoToLocalInput(editSession.rsvp_opens_at);
      const origCloses = isoToLocalInput(editSession.rsvp_closes_at);
      payload.rsvp_opens_at = (editForm.rsvp_opens_at && editForm.rsvp_opens_at !== origOpens)
        ? new Date(editForm.rsvp_opens_at).toISOString() : null;
      payload.rsvp_closes_at = (editForm.rsvp_closes_at && editForm.rsvp_closes_at !== origCloses)
        ? new Date(editForm.rsvp_closes_at).toISOString() : null;
      const res = await api.patch(`/sessions/${editSession.id}`, payload);
      const changes = res.data?.changes || [];
      if (changes.length === 0) {
        toast('No changes to save', { icon: 'ℹ️' });
      } else {
        toast.success(`Session updated (${changes.length} change${changes.length === 1 ? '' : 's'})`);
      }
      setEditSession(null);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setEditBusy(false);
    }
  };

  // RSVP handlers
  const handleRsvp = async (sessionId, action, sessionMeta) => {
    // Warn user if this is a late-cancel (locked + confirmed + within threshold hours)
    if (action === 'out' && sessionMeta?.rsvp_locked && sessionMeta?.my_rsvp === 'confirmed') {
      const lateCancelFee = fineDefaults.late_cancel ?? 300;
      const lateCancelHours = fineDefaults.late_cancel_hours ?? 15;
      // Compute hours to session (start_time defaults to 19:00 IST)
      const [hh, mm] = String(sessionMeta.start_time || '19:00').split(':').map(x => parseInt(x) || 0);
      const [yy, mo, dd] = sessionMeta.date.split('-').map(x => parseInt(x) || 0);
      // IST = UTC+5:30
      const sessionDt = new Date(Date.UTC(yy, mo - 1, dd, hh - 5, mm - 30));
      const hoursToSession = (sessionDt.getTime() - Date.now()) / 3600000;
      if (lateCancelFee > 0 && hoursToSession < lateCancelHours && hoursToSession > 0) {
        const ok = window.confirm(`Dropping out now will charge you ${formatNXC(lateCancelFee)} (late-cancel fine — less than ${lateCancelHours}h before session). Continue?`);
        if (!ok) return;
      }
    }
    // Optimistic — update local sessions list immediately so the IN/OUT toggle feels instant.
    // Backend is the source of truth for capacity/waitlist edge cases — fetchSessions reconciles.
    const prev = sessions;
    const myUid = user?.id;
    if (myUid) {
      setSessions((cur) => cur.map((s) => {
        if (s.id !== sessionId) return s;
        const inIds = [...(s.rsvp_in_ids || [])];
        const wait = [...(s.waitlist_ids || [])];
        const confirmed = [...(s.confirmed_ids || [])];
        const idxIn = inIds.indexOf(myUid);
        const idxWait = wait.indexOf(myUid);
        const idxConf = confirmed.indexOf(myUid);
        if (action === 'in' && idxIn === -1 && idxWait === -1) {
          // Determine confirmed vs waitlist by current capacity
          const cap = s.max_players || Infinity;
          if (confirmed.length < cap) {
            inIds.push(myUid);
            confirmed.push(myUid);
          } else {
            inIds.push(myUid);
            wait.push(myUid);
          }
        } else if (action === 'out') {
          if (idxIn >= 0) inIds.splice(idxIn, 1);
          if (idxWait >= 0) wait.splice(idxWait, 1);
          if (idxConf >= 0) confirmed.splice(idxConf, 1);
        }
        return { ...s, rsvp_in_ids: inIds, waitlist_ids: wait, confirmed_ids: confirmed };
      }));
    }
    try {
      const res = await api.post(`/sessions/${sessionId}/rsvp`, { action });
      if (res.data.fine_applied) {
        toast.warning(`RSVP dropped. Late-cancel fine of ${formatNXC(res.data.fine_applied.amount)} charged.`);
      } else if (action === 'in') {
        // Build a precise toast from the server's queue position + timestamp
        // so the user sees their EXACT slot — confirmed (#3 of 12 at 17:00:00.412)
        // or waitlist (#2 of 4).
        const pos = res.data.position;
        const total = res.data.position_total;
        const at = res.data.rsvp_at
          ? new Date(res.data.rsvp_at).toLocaleTimeString('en-IN', {
              hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
            })
          : null;
        if (res.data.status === 'confirmed') {
          const base = pos && total ? `You're confirmed — #${pos} of ${total}` : `You're confirmed`;
          toast.success(at ? `${base} · locked in at ${at}` : base);
        } else if (res.data.status === 'waitlist') {
          const ahead = pos && pos > 1 ? `${pos - 1} ahead of you` : 'next in line';
          const base = pos && total ? `Waitlisted — #${pos} of ${total} (${ahead})` : `On the waitlist`;
          toast.info(at ? `${base} · at ${at}` : base);
        } else {
          toast.success('RSVP confirmed');
        }
      } else {
        toast.success(`RSVP withdrawn`);
      }
      fetchSessions();
      fetchFund();
      fetchGroup();
    } catch (err) {
      setSessions(prev);
      toast.error(err.response?.data?.detail || 'Failed to RSVP');
    }
  };

  const handleLockRsvp = async (sessionId) => {
    if (!window.confirm('Lock RSVP now? No more changes allowed.')) return;
    const prev = sessions;
    setSessions((cur) => cur.map((s) => s.id === sessionId ? { ...s, rsvp_locked: true } : s));
    try {
      const res = await api.post(`/sessions/${sessionId}/lock-rsvp`);
      toast.success(res.data.message);
      fetchSessions();
    } catch (err) {
      setSessions(prev);
      toast.error(err.response?.data?.detail || 'Failed to lock');
    }
  };

  const handleAddGuest = (sessionId) => {
    // iter197.4 — open phone-first modal instead of window.prompt
    const s = sessions.find((x) => x.id === sessionId);
    setAddGuestSessionId(sessionId);
    setAddGuestSessionTitle(s?.title || '');
  };

  const handleRemoveGuest = async (sessionId, guestId) => {
    if (!window.confirm('Remove this guest?')) return;
    const prev = sessions;
    setSessions((cur) => cur.map((s) => s.id === sessionId
      ? { ...s, external_guests: (s.external_guests || []).filter((g) => g.id !== guestId) }
      : s));
    try {
      await api.delete(`/sessions/${sessionId}/guest/${guestId}`);
      toast.success('Guest removed');
      fetchSessions();
    } catch (err) {
      setSessions(prev);
      toast.error(err.response?.data?.detail || 'Failed to remove guest');
    }
  };

  const handleCancelSession = async (sessionId) => {
    if (!window.confirm('Cancel this session? Any late-cancel fines for this session will be refunded automatically.')) return;
    // Optimistic UI — flip to cancelled status immediately
    const prev = sessions;
    setSessions((cur) => cur.map((s) => s.id === sessionId ? { ...s, status: 'cancelled' } : s));
    try {
      const res = await api.post(`/sessions/${sessionId}/cancel`);
      const refundCount = res.data?.refund_count || 0;
      const refundTotal = res.data?.refund_total || 0;
      if (refundCount > 0) {
        toast.success(`Session cancelled — refunded ${refundCount} late-cancel fine(s) totalling \u20b9${Math.round(refundTotal)}`);
      } else {
        toast.success('Session cancelled');
      }
      fetchSessions();
      fetchGroup();
    } catch (err) {
      setSessions(prev);
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  // Admin-RSVP a member on their behalf (opens picker)
  const [adminRsvpModal, setAdminRsvpModal] = useState(null); // { session_id, eligible_members }
  const [adminRsvpBusy, setAdminRsvpBusy] = useState(false);

  const openAdminRsvp = (session) => {
    const inIds = new Set([...(session.rsvp_in_ids || []), ...(session.waitlist_ids || []), ...(session.confirmed_ids || [])]);
    const eligible = (group?.members || []).filter((m) => !inIds.has(m.id));
    setAdminRsvpModal({ session_id: session.id, session_title: session.title, eligible });
  };

  const handleAdminRsvpAdd = async (userId) => {
    if (!adminRsvpModal) return;
    setAdminRsvpBusy(true);
    const sessionId = adminRsvpModal.session_id;
    // Optimistic — drop from eligible AND push into the local session's confirmed_ids
    const prevEligible = adminRsvpModal;
    const prevSessions = sessions;
    setAdminRsvpModal((cur) => cur ? { ...cur, eligible: cur.eligible.filter((m) => m.id !== userId) } : cur);
    setSessions((cur) => cur.map((s) => {
      if (s.id !== sessionId) return s;
      const cap = s.max_players || Infinity;
      const inIds = [...(s.rsvp_in_ids || []), userId];
      const confirmed = [...(s.confirmed_ids || [])];
      const wait = [...(s.waitlist_ids || [])];
      if (confirmed.length < cap) confirmed.push(userId); else wait.push(userId);
      return { ...s, rsvp_in_ids: inIds, confirmed_ids: confirmed, waitlist_ids: wait };
    }));
    try {
      const res = await api.post(`/sessions/${sessionId}/admin-rsvp`, {
        user_id: userId, action: 'in',
      });
      toast.success(res.data?.message || 'Member added');
      fetchSessions();
    } catch (err) {
      // Restore on failure
      setAdminRsvpModal(prevEligible);
      setSessions(prevSessions);
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdminRsvpBusy(false);
    }
  };

  // iter145 — admin-removes a member from a session WITHOUT a fine.
  // Used when a confirmed player drops last-minute and admin needs to swap them
  // for someone on the waitlist (auto-promotion happens server-side).
  const handleAdminRsvpRemove = async (sessionId, userId, memberName) => {
    if (!sessionId || !userId) return;
    const ok = window.confirm(
      `Remove ${memberName || 'this member'} from the session?\n\n` +
      `• No late-cancel fine will be issued (admin action).\n` +
      `• The next person on the waitlist auto-promotes to confirmed.\n` +
      `• Both members will be notified.`
    );
    if (!ok) return;
    try {
      const res = await api.post(`/sessions/${sessionId}/admin-rsvp`, {
        user_id: userId, action: 'out',
      });
      toast.success(res.data?.message || `${memberName || 'Member'} removed`);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const openAttendance = (session) => {
    setAttendanceTouched(false);
    setActiveSession(session);
    setPresentIds(session.present_ids || []);
  };

  const togglePresent = (memberId) => {
    setPresentIds(prev => prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]);
  };

  // iter48: 4-state attendance — toggle a member into one of {ontime, late_under, late_above, absent}
  const setAttendanceState = (memberId, state) => {
    setAttendanceTouched(true);
    // iter209/iter210 — When admin marks a member as "didn't play" (ABSENT
    // or LATE-CANCEL) on an originally-confirmed member, offer to promote
    // the next waitlist person to fill the slot. When admin reverses it
    // (back to ontime/late), and there's a promotion linked to this
    // absentee, ask if we should demote the promoted person back.
    //
    // FIX (iter210.2): Don't require a state TRANSITION — when the modal
    // first opens, presentIds=[] so every confirmed member starts as
    // "absent" by default. The previous transition check (!wasAbsent &&
    // willBeAbsent) never fired because the member was always already
    // absent. New trigger: fire whenever the resulting state is a
    // "didn't play" state AND the absentee isn't already linked to an
    // active promotion AND the dialog isn't currently open for them.
    const s = activeSession;
    if (s && !s._skipWaitlistPrompts) {
      const previousState = getAttendanceState(memberId);
      const didntPlay = (st) => st === 'absent' || st === 'late_cancel';
      const willDidntPlay = didntPlay(state);
      const wasDidntPlay = didntPlay(previousState);

      // Case 1: admin marked member as ABSENT or LATE-CANCEL → offer promotion
      if (willDidntPlay) {
        const originally = s.originally_confirmed_ids || s.confirmed_ids || [];
        const waitlist = s.waitlist_ids || [];
        const alreadyPromotedAbsentees = new Set(
          (s.waitlist_promotions || [])
            .filter((p) => !p.demoted_at)
            .map((p) => p.absentee_user_id),
        );
        if (
          originally.includes(memberId) &&
          waitlist.length > 0 &&
          !alreadyPromotedAbsentees.has(memberId) &&
          promotePrompt?.absentee_id !== memberId
        ) {
          const memberMap = Object.fromEntries((group?.members || []).map((m) => [m.id, m]));
          const absentee = memberMap[memberId];
          const waitlistUserId = waitlist[0];
          const waitlistUser = memberMap[waitlistUserId];
          if (absentee && waitlistUser) {
            setPromotePrompt({
              absentee_id: memberId,
              absentee_name: absentee.name,
              waitlist_user_id: waitlistUserId,
              waitlist_user_name: waitlistUser.name,
            });
          }
        }
      }

      // Case 2: admin reversed a "didn't play" → "played" — offer to demote
      if (wasDidntPlay && !willDidntPlay) {
        const linked = (s.waitlist_promotions || []).find(
          (p) => p.absentee_user_id === memberId && !p.demoted_at,
        );
        if (linked) {
          const memberMap = Object.fromEntries((group?.members || []).map((m) => [m.id, m]));
          const promotedUser = memberMap[linked.promoted_user_id];
          const absentee = memberMap[memberId];
          if (promotedUser && absentee) {
            setDemotePrompt({
              promoted_user_id: linked.promoted_user_id,
              promoted_user_name: promotedUser.name,
              absentee_name: absentee.name,
            });
          }
        }
      }
    }

    if (state === 'absent') {
      setPresentIds((prev) => prev.filter((id) => id !== memberId));
      setLateUnderIds((prev) => prev.filter((id) => id !== memberId));
      setLateAboveIds((prev) => prev.filter((id) => id !== memberId));
      setLateCancelIds((prev) => prev.filter((id) => id !== memberId));
      return;
    }
    // iter210 — late_cancel is "absent but charged ₹300 instead of ₹500"
    if (state === 'late_cancel') {
      setPresentIds((prev) => prev.filter((id) => id !== memberId));
      setLateUnderIds((prev) => prev.filter((id) => id !== memberId));
      setLateAboveIds((prev) => prev.filter((id) => id !== memberId));
      setLateCancelIds((prev) => prev.includes(memberId) ? prev : [...prev, memberId]);
      return;
    }
    // All non-absent + non-late-cancel states are "present"
    setPresentIds((prev) => prev.includes(memberId) ? prev : [...prev, memberId]);
    setLateCancelIds((prev) => prev.filter((id) => id !== memberId));
    if (state === 'ontime') {
      setLateUnderIds((prev) => prev.filter((id) => id !== memberId));
      setLateAboveIds((prev) => prev.filter((id) => id !== memberId));
    } else if (state === 'late_under') {
      setLateUnderIds((prev) => prev.includes(memberId) ? prev : [...prev, memberId]);
      setLateAboveIds((prev) => prev.filter((id) => id !== memberId));
    } else if (state === 'late_above') {
      setLateUnderIds((prev) => prev.filter((id) => id !== memberId));
      setLateAboveIds((prev) => prev.includes(memberId) ? prev : [...prev, memberId]);
    }
  };

  // iter325 — somebody cancelled or didn't show, bring in whoever turned up
  const addReplacement = async (payload, label) => {
    setReplacementBusy(label);
    try {
      const r = await api.post(`/sessions/${activeSession.id}/add-replacement`, payload);
      toast.success(r.data?.message || `${label} added`);
      setReplacementGuest('');
      const fresh = await api.get(`/sessions/${activeSession.id}/detail`);
      setActiveSession(fresh.data.session || fresh.data);
      if (payload.user_id) setPresentIds((prev) => [...new Set([...prev, payload.user_id])]);
      fetchSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add them');
    } finally {
      setReplacementBusy(null);
    }
  };

  const getAttendanceState = (memberId) => {
    if (lateCancelIds.includes(memberId)) return 'late_cancel';
    if (!presentIds.includes(memberId)) return 'absent';
    if (lateUnderIds.includes(memberId)) return 'late_under';
    if (lateAboveIds.includes(memberId)) return 'late_above';
    return 'ontime';
  };

  // iter209 — Promote next waitlist person to replace absentee
  const handlePromoteWaitlist = async () => {
    if (!promotePrompt || !activeSession) return;
    setPromoting(true);
    try {
      const res = await api.post(`/sessions/${activeSession.id}/promote-waitlist`, {
        absentee_user_id: promotePrompt.absentee_id,
        promoted_user_id: promotePrompt.waitlist_user_id,
      });
      const notif = res.data?.notification || {};
      let msg = `${promotePrompt.waitlist_user_name} promoted from waitlist`;
      if (notif.sent) msg += ' · WhatsApp sent';
      else if (notif.reason && notif.reason.startsWith('too_close_to_kickoff')) msg += ' · No WhatsApp (within 30 min of kickoff)';
      toast.success(msg);
      // Refresh sessions + the active modal session
      await fetchSessions();
      // Refetch full session to refresh confirmed_ids + waitlist
      const fresh = await api.get(`/sessions/${activeSession.id}/detail`);
      if (fresh.data?.session) setActiveSession({ ...fresh.data.session });
      setPromotePrompt(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote');
    } finally {
      setPromoting(false);
    }
  };

  // iter209 — Demote a previously-promoted waitlist person back
  const handleDemotePromoted = async () => {
    if (!demotePrompt || !activeSession) return;
    setPromoting(true);
    try {
      await api.post(`/sessions/${activeSession.id}/demote-promoted`, {
        promoted_user_id: demotePrompt.promoted_user_id,
      });
      toast.success(`${demotePrompt.promoted_user_name} returned to waitlist`);
      await fetchSessions();
      const fresh = await api.get(`/sessions/${activeSession.id}/detail`);
      if (fresh.data?.session) setActiveSession({ ...fresh.data.session });
      setDemotePrompt(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to demote');
    } finally {
      setPromoting(false);
    }
  };

  const handleSaveAttendance = async () => {
    if (!activeSession) return;
    setSubmitting(true);
    try {
      const response = await api.put(`/sessions/${activeSession.id}/attendance`, {
        present_ids: presentIds,
        late_under_ids: lateUnderIds,
        late_above_ids: lateAboveIds,
        late_cancel_ids: lateCancelIds,
      });
      const fines = response.data?.late_fines_issued || [];
      const skipped = response.data?.late_fines_skipped || [];
      let msg = `Attendance saved: ${response.data.present_count} present, ${response.data.absent_count} absent`;
      if (fines.length > 0) {
        const total = fines.reduce((s, f) => s + (f.amount || 0), 0);
        msg += ` · ${fines.length} fine(s) issued (\u20b9${Math.round(total)})`;
      }
      if (skipped.length > 0) {
        msg += ` · ${skipped.length} skipped (balance limit)`;
      }
      toast.success(msg);
      setActiveSession(null);
      setLateUnderIds([]);
      setLateAboveIds([]);
      setLateCancelIds([]);
      fetchSessions();
      fetchFund();
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoFine = async (sessionId) => {
    setSubmitting(true);
    try {
      const response = await api.post(`/sessions/${sessionId}/auto-fine`);
      toast.success(response.data.message);
      fetchSessions();
      fetchFund();
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to auto-fine');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChargeSessionFee = async (sessionId) => {
    const fee = fineDefaults.session_fee || 0;
    if (fee <= 0) {
      toast.error('Set a session fee first under Fine Defaults');
      setShowFineDefaultsModal(true);
      return;
    }
    if (!window.confirm(`Charge ${formatNXC(fee)} per attendee? This will deduct from each present member's balance.`)) return;
    setSubmitting(true);
    try {
      const response = await api.post(`/sessions/${sessionId}/charge-fee`);
      toast.success(response.data.message);
      fetchSessions();
      fetchFund();
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to charge session fee');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Delete this attendance session?')) return;
    const prev = sessions;
    await runWithUndo({
      applyOptimistic: () => setSessions((cur) => cur.filter((s) => s.id !== sessionId)),
      restore: () => setSessions(prev),
      commit: async () => {
        await api.delete(`/sessions/${sessionId}`);
        fetchSessions();
      },
      toastMsg: 'Session deleted',
      undoMsg: 'Session restored',
      onError: (err) => toast.error(err.response?.data?.detail || 'Failed to delete session'),
    });
  };

  // Open Group Settings modal (iter37)
  const openGroupSettings = async () => {
    setGroupSettings({
      name: group?.name || '',
      description: group?.description || '',
      currency: group?.currency || 'INR',
      sport: group?.sport || '',
      currency_name: group?.currency_name || 'Coins',
      emoji: group?.emoji || '',
      treasurer_id: group?.treasurer_id || '',
      ledger_pdf_freq: group?.ledger_pdf_schedule?.frequency || 'off',
      ledger_pdf_day: group?.ledger_pdf_schedule?.day_of_month || 1,
      allow_external_donations: !!group?.allow_external_donations,
      expense_approval_policy: group?.expense_approval_policy || 'admin',
    });
    // iter223 — lazy-load sports catalog when settings modal opens
    if (sportsCatalog.length === 0) {
      api.get('/sports').then((r) => setSportsCatalog(r.data?.rows || [])).catch(() => {});
    }
    // iter169 — seed party composition + kid weight from group
    // iter183 — also seed `nights` (default to trip duration when computable, else blank)
    const tripStart = group?.trip_start_date ? new Date(group.trip_start_date) : null;
    const tripEnd = group?.trip_end_date ? new Date(group.trip_end_date) : null;
    let defaultNights = 0;
    if (tripStart && tripEnd && !isNaN(tripStart) && !isNaN(tripEnd)) {
      defaultNights = Math.max(0, Math.round((tripEnd - tripStart) / 86400000));
    }
    const seedParty = {};
    (group?.members || []).forEach((m) => {
      const c = (group?.party_composition || {})[m.id] || {};
      seedParty[m.id] = {
        adults: Number.isFinite(c.adults) ? c.adults : 1,
        kids: Number.isFinite(c.kids) ? c.kids : 0,
        nights: Number.isFinite(c.nights) ? c.nights : defaultNights,
      };
    });
    setPartyDraft(seedParty);
    setKidWeightDraft(Number.isFinite(group?.kid_head_weight) ? group.kid_head_weight : 1);
    // iter181 — Seed drinker tags (default true) + trip rooms
    const seedDrinker = {};
    (group?.members || []).forEach((m) => {
      const t = (group?.member_tags || {})[m.id];
      seedDrinker[m.id] = t ? (t.drinker !== false) : true;
    });
    setDrinkerDraft(seedDrinker);
    setRoomsDraft((group?.trip_rooms || []).map((r) => ({
      id: r.id,
      label: r.label || '',
      occupant_user_ids: [...(r.occupant_user_ids || [])],
    })));
    // iter171 — Seed trip_budget draft (defaults on first open)
    const srcBudget = (group?.trip_budget && group.trip_budget.length > 0) ? group.trip_budget : TRIP_BUDGET_DEFAULTS;
    setBudgetDraft(srcBudget.map((b) => ({ ...b, expense_categories: [...(b.expense_categories || [])] })));
    setShowGroupSettings(true);
    if (currencies.length === 0) {
      try {
        const res = await api.get('/currencies');
        setCurrencies(res.data.currencies || []);
      } catch { /* silent */ }
    }
  };

  // iter169 — Save trip party composition + kid weight (admin-only, trip-only)
  // iter181 — Also saves member_tags (drinker per member)
  const handleSaveParty = async () => {
    if (!isAdmin) return;
    setSavingParty(true);
    try {
      // Strip out zeros + non-positive entries so the doc stays clean
      const clean = {};
      Object.entries(partyDraft).forEach(([uid, c]) => {
        const a = Math.max(0, parseInt(c.adults, 10) || 0);
        const k = Math.max(0, parseInt(c.kids, 10) || 0);
        if (a > 0 || k > 0) {
          const entry = { adults: a, kids: k };
          // iter183 — pass nights when > 0
          const n = parseFloat(c.nights);
          if (Number.isFinite(n) && n > 0) entry.nights = n;
          clean[uid] = entry;
        }
      });
      // iter181 — member_tags payload
      const tags = {};
      (group?.members || []).forEach((m) => {
        tags[m.id] = { drinker: drinkerDraft[m.id] !== false };
      });
      await api.put(`/groups/${groupId}`, {
        party_composition: clean,
        kid_head_weight: Math.max(0, Math.min(2, parseFloat(kidWeightDraft) || 0)),
        member_tags: tags,
      });
      toast.success('Trip party settings saved');
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save party settings');
    } finally {
      setSavingParty(false);
    }
  };

  // iter181 — Save trip rooms (admin-only, trip-only)
  const handleSaveRooms = async () => {
    if (!isAdmin) return;
    setSavingRooms(true);
    try {
      const clean = roomsDraft
        .map((r) => ({
          id: r.id,
          label: (r.label || '').trim(),
          occupant_user_ids: (r.occupant_user_ids || []).filter((uid) => (group?.member_ids || []).includes(uid)),
        }))
        .filter((r) => r.occupant_user_ids.length > 0);
      await api.put(`/groups/${groupId}`, { trip_rooms: clean });
      toast.success(`Saved ${clean.length} room${clean.length === 1 ? '' : 's'}`);
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rooms');
    } finally {
      setSavingRooms(false);
    }
  };

  const addRoom = () => {
    setRoomsDraft((prev) => [...prev, {
      id: `room_${Date.now()}`,
      label: `Room ${prev.length + 1}`,
      occupant_user_ids: [],
    }]);
  };
  const removeRoom = (rid) => setRoomsDraft((prev) => prev.filter((r) => r.id !== rid));
  const toggleRoomOccupant = (rid, uid) => {
    setRoomsDraft((prev) => prev.map((r) => {
      if (r.id !== rid) return r;
      const has = (r.occupant_user_ids || []).includes(uid);
      return { ...r, occupant_user_ids: has ? r.occupant_user_ids.filter((x) => x !== uid) : [...(r.occupant_user_ids || []), uid] };
    }));
  };
  const renameRoom = (rid, label) => {
    setRoomsDraft((prev) => prev.map((r) => r.id === rid ? { ...r, label } : r));
  };

  // iter171 — Save trip budget (admin-only, trip-only)
  const handleSaveBudget = async () => {
    if (!isAdmin) return;
    setSavingBudget(true);
    try {
      const cleaned = (budgetDraft || [])
        .map((b, idx) => ({
          key: (b.key || `item${idx}`).toLowerCase().replace(/[^a-z0-9_-]/g, '_'),
          label: (b.label || '').trim() || `Item ${idx + 1}`,
          amount: Math.max(0, parseFloat(b.amount) || 0),
          expense_categories: (b.expense_categories || []).map((c) => c.toLowerCase()),
        }))
        .filter((b) => b.label);
      await api.put(`/groups/${groupId}`, { trip_budget: cleaned });
      toast.success('Trip budget saved');
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save budget');
    } finally {
      setSavingBudget(false);
    }
  };

  const handleSaveGroupSettings = async (e) => {
    e.preventDefault();
    if (!groupSettings.name.trim()) { toast.error('Group name required'); return; }
    setSavingGroupSettings(true);
    try {
      await api.put(`/groups/${groupId}`, {
        name: groupSettings.name.trim(),
        description: groupSettings.description?.trim() || '',
        currency: groupSettings.currency,
        // iter233 — coin name + per-group cash holder
        currency_name: groupSettings.currency_name?.trim() || 'Coins',
        emoji: groupSettings.emoji || '',
        treasurer_id: groupSettings.treasurer_id || '',
        // iter240 — scheduled ledger PDFs
        ledger_pdf_schedule: {
          frequency: groupSettings.ledger_pdf_freq || 'off',
          day_of_month: Number(groupSettings.ledger_pdf_day) || 1,
        },
        // iter256 — external donation visibility toggle
        allow_external_donations: !!groupSettings.allow_external_donations,
        expense_approval_policy: groupSettings.expense_approval_policy || 'admin',
        // iter223 — persist sport when provided (backend ignores if not set)
        ...(groupSettings.sport !== undefined ? { sport: groupSettings.sport || null } : {}),
      });
      toast.success('Group settings saved');
      setShowGroupSettings(false);
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingGroupSettings(false);
    }
  };

  // Toggle a single feature override (admin can re-enable/disable per group).
  const handleToggleFeature = async (featureKey, currentValue) => {
    if (!isAdmin) return;
    setSavingFeatures(true);
    try {
      await api.put(`/groups/${groupId}`, {
        feature_overrides: { [featureKey]: !currentValue },
      });
      toast.success(!currentValue ? 'Feature enabled' : 'Feature hidden');
      fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update feature');
    } finally {
      setSavingFeatures(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Leave this group? You will lose access to its expenses and balances.')) return;
    try {
      await api.delete(`/groups/${groupId}/members/${user.id}`);
      toast.success('You left the group');
      navigate('/groups');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave');
    }
  };

  // AI Observations (iter37)
  const fetchAiObservations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get(`/ai-observations/${groupId}`, { params: { limit: 50 } });
      setAiObservations(res.data.observations || []);
      setAiActiveCount(res.data.active_count || 0);
      setAiSeverity(res.data.by_severity || { low: 0, medium: 0, high: 0 });
    } catch { /* silent */ }
  }, [groupId, isAdmin]);

  useEffect(() => {
    fetchAiObservations();
    const t = setInterval(fetchAiObservations, 60000);
    return () => clearInterval(t);
  }, [fetchAiObservations]);

  const handleDismissObservation = async (obsId, reason = '') => {
    try {
      await api.post(`/ai-observations/${obsId}/dismiss`, { reason });
      toast.success('Dismissed');
      fetchAiObservations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to dismiss');
    }
  };

  const handleRunAiScan = async () => {
    setAiObsLoading(true);
    try {
      const res = await api.post(`/ai-observations/scan/${groupId}`);
      toast.success(res.data.message);
      fetchAiObservations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setAiObsLoading(false);
    }
  };

  // AI Co-Admin (iter38)
  const fetchCoAdminSettings = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get(`/co-admin/settings/${groupId}`);
      setCoAdminSettings(res.data);
    } catch { /* silent */ }
  }, [groupId, isAdmin]);

  const fetchCoAdminActions = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get(`/co-admin/actions/${groupId}`, { params: { limit: 50 } });
      setCoAdminActions(res.data.actions || []);
      setCoAdminActiveCount(res.data.active_count || 0);
    } catch { /* silent */ }
  }, [groupId, isAdmin]);

  useEffect(() => {
    fetchCoAdminSettings();
    fetchCoAdminActions();
    const t = setInterval(fetchCoAdminActions, 60000);
    return () => clearInterval(t);
  }, [fetchCoAdminSettings, fetchCoAdminActions]);

  const handleSaveCoAdmin = async () => {
    setSavingCoAdmin(true);
    try {
      const res = await api.put(`/co-admin/settings/${groupId}`, coAdminSettings);
      setCoAdminSettings({ ...coAdminSettings, ...res.data });
      toast.success(coAdminSettings.enabled ? 'AI Co-Admin activated' : 'AI Co-Admin paused');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingCoAdmin(false);
    }
  };

  const handleVetoAction = async (actionId) => {
    if (vetoingIds.has(actionId)) return; // already in flight
    const reason = window.prompt('Why veto this AI action? (optional)') ?? null;
    if (reason === null) return; // cancelled
    setVetoingIds((s) => new Set(s).add(actionId));
    try {
      await api.post(`/co-admin/actions/${actionId}/veto`, { reason });
      toast.success('Action vetoed and reversed');
      fetchCoAdminActions();
      fetchGroup();
    } catch (err) {
      // Treat "already vetoed" as a no-op (duplicate-click race)
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 400 && /already vetoed/i.test(detail)) {
        fetchCoAdminActions();
      } else {
        toast.error(detail || 'Veto failed');
      }
    } finally {
      setVetoingIds((s) => {
        const next = new Set(s);
        next.delete(actionId);
        return next;
      });
    }
  };

  // Trash drawer handlers (iter47)
  const fetchTrash = useCallback(async () => {
    if (!isAdmin) return;
    setTrashLoading(true);
    try {
      const res = await api.get(`/trash/groups/${groupId}`);
      setTrashItems(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load trash');
    } finally {
      setTrashLoading(false);
    }
  }, [groupId, isAdmin]);

  const handleRestoreTrashItem = async (item) => {
    try {
      await api.post(`/trash/${item.type}/${item.id}/restore`);
      toast.success(`${item.type === 'session' ? 'Session' : 'Recurring expense'} restored`);
      setTrashItems((cur) => cur.filter((i) => !(i.id === item.id && i.type === item.type)));
      if (item.type === 'session') fetchSessions();
      else fetchGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const handlePurgeTrashItem = async (item) => {
    if (!window.confirm(`Permanently delete this ${item.type}? This cannot be undone.`)) return;
    try {
      await api.delete(`/trash/${item.type}/${item.id}`);
      setTrashItems((cur) => cur.filter((i) => !(i.id === item.id && i.type === item.type)));
      toast.success('Permanently deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to purge');
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm(`Empty all ${trashItems.length} trashed item(s)? This cannot be undone.`)) return;
    try {
      const res = await api.post(`/trash/groups/${groupId}/empty`);
      toast.success(res.data.message);
      setTrashItems([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to empty trash');
    }
  };

  // Member trust tier update
  const handleUpdateMemberTrust = async (userId, tier) => {    try {
      const next = { ...(coAdminSettings.member_trust || {}), [userId]: tier };
      await api.put(`/co-admin/trust/${groupId}`, { member_trust: next });
      setCoAdminSettings({ ...coAdminSettings, member_trust: next });
      toast.success('Trust updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update trust');
    }
  };

  // Authenticated blob download helper (used by ledger + fund export buttons)
  const downloadBlob = async (url, fallbackName = 'download') => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${baseUrl}${url}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Downloaded');
    } catch (err) {
      toast.error('Download failed: ' + err.message);
    }
  };

  const openMemberLedger = async (member) => {    setLedgerForMember({ id: member.id, name: member.name });
    setLedgerData(null);
    setLedgerLoading(true);
    try {
      const res = await api.get(`/groups/${groupId}/members/${member.id}/ledger`);
      setLedgerData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load ledger');
      setLedgerForMember(null);
    } finally {
      setLedgerLoading(false);
    }
  };

  const openAuditLogs = async () => {
    setShowAuditLogs(true);
    setAuditLoading(true);
    try {
      const res = await api.get('/audit/logs', { params: { group_id: groupId, limit: 100 } });
      setAuditLogs(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  };

  // iter222 — pair-wise ledger (with-me view)
  const [pairLedgerTarget, setPairLedgerTarget] = useState(null);
  const openPairLedger = (member) => {
    if (!member || member.id === user?.id) return;
    setPairLedgerTarget({ id: member.id, name: member.name });
  };

  const handleAddMemberBack = async (userId, userName) => {
    try {
      await api.put(`/groups/${groupId}/members/${userId}/add-back`);
      toast.success(`${userName} added back!`);
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member back');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/ai/import/${groupId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} row(s) had issues`);
      }
      fetchGroup();
      fetchFund();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    }
    e.target.value = '';
  };

  const handleImportFines = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setImportLoading('preview');
    try {
      const res = await api.post(`/ai/import-fines/${groupId}/preview`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse file');
    } finally {
      setImportLoading(false);
    }
    e.target.value = '';
  };

  const confirmFineImport = async (force = false) => {
    if (!importPreview?.preview_id) return;
    setImportLoading('confirm');
    try {
      const url = `/ai/import-fines/${groupId}/confirm/${importPreview.preview_id}${force ? '?force=true' : ''}`;
      const res = await api.post(url);
      toast.success(res.data.message);
      if (res.data.errors?.length > 0) {
        toast.warning(`Errors: ${res.data.errors.map(e => e.name).join(', ')}`);
      }
      setImportPreview(null);
      fetchGroup();
      fetchFund();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.violations) {
        // Surface violations into the preview modal so user can decide
        setImportPreview(prev => prev ? { ...prev, server_violations: detail.violations, server_violation_msg: detail.message } : prev);
        toast.error(detail.message || 'Balance policy violation');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Fine import failed');
      }
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const res = await api.get(`/ai/import/${groupId}/sample`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${group?.name?.replace(/\s/g, '_') || 'group'}_balances.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download sample');
    }
  };

  const handleResetGroup = async () => {
    if (!window.confirm('RESET ALL DATA for this group? Expenses, fund, attendance, money will be cleared. Members stay. A backup will be created first.')) return;
    if (!window.confirm('Are you SURE? This cannot be undone.')) return;
    try {
      const res = await api.post(`/ai/reset/${groupId}`);
      toast.success(res.data.message);
      fetchGroup();
      fetchFund();
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    }
  };

  const handleOpenComments = async (expenseId) => {
    setShowComments(expenseId);
    setCommentsLoading(true);
    try {
      const response = await api.get(`/expenses/${expenseId}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !showComments) return;
    try {
      const response = await api.post(`/expenses/${showComments}/comments`, { text: newComment.trim() });
      setComments(prev => [...prev, response.data]);
      setNewComment('');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const toggleMemberInSplit = (memberId, isRecurring = false) => {
    if (isRecurring) {
      setNewRecurring(prev => ({
        ...prev,
        split_among: prev.split_among.includes(memberId)
          ? prev.split_among.filter(id => id !== memberId)
          : [...prev.split_among, memberId],
      }));
    } else {
      setNewExpense(prev => ({
        ...prev,
        split_among: prev.split_among.includes(memberId)
          ? prev.split_among.filter(id => id !== memberId)
          : [...prev.split_among, memberId],
      }));
    }
  };

  const toggleDayOfWeek = (day) => {
    setNewRecurring(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day],
    }));
  };

  const updateCustomSplit = (memberId, amount) => {
    setNewExpense(prev => ({
      ...prev,
      custom_splits: {
        ...prev.custom_splits,
        [memberId]: parseFloat(amount) || 0,
      },
    }));
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="group-detail-loading">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-44" />
        </div>
        <Skeleton className="h-32 rounded-2xl" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-20 rounded-full" />
          <Skeleton className="h-9 w-20 rounded-full" />
          <Skeleton className="h-9 w-20 rounded-full" />
          <Skeleton className="h-9 w-20 rounded-full" />
        </div>
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
      </div>
    );
  }

  return (
    <div data-testid="group-detail-page">
      {/* iter94 — sticky contextual header replaces the old back-button row.
          Global app header is hidden on /groups/:id via Layout.js. */}
      <GroupContextHeader
        group={group ? { ...group, _sport_meta: sportsCatalog.find((s) => s.key === group?.sport) } : null}
        isAdmin={isAdmin}
        liveDot={Object.values(liveActivity || {}).some(Boolean)}
        onOpenAi={() => setShowAiSheet(true)}
        onOpenMenu={() => setShowGroupMenu(true)}
        onInvite={() => invite.openShareMenu(group)}
      />

      {/* iter290 — low fund alert banner (visible to everyone in the group) */}
      {group?.low_fund_alert?.enabled && (group?.fund_balance ?? 0) < (group?.low_fund_alert?.threshold ?? 0) && (
        <div className="mb-3 p-3 rounded-2xl border-2 border-red-500/40 bg-red-500/5 flex items-center gap-2" data-testid="low-fund-banner">
          <span className="text-lg">⚠️</span>
          <p className="text-sm font-semibold flex-1">
            Fund is running low — {formatNXC(group.fund_balance)} left (alert threshold {formatNXC(group.low_fund_alert.threshold)}). Time to collect from members.
          </p>
        </div>
      )}

      {/* iter132 — Treasury approval queue (only admins see this) */}
      {isAdmin && pendingTreasury.length > 0 && (
        <div className="mb-3">
          <PendingTreasuryBanner
            items={pendingTreasury}
            currentUserId={user?.id}
            isSuperAdmin={!!user?.is_super_admin}
            onApprove={handleTreasuryApprove}
            onReject={handleTreasuryReject}
            onBulkApprove={handleTreasuryBulkApprove}
          />
        </div>
      )}

      {/* iter203 — Silent-guest signup matches banner (admin-only).
          Surfaces silent-added guests (iter202) whose phones have since
          registered on SplitEasy so admins can promote them in 1 tap. */}
      {isAdmin && silentGuestMatches.matches.length > 0 && (
        <div className="mb-3">
          <SilentGuestMatchesBanner
            groupId={groupId}
            matches={silentGuestMatches.matches}
            dismissed={silentGuestMatches.dismissed}
            setDismissed={silentGuestMatches.setDismissed}
            refresh={async () => {
              await silentGuestMatches.refresh();
              await fetchSessions();
              await fetchGroup();
            }}
          />
        </div>
      )}

      {/* iter193 — Archived trip banner (renders nothing when not archived). */}
      <ArchivedTripBanner
        group={group}
        isAdmin={isAdmin}
        currentUserId={user?.id}
        onReopened={() => fetchGroup()}
      />

      {/* User's per-group net position (To Pay vs To Receive) — kept tight & scannable */}
      <div className="grid grid-cols-2 gap-3 mb-4" data-testid="group-balance-card">
        <div className="rounded-2xl border border-border bg-card px-4 py-3">
          <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-bold">To Pay</p>
          <p className="font-display text-xl font-bold tracking-tight tabular-nums text-rose-500 mt-0.5">
            {formatCoinAmount(group?.balances?.[user?.id]?.to_pay || 0)}
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card px-4 py-3">
          <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-bold">To Receive</p>
          <p className="font-display text-xl font-bold tracking-tight tabular-nums text-emerald-500 mt-0.5">
            {formatCoinAmount(group?.balances?.[user?.id]?.to_receive || 0)}
          </p>
        </div>
      </div>

      {/* Fund Hero — Obsidian rich-state / dashed empty-state */}
      <div className="mb-5">
        <FundHeroCard
          fundBalance={fundData?.fund_balance ?? group?.fund_balance ?? 0}
          fundData={fundData}
          myUserId={user?.id}
          isAdmin={isAdmin}
          onTopUp={() => ((user?.is_super_admin || group?.treasurer_id === user?.id) ? setShowFundModal(true) : setShowContribRequest(true))}
          members={group?.members || []}
          groupId={group?.id}
          currency={group?.currency || 'INR'}
          holderName={
            group?.treasurer_id
              ? (group?.members || []).find((m) => m.id === group.treasurer_id)?.name || null
              : 'Super Admin'
          }
          onViewAllMembers={() => { setPeopleInitialFilter('members'); setActiveTab('people'); }}
          onOpenMoney={() => setActiveTab('ledger')}
        />
      </div>

      {/* Tabs */}
      {/* AI Supervisor banner (iter37) */}
      {isAdmin && aiActiveCount > 0 && (
        <motion.button
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setShowAiObsDrawer(true)}
          data-testid="ai-flags-banner"
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl mb-3 text-left ${
            aiSeverity.high > 0
              ? 'bg-red-500/10 border border-red-500/30 text-red-700'
              : 'bg-amber-500/10 border border-amber-500/30 text-amber-700'
          }`}
        >
          <Bot className="w-5 h-5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">AI flagged {aiActiveCount} item{aiActiveCount === 1 ? '' : 's'}</p>
            <p className="text-xs opacity-80 truncate">
              {aiSeverity.high > 0 && `${aiSeverity.high} high · `}
              {aiSeverity.medium > 0 && `${aiSeverity.medium} medium · `}
              {aiSeverity.low > 0 && `${aiSeverity.low} low · `}
              tap to review
            </p>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0" />
        </motion.button>
      )}

      {/* AI Co-Admin active actions banner (iter38) */}
      {isAdmin && coAdminSettings.enabled && coAdminActiveCount > 0 && (
        <motion.button
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setShowCoAdminDrawer(true)}
          data-testid="co-admin-actions-banner"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl mb-3 text-left bg-primary/10 border border-primary/30 text-primary"
        >
          <Bot className="w-5 h-5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">
              AI Co-Admin · {coAdminActiveCount} action{coAdminActiveCount === 1 ? '' : 's'} in veto window
            </p>
            <p className="text-xs opacity-80 truncate">Tap to review or veto within {coAdminSettings.veto_window_hours}h</p>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0" />
        </motion.button>
      )}

      {/* iter95 — 3-tab Information Architecture (Wave 2 of UX redesign).
          Built up a single `groupCtx` shared across Overview/Ledger/People to
          avoid 5 different prop pyramids.
          iter165 — promoted Sessions to its own top-level tab (between Ledger
          and People) for group types where `features.sessions` is true.
          iter189 — type-aware PrimaryActionBar + tab relabeling. */}
      {/* iter166 — compute the Sessions tab live badge from `sessions`. */}
      {(() => {
        // Compute the "next session" + my RSVP state once per render so both
        // the PrimaryActionBar and the iam-in callback can share it. Picks
        // sessions tier-by-tier (rsvp_open > opening_soon > upcoming).
        const nowMs = Date.now();
        const upcomingSessions = (sessions || [])
          .filter((s) => s?.starts_at && new Date(s.starts_at).getTime() > nowMs - 60_000)
          .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
        const next = upcomingSessions[0] || null;
        const myId = user?.id;
        let rsvpState = 'pending';
        if (next && myId) {
          if ((next.confirmed_ids || []).includes(myId)) rsvpState = 'in';
          else if ((next.rsvp_out_ids || []).includes(myId)) rsvpState = 'out';
        }
        return (
          <PrimaryActionBar
            typeConfig={groupTypeConfig}
            group={group}
            isAdmin={isAdmin}
            currentUserId={myId}
            nextSessionId={next?.id || null}
            rsvpState={rsvpState}
            onAddExpense={() => setShowAddExpense(true)}
            onSettle={() => openQuickSettle()}
            onTopUp={() => ((user?.is_super_admin || group?.treasurer_id === user?.id) ? setShowFundModal(true) : setShowContribRequest(true))}
            onMyLedger={(uid) => openMemberLedger({ id: uid, name: user?.name || 'Me' })}
            onChargeFee={() => { setActiveTab('sessions'); }}
            onIamIn={() => next?.id && handleRsvp(next.id, rsvpState === 'in' ? 'out' : 'in')}
            onManage={() => setShowAdminSheet(true)}
          />
        );
      })()}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList
          className={`w-full mb-4 rounded-xl sticky top-16 z-30 shadow-sm ${(group?.features?.sessions !== false ? 4 : 3) + (playEnabled ? 1 : 0) === 5 ? 'grid-cols-5' : group?.features?.sessions !== false || playEnabled ? 'grid-cols-4' : 'grid-cols-3'} grid h-12 p-1`}
          data-testid="group-detail-tabs"
        >
          <TabsTrigger value="overview" className="rounded-lg text-xs sm:text-sm font-bold tracking-tight relative" data-testid="overview-tab">
            <BarChart3 className="w-4 h-4 mr-1.5" strokeWidth={2.25} />
            <span>Overview</span>
            {(liveActivity.fund || liveActivity.expenses) && <LiveDot testId="live-dot-overview" />}
          </TabsTrigger>
          {group?.features?.sessions !== false && (() => {
            const sessionsBadge = deriveSessionsBadge(sessions, nowTick);
            return (
              <TabsTrigger value="sessions" className="rounded-lg text-xs sm:text-sm font-bold tracking-tight relative" data-testid="sessions-tab">
                <CalendarDays className="w-4 h-4 mr-1.5" strokeWidth={2.25} />
                <span>Sessions</span>
                <SessionsBadge badge={sessionsBadge} />
                {liveActivity.attendance && !sessionsBadge && <LiveDot testId="live-dot-sessions" />}
              </TabsTrigger>
            );
          })()}
          <TabsTrigger value="ledger" className="rounded-lg text-xs sm:text-sm font-bold tracking-tight relative" data-testid="ledger-tab">
            <Receipt className="w-4 h-4 mr-1.5" strokeWidth={2.25} />
            <span>{(groupTypeConfig?.isTrip || groupTypeConfig?.isEvent) ? 'Expenses' : 'Money'}</span>
            {(liveActivity.expenses || liveActivity.recurring) && <LiveDot testId="live-dot-ledger" />}
          </TabsTrigger>
          {playEnabled && (
            <TabsTrigger value="play" className="rounded-lg text-xs sm:text-sm font-bold tracking-tight relative" data-testid="play-tab">
              <Trophy className="w-4 h-4 mr-1.5" strokeWidth={2.25} />
              <span>Play</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="people" className="rounded-lg text-xs sm:text-sm font-bold tracking-tight relative" data-testid="people-tab">
            <Users className="w-4 h-4 mr-1.5" strokeWidth={2.25} />
            <span>Members</span>
          </TabsTrigger>
        </TabsList>

        {(() => {
          // Single ctx object — every tab section reads what it needs from this map.
          // Adding a new prop here lets every nested tab use it without touching three call sites.
          const groupCtx = {
            // shared
            group, user, isAdmin, groupId, navigate,
            formatCurrency, formatCoinAmount,
            // iter119 — expose fetchers so QR-scan settle can refresh on success
            fetchGroup, fetchFund,
            // iter197.5 — fee reconcile + per-attendee fee edit + recovery use these
            fetchSessions,
            // expenses
            setShowAddExpense, setShowBulkAdd, setTransferMode, setShowTransferModal,
            openEditExpense, handleReverseExpense, handleResubmitExpense, handleOpenComments,
            // recurring
            recurringExpenses, setShowRecurringModal, handleTriggerRecurring,
            handleToggleRecurring, handleDeleteRecurring,
            // attendance
            sessions, fineDefaults, submitting, sessionDefaults,
            setShowSessionDefaults, openNewSessionWithDefaults, openAttendance,
            handleAutoFine, handleChargeSessionFee, openEditSession, openDuplicateSession,
            handleDeleteSession, handleRemoveGuest, handleRsvp, handleLockRsvp,
            openAdminRsvp, handleAdminRsvpRemove, handleAddGuest, handleCancelSession,
            // deep-link state
            peopleInitialFilter, focusedSessionId,
            // fund
            fundData, fundSnapshot, weeklyStats, pendingTransfers,
            expandedTransferId, setExpandedTransferId,
            balancePolicy, selectedTxns, bulkReversing, importLoading,
            downloadBlob, handleRepayLoan, handleMarkPledgeReceived,
            handleApproveTransfer, handleRejectTransfer, handleCancelTransfer,
            setShowFundModal, openFineWithType, setShowAllotCoins,
            setShowGuestContribModal, setShowLoanToMemberModal, openQuickSettle,
            handleImportFile, handleImportFines, handleDownloadSample,
            setShowFineDefaultsModal, setShowBalancePolicyModal, handleResetGroup,
            toggleSelectAllTxns, handleBulkReverse, toggleTxnSelect, handleReverseTransaction,
            // balances
            setSelectedSettlement, setShowSettleModal,
            // members
            sendingGroupDigest, sendingReminder, handleSendGroupDigest, handleSendReminder,
            handleToggleGroupAdmin, handleRemoveMember, handleAddMemberBack,
            setShowAddMember, setShowInviteModal, setInviteCode,
            setNewMemberPhone,   // iter247 — pending-invite "Add" prefills the dialog
            openMemberLedger, openAuditLogs,
            openPairLedger,    // iter222 — pair-wise drill-down
            // iter191 — multi-currency: let child cards open the lock-in modal.
            onOpenTripRateModal: () => setShowSetTripRate(true),
            // iter203 — silent-guest match map for inline per-chip badges in AttendanceTab.
            silentGuestMatchByGuestId: silentGuestMatches.matchByGuestId,
            silentGuestRefresh: silentGuestMatches.refresh,
            // Play — doubles Elo leaderboard + Court Mode
            playEnabled, canScore: playEnabled && canScorePlay(group, user?.id),
            openCourtMode: (sessionId) => setCourtModeSessionId(sessionId),
            openPlayTab: () => setActiveTab('play'),
          };
          return (
            <>
              <TabsContent value="overview" className="space-y-3"><OverviewTab ctx={groupCtx} /></TabsContent>
              <TabsContent value="ledger"   className="space-y-3"><LedgerTab   ctx={groupCtx} /></TabsContent>
              <TabsContent value="sessions" className="space-y-3"><AttendanceTab ctx={groupCtx} /></TabsContent>
              <TabsContent value="people"   className="space-y-3"><PeopleTab   ctx={groupCtx} /></TabsContent>
              {playEnabled && <TabsContent value="play" className="space-y-3"><PlayTab ctx={groupCtx} /></TabsContent>}
            </>
          );
        })()}
      </Tabs>
      {/* iter191 — Trip-only multi-currency lock-in modal. Opens from
          TripCurrencyBadge CTA on the Overview tab. */}
      <CourtModeSheet
        open={Boolean(courtModeSessionId)}
        sessionId={courtModeSessionId}
        group={group}
        user={user}
        onClose={() => setCourtModeSessionId(null)}
      />
      <SetTripRateModal
        open={showSetTripRate}
        onOpenChange={setShowSetTripRate}
        group={group}
        onSaved={() => fetchGroup()}
      />
      {/* iter193 — End-trip modal opens from the kebab menu */}
      <EndTripModal
        open={showEndTrip}
        onOpenChange={setShowEndTrip}
        group={group}
        onEnded={() => fetchGroup()}
      />
      {/* iter197 — Trip view (read-only) magic link */}
      <GuestLinkSheet
        open={showTripViewLinkSheet}
        onClose={() => setShowTripViewLinkSheet(false)}
        scope="trip"
        groupId={groupId}
        groupName={group?.name}
      />
      {/* iter197.4 — Phone-first add-guest modal */}
      <AddGuestModal
        open={Boolean(addGuestSessionId)}
        sessionId={addGuestSessionId}
        sessionTitle={addGuestSessionTitle}
        groupName={group?.name}
        onClose={() => setAddGuestSessionId(null)}
        onSuccess={() => {
          fetchSessions();
          fetchGroup();
        }}
      />
      <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-expense-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add Expense</DialogTitle>
          </DialogHeader>

          {/* iter195 — AI quick-fill row (📷 + 🎤). Hidden on archived trips. */}
          {!group?.archived_at && (
            <AIExpenseCapture
              groupCurrency={group?.currency || 'INR'}
              onParsed={(payload) => {
                if (!payload) return;
                setNewExpense((prev) => {
                  const next = { ...prev };
                  if (payload.amount) next.amount = String(payload.amount);
                  if (payload.vendor || payload.raw_text) next.description = payload.vendor || (payload.raw_text || '').slice(0, 60);
                  if (payload.category) next.category = payload.category;
                  // Map currency only if the trip is multi-currency and AI returned a recognised one.
                  if (payload.currency && group?.trip_currency && payload.currency === group.trip_currency) {
                    next.entry_currency = group.trip_currency;
                  }
                  return next;
                });
              }}
            />
          )}

          <form onSubmit={handleAddExpense} className="space-y-5 mt-4">
            {/* iter191 — Trip multi-currency: currency toggle above amount.
                Hidden when the group isn't multi-currency. */}
            {group?.trip_currency && group?.trip_rate && (
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Pay in</Label>
                <div className="grid grid-cols-2 gap-1.5 rounded-xl border-2 border-border p-1 bg-muted/30">
                  {[
                    { code: group.currency || 'INR', sym: CURRENCY_SYMBOLS[group.currency || 'INR'] || '₹', label: 'home' },
                    { code: group.trip_currency, sym: CURRENCY_SYMBOLS[group.trip_currency] || group.trip_currency, label: 'trip' },
                  ].map((opt) => {
                    const active = newExpense.entry_currency === opt.code;
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => setNewExpense({ ...newExpense, entry_currency: opt.code })}
                        data-testid={`expense-currency-${opt.code}`}
                        className={`rounded-lg px-3 py-2 text-sm font-bold tabular-nums transition ${active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-accent'}`}
                      >
                        {opt.sym} {opt.code}
                      </button>
                    );
                  })}
                </div>
                {Number(newExpense.amount) > 0 && newExpense.entry_currency && newExpense.entry_currency !== (group.currency || 'INR') && (
                  <p className="text-xs text-muted-foreground tabular-nums" data-testid="expense-conversion-preview">
                    ≈ {formatNXC(Number(newExpense.amount) * Number(group.trip_rate || 1), group.currency || 'INR')} at the locked rate
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Amount</Label>
              <Input
                type="number"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                placeholder="0"
                data-testid="expense-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Description</Label>
              <Input
                value={newExpense.description}
                onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                placeholder="What's this expense for?"
                data-testid="expense-description-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Category</Label>
              <Select
                value={newExpense.category}
                onValueChange={(value) => handleCategoryChange(value)}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="expense-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryIcons).map(([key, Icon]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span className="capitalize">{key}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* iter181 — Smart Trip preset banner */}
              {group?.group_type === 'trip' && TRIP_CATEGORY_PRESETS[newExpense.category] && (
                <p className="text-xs text-primary/80 bg-primary/5 border border-primary/20 rounded-lg px-2.5 py-1.5" data-testid="trip-category-preset-hint">
                  <span className="font-semibold uppercase tracking-wide">Smart preset:</span>{' '}
                  {TRIP_CATEGORY_PRESETS[newExpense.category].split_type === 'per_head' && 'Per Head split (adults + kids weighted)'}
                  {TRIP_CATEGORY_PRESETS[newExpense.category].split_type === 'equal' && (TRIP_CATEGORY_PRESETS[newExpense.category].scope === 'drinkers' ? 'Equal split among drinkers only' : 'Equal split')}
                  {TRIP_CATEGORY_PRESETS[newExpense.category].split_type === 'custom' && 'Custom per-person amounts'}
                  <span className="text-muted-foreground"> · you can override below</span>
                </p>
              )}
            </div>

            {/* iter181 — Hotel room picker (Trip + Hotel/Accommodation only, when rooms configured) */}
            {group?.group_type === 'trip'
              && (newExpense.category === 'hotel' || newExpense.category === 'lodging' || newExpense.category === 'accommodation')
              && (group?.trip_rooms?.length || 0) > 0 && (
              <div className="space-y-2" data-testid="hotel-room-picker">
                <Label className="text-sm font-semibold uppercase tracking-wide">Which Room?</Label>
                <Select
                  value={newExpense.room_id || ''}
                  onValueChange={handleRoomPick}
                >
                  <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="hotel-room-select">
                    <SelectValue placeholder="Pick a room (auto-splits among occupants)" />
                  </SelectTrigger>
                  <SelectContent>
                    {(group?.trip_rooms || []).map((r) => {
                      const occ = (r.occupant_user_ids || [])
                        .map((uid) => group?.members?.find((m) => m.id === uid)?.name)
                        .filter(Boolean);
                      return (
                        <SelectItem key={r.id} value={r.id}>
                          {r.label} — {occ.join(', ') || 'no occupants'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {newExpense.room_id && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                    Splits equally among the room's occupants (couple in one room pays 2× a solo person).
                  </p>
                )}
              </div>
            )}

            {/* iter183 — Stay-nights weighting toggle (Trip + night-sensitive category only) */}
            {group?.group_type === 'trip' && NIGHT_SENSITIVE_CATEGORIES.has(newExpense.category) && newExpense.paid_by !== 'group_fund' && !newExpense.reimburse_from_fund && !newExpense.direct_payment && (
              <div className="rounded-xl border-2 border-border p-3" data-testid="weight-by-nights-wrap">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!newExpense.weight_by_nights}
                    onChange={(e) => setNewExpense({ ...newExpense, weight_by_nights: e.target.checked })}
                    data-testid="weight-by-nights-toggle"
                    className="mt-1 w-4 h-4 accent-primary"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">⏰ Weight by stay-nights</p>
                    <p className="text-xs text-muted-foreground leading-snug">
                      Pro-rate each member's share by their nights (set in Group Settings → Trip Party). Use when someone stays fewer nights than others.
                    </p>
                  </div>
                </label>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Paid By</Label>
              <Select
                value={newExpense.paid_by}
                onValueChange={(value) => setNewExpense({ ...newExpense, paid_by: value })}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="expense-paidby-select">
                  <SelectValue placeholder="Who paid?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="group_fund">
                    <span className="font-semibold text-primary">Group Fund</span>
                  </SelectItem>
                  {group?.members?.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {newExpense.paid_by === 'group_fund' && (
              <div className="space-y-2">
                <div className="p-3 rounded-xl bg-primary/5 border-2 border-primary/20">
                  <p className="text-sm font-medium text-primary">Paid from Group Fund</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {newExpense.fund_cap_enabled
                      ? 'Fund covers up to the cap below; members share the rest.'
                      : 'Amount will be deducted from the group fund. No individual split needed.'}
                  </p>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                    <Checkbox
                      checked={!!newExpense.fund_cap_enabled}
                      onCheckedChange={(v) => setNewExpense((s) => {
                        const saved = group?.fund_cap_defaults?.[(s.category || 'miscellaneous').replace(/[.$]/g, '_')];
                        return {
                          ...s,
                          fund_cap_enabled: !!v,
                          fund_cap: !!v && s.fund_cap === '' && saved != null ? String(saved) : s.fund_cap,
                          split_among: !!v ? (group?.member_ids || []) : [],
                        };
                      })}
                      data-testid="fund-cap-toggle"
                    />
                    <span className="text-xs font-semibold">Cap the fund's share (members split the rest)</span>
                  </label>
                </div>
                {newExpense.fund_cap_enabled && (() => {
                  const amt = parseFloat(newExpense.amount || 0) || 0;
                  const cap = newExpense.fund_cap === '' ? null : (parseFloat(newExpense.fund_cap) || 0);
                  const excess = cap === null ? 0 : Math.max(0, amt - cap);
                  const nSel = (newExpense.split_among || []).length;
                  const per = excess > 0 && nSel > 0 ? excess / nSel : 0;
                  return (
                    <div className="p-3 rounded-xl border-2 border-border space-y-2" data-testid="fund-cap-panel">
                      <Label className="text-xs font-semibold uppercase tracking-wide">Fund covers up to</Label>
                      <Input
                        type="number"
                        min="0"
                        inputMode="decimal"
                        placeholder="e.g. 1000"
                        value={newExpense.fund_cap}
                        onChange={(e) => setNewExpense((s) => ({ ...s, fund_cap: e.target.value }))}
                        className="h-11 border-2 rounded-xl"
                        data-testid="fund-cap-input"
                      />
                      {(() => {
                        const saved = group?.fund_cap_defaults?.[(newExpense.category || 'miscellaneous').replace(/[.$]/g, '_')];
                        return saved != null ? (
                          <p className="text-xs text-muted-foreground" data-testid="fund-cap-remembered">
                            Remembered cap for this category: ₹{Number(saved).toLocaleString('en-IN')} — using a different value updates the memory.
                          </p>
                        ) : null;
                      })()}
                      {cap !== null && excess > 0 && (
                        <>
                          <Label className="text-xs font-semibold uppercase tracking-wide">Split the remaining {formatNXC(excess)} among</Label>
                          <div className="flex flex-wrap gap-1.5">
                            {(group?.members || []).map((m) => {
                              const on = (newExpense.split_among || []).includes(m.id);
                              return (
                                <button
                                  key={m.id}
                                  type="button"
                                  onClick={() => setNewExpense((s) => ({
                                    ...s,
                                    split_among: on ? s.split_among.filter((id) => id !== m.id) : [...s.split_among, m.id],
                                  }))}
                                  data-testid={`fund-cap-member-${m.id}`}
                                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border-2 transition ${
                                    on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-primary/40'
                                  }`}
                                >
                                  {m.name}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-xs rounded-lg px-2.5 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-700" data-testid="fund-cap-preview">
                            {nSel > 0
                              ? <>Fund pays <span className="font-bold">{formatNXC(Math.min(cap, amt))}</span> · each of {nSel} member{nSel > 1 ? 's' : ''} owes <span className="font-bold">{formatNXC(per)}</span></>
                              : 'Pick at least one member to share the excess.'}
                          </p>
                        </>
                      )}
                      {cap !== null && excess === 0 && amt > 0 && (
                        <p className="text-xs text-muted-foreground" data-testid="fund-cap-no-excess">
                          The cap covers the whole bill — fund pays {formatNXC(amt)}, members owe nothing.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* iter217 — 3-mode radio: Peer-to-Peer / Reimburse from Fund / Advance from Fund */}
            {newExpense.paid_by && newExpense.paid_by !== 'group_fund' && (
              <div className="space-y-2" data-testid="reimburse-toggle-wrap">
                <Label className="text-sm font-semibold uppercase tracking-wide">How is this paid?</Label>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setNewExpense({ ...newExpense, reimburse_from_fund: false, advance_from_fund: false })}
                    data-testid="expense-mode-p2p"
                    className={`text-left rounded-xl border-2 p-3 transition-colors ${
                      !newExpense.reimburse_from_fund && !newExpense.advance_from_fund
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-background hover:bg-accent'
                    }`}
                  >
                    <p className="text-sm font-bold flex items-center gap-2">
                      <span className="text-base">💵</span> Peer-to-peer split <span className="text-xs text-muted-foreground font-normal">(default)</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug" data-testid="expense-mode-p2p-hint">
                      <span className="font-semibold text-foreground/80">Payer used their own money — no cash leaves the fund box.</span>{' '}
                      Payer's balance goes UP by the full amount (group owes them); split members' balances go DOWN by their share.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewExpense({ ...newExpense, reimburse_from_fund: true, advance_from_fund: false })}
                    data-testid="expense-mode-reimburse"
                    className={`text-left rounded-xl border-2 p-3 transition-colors ${
                      newExpense.reimburse_from_fund
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-border bg-background hover:bg-accent'
                    }`}
                  >
                    <p className="text-sm font-bold flex items-center gap-2">
                      <span className="text-base">🏦</span> Reimburse from fund
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug" data-testid="expense-mode-reimburse-hint">
                      <span className="font-semibold text-foreground/80">Payer used their own money — the group treats.</span>{' '}
                      Fund pays the payer back in full; NO member owes anything.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewExpense({ ...newExpense, reimburse_from_fund: false, advance_from_fund: true })}
                    data-testid="expense-mode-advance"
                    className={`text-left rounded-xl border-2 p-3 transition-colors ${
                      newExpense.advance_from_fund
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : 'border-border bg-background hover:bg-accent'
                    }`}
                  >
                    <p className="text-sm font-bold flex items-center gap-2">
                      <span className="text-base">⚡</span> Advance from fund
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug" data-testid="expense-mode-advance-hint">
                      <span className="font-semibold text-foreground/80">Real cash from the fund box was handed to the payer.</span>{' '}
                      Payer is already square (balance unchanged); split members owe their share to the FUND.
                    </p>
                  </button>
                </div>
                {newExpense.reimburse_from_fund && (
                  <p className="text-xs text-amber-700/90 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                    The fund will refund <span className="font-semibold">{group?.members?.find((m) => m.id === newExpense.paid_by)?.name || 'the payer'}</span> the full {formatNXC(newExpense.amount || 0)}. No split among members.
                  </p>
                )}
                {newExpense.advance_from_fund && (
                  <p className="text-xs text-emerald-700/90 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2">
                    Fund pays <span className="font-semibold">{group?.members?.find((m) => m.id === newExpense.paid_by)?.name || 'the payer'}</span> the full {formatNXC(newExpense.amount || 0)}, then collects the share from each split member (including payer). Requires enough fund balance.
                  </p>
                )}
              </div>
            )}

            {/* iter273 — live approval-path hint (mirrors backend rules in routes/expenses.py) */}
            {newExpense.paid_by && (() => {
              const adminIds = group?.admin_ids || [];
              const soleAdminCreator = adminIds.length === 1 && adminIds[0] === user?.id;
              const adminLabel = soleAdminCreator ? 'you (admin)' : 'an admin';
              const payerConsent = newExpense.paid_by !== 'group_fund' && newExpense.paid_by !== user?.id;
              const payerName = group?.members?.find((m) => m.id === newExpense.paid_by)?.name || 'the payer';
              let approver;
              if (newExpense.paid_by !== 'group_fund') {
                const others = (newExpense.split_among || []).filter((id) => id !== newExpense.paid_by);
                const policy = group?.expense_approval_policy || 'admin';
                const fundTouching = newExpense.reimburse_from_fund || newExpense.advance_from_fund;
                if (fundTouching) {
                  approver = adminLabel;
                } else if (others.length === 1) {
                  const peerName = group?.members?.find((m) => m.id === others[0])?.name || 'the other member';
                  approver = `${peerName} (1-on-1 peer approval)`;
                } else if (others.length >= 2 && policy !== 'admin' && !fundTouching) {
                  approver = policy === 'any_one'
                    ? `any 1 of the ${others.length} split members`
                    : policy === 'majority'
                      ? `${Math.floor(others.length / 2) + 1} of ${others.length} split members (majority)`
                      : `all ${others.length} split members`;
                } else {
                  approver = adminLabel;
                }
              } else {
                approver = adminLabel;
              }
              const isAuto = !payerConsent && approver === 'you (admin)';
              return (
                <p
                  data-testid="approval-hint"
                  className={`text-xs rounded-lg px-2.5 py-1.5 border ${
                    isAuto
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700'
                      : 'bg-sky-500/10 border-sky-500/30 text-sky-800'
                  }`}
                >
                  {isAuto
                    ? "✓ Auto-approved instantly — you're the only admin"
                    : payerConsent
                      ? <>🔒 <span className="font-semibold">{payerName}</span> must first confirm they paid → then approval by <span className="font-semibold">{approver}</span></>
                      : <>Needs approval from: <span className="font-semibold">{approver}</span></>}
                </p>
              );
            })()}

            {/* iter181 — Direct payment toggle (Trip only; mutually exclusive with reimburse) */}
            {group?.group_type === 'trip' && newExpense.paid_by !== 'group_fund' && !newExpense.reimburse_from_fund && (
              <div className="rounded-xl border-2 border-border p-3" data-testid="direct-payment-wrap">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!newExpense.direct_payment}
                    onChange={(e) => setNewExpense({ ...newExpense, direct_payment: e.target.checked })}
                    data-testid="direct-payment-toggle"
                    className="mt-1 w-4 h-4 accent-primary"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">Direct payment (no settlement)</p>
                    <p className="text-xs text-muted-foreground leading-snug">
                      Each member paid directly (e.g. agent-booked flights). Record-only — no fund or coin balance moves. Excluded from budget totals.
                    </p>
                  </div>
                </label>
                {newExpense.direct_payment && newExpense.split_type === 'equal' && newExpense.split_among.length > 0 && newExpense.amount > 0 && (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2">
                    Each of <strong>{newExpense.split_among.length}</strong> members paid <strong>{formatNXC(parseFloat(newExpense.amount) / newExpense.split_among.length)}</strong> directly. Use <em>Custom Amounts</em> if amounts differ per person.
                  </p>
                )}
              </div>
            )}

            {newExpense.paid_by !== 'group_fund' && !newExpense.reimburse_from_fund && (
              <>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Split Type</Label>
              <Select
                value={newExpense.split_type}
                onValueChange={(value) => setNewExpense({ ...newExpense, split_type: value })}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="expense-splittype-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equal">Equal Split</SelectItem>
                  <SelectItem value="exclude">Select Members</SelectItem>
                  <SelectItem value="custom">Custom Amounts</SelectItem>
                  <SelectItem value="percentage" data-testid="splittype-percentage-option">Percentage %</SelectItem>
                  {/* iter169 — Per-Head only available on Trip groups */}
                  {group?.group_type === 'trip' && (
                    <SelectItem value="per_head" data-testid="splittype-per-head-option">Per Head (Adults + Kids)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold uppercase tracking-wide">Split Among</Label>
                {/* iter268 — quick-pick helpers */}
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    data-testid="split-only-me-btn"
                    onClick={() => {
                      const payerId = newExpense.paid_by && newExpense.paid_by !== 'group_fund' ? newExpense.paid_by : user?.id;
                      setNewExpense({ ...newExpense, split_among: payerId ? [payerId] : [] });
                    }}
                    className="px-2.5 h-7 rounded-full border-2 border-border text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                  >
                    Only me +…
                  </button>
                  {newExpense.split_among.length > 0 ? (
                    <button
                      type="button"
                      data-testid="split-clear-all-btn"
                      onClick={() => setNewExpense({ ...newExpense, split_among: [] })}
                      className="px-2.5 h-7 rounded-full border-2 border-border text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                    >
                      Clear all
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="split-select-all-btn"
                      onClick={() => setNewExpense({ ...newExpense, split_among: group?.member_ids || [] })}
                      className="px-2.5 h-7 rounded-full border-2 border-primary/40 text-xs font-bold uppercase tracking-wider text-primary hover:bg-primary/10 transition"
                    >
                      Select all
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-1" data-testid="split-count-hint">
                {newExpense.split_among.length} of {group?.members?.length || 0} members selected
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {group?.members?.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 border-2 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={newExpense.split_among.includes(member.id)}
                        onCheckedChange={() => toggleMemberInSplit(member.id)}
                        data-testid={`split-member-${member.id}`}
                      />
                      <span className="font-medium">{member.name}</span>
                    </div>
                    {newExpense.split_type === 'custom' && newExpense.split_among.includes(member.id) && (
                      <Input
                        type="number"
                        value={newExpense.custom_splits[member.id] || ''}
                        onChange={(e) => updateCustomSplit(member.id, e.target.value)}
                        placeholder="0"
                        className="w-24 h-8 text-right"
                        data-testid={`custom-amount-${member.id}`}
                      />
                    )}
                    {/* iter258 — percentage split inputs */}
                    {newExpense.split_type === 'percentage' && newExpense.split_among.includes(member.id) && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={(newExpense.percent_splits || {})[member.id] ?? ''}
                          onChange={(e) => setNewExpense({ ...newExpense, percent_splits: { ...(newExpense.percent_splits || {}), [member.id]: e.target.value } })}
                          placeholder="0"
                          className="w-16 h-8 text-right"
                          data-testid={`percent-amount-${member.id}`}
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                        {newExpense.amount && (
                          <span className="text-xs text-muted-foreground tabular-nums w-14 text-right">
                            {formatCurrency((parseFloat(newExpense.amount) || 0) * (parseFloat((newExpense.percent_splits || {})[member.id]) || 0) / 100)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {newExpense.split_type === 'equal' && newExpense.split_among.length > 0 && newExpense.amount && (
                <p className="text-sm text-muted-foreground">
                  Each person pays: {formatCurrency(parseFloat(newExpense.amount) / newExpense.split_among.length)}
                </p>
              )}
              {/* iter258 — percentage split total + even quick-fill */}
              {newExpense.split_type === 'percentage' && newExpense.split_among.length > 0 && (() => {
                const ps = newExpense.percent_splits || {};
                const totalPct = newExpense.split_among.reduce((s, id) => s + (parseFloat(ps[id]) || 0), 0);
                const ok = Math.abs(totalPct - 100) <= 0.5;
                return (
                  <div className="flex items-center justify-between gap-2" data-testid="percent-summary">
                    <button
                      type="button"
                      onClick={() => {
                        const even = Math.floor(10000 / newExpense.split_among.length) / 100;
                        const next = {};
                        newExpense.split_among.forEach((id, i) => {
                          next[id] = i === newExpense.split_among.length - 1
                            ? Math.round((100 - even * (newExpense.split_among.length - 1)) * 100) / 100
                            : even;
                        });
                        setNewExpense({ ...newExpense, percent_splits: next });
                      }}
                      data-testid="distribute-percent-btn"
                      className="px-3 h-8 rounded-full border-2 border-primary/40 text-primary text-xs font-bold uppercase tracking-wider hover:bg-primary/10"
                    >
                      ÷ Even %
                    </button>
                    <span className={`text-xs font-bold tabular-nums ${ok ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="percent-total">
                      Total: {Math.round(totalPct * 100) / 100}% {ok ? '✓' : '(must be 100%)'}
                    </span>
                  </div>
                );
              })()}
              {/* iter182 — Option A: Custom-split quick-fill helpers */}
              {newExpense.split_type === 'custom' && newExpense.split_among.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center" data-testid="custom-quickfill">
                  <button
                    type="button"
                    onClick={distributeEvenlyCustom}
                    data-testid="distribute-evenly-btn"
                    className="px-3 h-8 rounded-full border-2 border-primary/40 text-primary text-xs font-bold uppercase tracking-wider hover:bg-primary/10"
                  >
                    ÷ Distribute evenly
                  </button>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Apply</span>
                    <Input
                      type="number"
                      placeholder="₹"
                      data-testid="apply-amount-input"
                      className="w-20 h-8 text-right text-xs"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { applyAmountToAllCustom(e.target.value); e.target.value = ''; e.preventDefault(); }
                      }}
                    />
                    <span className="text-xs text-muted-foreground">to each (Enter)</span>
                  </div>
                </div>
              )}
              {/* iter169 — Per-Head preview: shows weighted shares using
                   group.party_composition + group.kid_head_weight.
                   iter181 — Per-kid override for Activities: each row gets an
                   inline kid counter that defaults to party_composition[uid].kids
                   but can be set to 0 (kid didn't go) or any value. */}
              {newExpense.split_type === 'per_head' && newExpense.split_among.length > 0 && newExpense.amount && (() => {
                const amt = parseFloat(newExpense.amount) || 0;
                const kidW = Number.isFinite(group?.kid_head_weight) ? group.kid_head_weight : 1;
                const party = group?.party_composition || {};
                const kpOverride = newExpense.kid_participation || {};
                const useNights = !!newExpense.weight_by_nights;
                const weights = {};
                newExpense.split_among.forEach((uid) => {
                  const c = party[uid] || {};
                  const a = c.adults || 0;
                  const k = (uid in kpOverride) ? Number(kpOverride[uid]) : (c.kids || 0);
                  let w = (a || k ? a : 1) + k * kidW;
                  if (useNights) {
                    const n = Number(c.nights) > 0 ? Number(c.nights) : 1;
                    w = w * n;
                  }
                  weights[uid] = w;
                });
                const totalW = Object.values(weights).reduce((s, v) => s + v, 0) || newExpense.split_among.length;
                // Show the per-kid override input UI only for Activities-family categories
                const showKidOverride = ['activities', 'entertainment', 'sports', 'nightlife'].includes((newExpense.category || '').toLowerCase());
                return (
                  <div className="text-xs text-muted-foreground space-y-1 mt-1 p-2 rounded-lg bg-primary/5 border border-primary/10" data-testid="per-head-preview">
                    <p className="font-bold text-foreground text-xs uppercase tracking-wider">
                      Per-head shares (kid ×{kidW.toFixed(2)}{useNights ? ', × stay nights' : ''})
                    </p>
                    {newExpense.split_among.map((uid) => {
                      const m = group?.members?.find((x) => x.id === uid);
                      const w = weights[uid] || 0;
                      const share = totalW > 0 ? (amt * w) / totalW : 0;
                      const defaultKids = (party[uid] || {}).kids || 0;
                      const overrideKids = (uid in kpOverride) ? Number(kpOverride[uid]) : defaultKids;
                      const c = party[uid] || {};
                      const nightsLabel = useNights ? ` · ${c.nights || 1}n` : '';
                      return (
                        <div key={uid} className="flex items-center justify-between gap-2" data-testid={`per-head-share-${uid}`}>
                          <span className="flex-1 truncate">{m?.name || uid} <span className="opacity-60">({w.toFixed(1)} hd{nightsLabel})</span></span>
                          {showKidOverride && defaultKids > 0 && (
                            <input
                              type="number"
                              min="0"
                              max={defaultKids}
                              value={overrideKids}
                              onChange={(e) => {
                                const v = Math.max(0, Math.min(defaultKids, parseInt(e.target.value, 10) || 0));
                                setNewExpense((prev) => ({ ...prev, kid_participation: { ...(prev.kid_participation || {}), [uid]: v } }));
                              }}
                              data-testid={`kid-participation-${uid}`}
                              title={`Kids that went (max ${defaultKids})`}
                              className="w-12 h-6 text-center text-xs rounded border border-border bg-background"
                            />
                          )}
                          <span className="tabular-nums font-semibold w-16 text-right">{formatCurrency(share)}</span>
                        </div>
                      );
                    })}
                    {showKidOverride && Object.values(newExpense.kid_participation || {}).length > 0 && (
                      <p className="text-xs text-blue-700">
                        Override applied — only the entered kids count for this expense.
                      </p>
                    )}
                    {(() => {
                      const missing = newExpense.split_among.filter((uid) => !party[uid]);
                      if (missing.length === 0) return null;
                      return (
                        <p className="text-xs text-amber-600 mt-1">
                          {missing.length} member{missing.length === 1 ? '' : 's'} have no party set — counted as 1 adult. Set in <span className="font-semibold">Group Settings → Trip Party</span>.
                        </p>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>
              </>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-expense-btn"
              className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
            >
              {submitting ? 'Adding...' : 'Add Expense'}
            </Button>

            {/* Receipt Upload */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1 cursor-pointer">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                  className="hidden"
                  data-testid="receipt-file-input"
                />
                <span className="px-3 py-1.5 border-2 border-dashed rounded-xl hover:border-primary transition-colors text-sm">
                  {receiptFile ? receiptFile.name : '+ Attach Receipt (optional)'}
                </span>
              </label>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter182 — Bulk Add Modal */}
      <Dialog open={showBulkAdd} onOpenChange={setShowBulkAdd}>
        <DialogContent className="sm:max-w-2xl overflow-y-auto" data-testid="bulk-add-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Bulk Add Expenses
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Add one expense per member in a single submit (e.g. agent-booked flights, individual mini-bar bills, session fees).
            </p>
          </DialogHeader>

          <div className="space-y-3">
            {/* Category */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide">Category</Label>
              <Select
                value={bulkData.category}
                onValueChange={(v) => setBulkData((prev) => ({ ...prev, category: v }))}
              >
                <SelectTrigger className="h-11 border-2 rounded-xl" data-testid="bulk-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryIcons).map(([key, Icon]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2"><Icon className="w-4 h-4" /><span className="capitalize">{key}</span></div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {group?.group_type === 'trip' && TRIP_CATEGORY_PRESETS[bulkData.category] && (
                <p className="text-xs text-primary/70 bg-primary/5 border border-primary/20 rounded-lg px-2 py-1">
                  Smart preset: {TRIP_CATEGORY_PRESETS[bulkData.category].split_type === 'custom' ? 'per-row amounts' : TRIP_CATEGORY_PRESETS[bulkData.category].split_type}
                </p>
              )}
            </div>

            {/* Payment mode */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide">Payment Mode</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setBulkData((prev) => ({ ...prev, payment_mode: 'direct' }))}
                  data-testid="bulk-mode-direct"
                  className={`p-2.5 rounded-xl border-2 text-left ${bulkData.payment_mode === 'direct' ? 'border-primary bg-primary/10' : 'border-border'}`}
                >
                  <p className="text-xs font-bold">📌 Direct (each paid)</p>
                  <p className="text-xs text-muted-foreground">Agent-booked. Record-only.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setBulkData((prev) => ({ ...prev, payment_mode: 'single' }))}
                  data-testid="bulk-mode-single"
                  className={`p-2.5 rounded-xl border-2 text-left ${bulkData.payment_mode === 'single' ? 'border-primary bg-primary/10' : 'border-border'}`}
                >
                  <p className="text-xs font-bold">👤 One person paid</p>
                  <p className="text-xs text-muted-foreground">Settlement applies.</p>
                </button>
              </div>
            </div>

            {/* Payer (only if single) */}
            {bulkData.payment_mode === 'single' && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide">Paid By</Label>
                <Select
                  value={bulkData.payer}
                  onValueChange={(v) => setBulkData((prev) => ({ ...prev, payer: v }))}
                >
                  <SelectTrigger className="h-10 border-2 rounded-xl" data-testid="bulk-payer-select">
                    <SelectValue placeholder="Pick the payer" />
                  </SelectTrigger>
                  <SelectContent>
                    {(group?.members || []).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!bulkData.reimburse_from_fund}
                    onChange={(e) => setBulkData((prev) => ({ ...prev, reimburse_from_fund: e.target.checked }))}
                    data-testid="bulk-reimburse-toggle"
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-xs">Reimburse payer from group fund (per row)</span>
                </label>
              </div>
            )}

            {/* Shared description */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide">Shared Description</Label>
              <Input
                value={bulkData.shared_description}
                onChange={(e) => setBulkData((prev) => ({ ...prev, shared_description: e.target.value }))}
                placeholder='e.g. "Mumbai-Delhi flight"'
                data-testid="bulk-shared-desc"
                className="h-10 border-2 rounded-xl"
              />
              <p className="text-xs text-muted-foreground">Used as fallback for rows without a per-row description.</p>
            </div>

            {/* Rows — responsive: cards on mobile, table on desktop */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wide">Rows</Label>
                <p className="text-xs text-muted-foreground">
                  {(bulkData.rows || []).filter((r) => r.included && parseFloat(r.amount) > 0).length} of {(bulkData.rows || []).length} active
                </p>
              </div>

              {/* Mobile — vertical card stack */}
              <div className="space-y-2 sm:hidden" data-testid="bulk-rows-mobile">
                {(bulkData.rows || []).map((r) => {
                  const m = group?.members?.find((x) => x.id === r.user_id);
                  return (
                    <div key={r.user_id} className={`p-2.5 rounded-xl border-2 ${r.included ? 'border-border bg-card' : 'border-border/50 bg-muted/30 opacity-60'}`} data-testid={`bulk-row-card-${r.user_id}`}>
                      <label className="flex items-center gap-2 mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!r.included}
                          onChange={(e) => setBulkData((prev) => ({
                            ...prev,
                            rows: prev.rows.map((x) => x.user_id === r.user_id ? { ...x, included: e.target.checked } : x),
                          }))}
                          data-testid={`bulk-row-include-${r.user_id}`}
                          className="w-4 h-4 accent-primary"
                        />
                        <span className="text-sm font-semibold">{m?.name || r.user_id}</span>
                      </label>
                      <Input
                        type="number"
                        value={r.amount}
                        onChange={(e) => setBulkData((prev) => ({
                          ...prev,
                          rows: prev.rows.map((x) => x.user_id === r.user_id ? { ...x, amount: e.target.value } : x),
                        }))}
                        placeholder="Amount ₹"
                        data-testid={`bulk-row-amount-${r.user_id}`}
                        className="h-9 mb-1.5 rounded-lg"
                        disabled={!r.included}
                      />
                      <Input
                        value={r.description}
                        onChange={(e) => setBulkData((prev) => ({
                          ...prev,
                          rows: prev.rows.map((x) => x.user_id === r.user_id ? { ...x, description: e.target.value } : x),
                        }))}
                        placeholder="Per-row description (optional)"
                        data-testid={`bulk-row-desc-${r.user_id}`}
                        className="h-9 rounded-lg text-xs"
                        disabled={!r.included}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Desktop — compact table */}
              <div className="hidden sm:block border-2 rounded-xl overflow-hidden" data-testid="bulk-rows-desktop">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="p-2 text-left w-8">✓</th>
                      <th className="p-2 text-left">Member</th>
                      <th className="p-2 text-left w-24">Amount</th>
                      <th className="p-2 text-left">Description (optional)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bulkData.rows || []).map((r) => {
                      const m = group?.members?.find((x) => x.id === r.user_id);
                      return (
                        <tr key={r.user_id} className={`border-t border-border ${r.included ? '' : 'opacity-50'}`} data-testid={`bulk-row-desktop-${r.user_id}`}>
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={!!r.included}
                              onChange={(e) => setBulkData((prev) => ({
                                ...prev,
                                rows: prev.rows.map((x) => x.user_id === r.user_id ? { ...x, included: e.target.checked } : x),
                              }))}
                              data-testid={`bulk-row-include-d-${r.user_id}`}
                              className="w-4 h-4 accent-primary"
                            />
                          </td>
                          <td className="p-2 font-medium">{m?.name || r.user_id}</td>
                          <td className="p-2">
                            <Input
                              type="number"
                              value={r.amount}
                              onChange={(e) => setBulkData((prev) => ({
                                ...prev,
                                rows: prev.rows.map((x) => x.user_id === r.user_id ? { ...x, amount: e.target.value } : x),
                              }))}
                              placeholder="₹"
                              data-testid={`bulk-row-amount-d-${r.user_id}`}
                              className="h-8 text-right rounded"
                              disabled={!r.included}
                            />
                          </td>
                          <td className="p-2">
                            <Input
                              value={r.description}
                              onChange={(e) => setBulkData((prev) => ({
                                ...prev,
                                rows: prev.rows.map((x) => x.user_id === r.user_id ? { ...x, description: e.target.value } : x),
                              }))}
                              placeholder={bulkData.shared_description || 'Same as shared'}
                              data-testid={`bulk-row-desc-d-${r.user_id}`}
                              className="h-8 text-xs rounded"
                              disabled={!r.included}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Live total */}
              {(() => {
                const total = (bulkData.rows || [])
                  .filter((r) => r.included && parseFloat(r.amount) > 0)
                  .reduce((s, r) => s + parseFloat(r.amount), 0);
                const n = (bulkData.rows || []).filter((r) => r.included && parseFloat(r.amount) > 0).length;
                if (total === 0) return null;
                return (
                  <p className="text-xs font-bold text-foreground bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 text-center" data-testid="bulk-total">
                    Total: {formatCurrency(total)} across {n} member{n === 1 ? '' : 's'}
                  </p>
                );
              })()}
            </div>

            <Button
              type="button"
              onClick={handleBulkSubmit}
              disabled={bulkSubmitting}
              data-testid="bulk-submit-btn"
              className="w-full h-11 rounded-full text-sm font-bold uppercase tracking-wider"
            >
              {bulkSubmitting ? 'Saving…' : `Save ${(bulkData.rows || []).filter((r) => r.included && parseFloat(r.amount) > 0).length} Expense${(bulkData.rows || []).filter((r) => r.included && parseFloat(r.amount) > 0).length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Recurring Modal */}
      <Dialog open={showRecurringModal} onOpenChange={setShowRecurringModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-recurring-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-primary" />
              Add Recurring Expense
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddRecurring} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Amount</Label>
              <Input
                type="number"
                value={newRecurring.amount}
                onChange={(e) => setNewRecurring({ ...newRecurring, amount: e.target.value })}
                placeholder="0"
                data-testid="recurring-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Description</Label>
              <Input
                value={newRecurring.description}
                onChange={(e) => setNewRecurring({ ...newRecurring, description: e.target.value })}
                placeholder="e.g., Court booking"
                data-testid="recurring-description-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Paid By</Label>
              <Select
                value={newRecurring.paid_by}
                onValueChange={(value) => setNewRecurring({ ...newRecurring, paid_by: value })}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="recurring-paidby-select">
                  <SelectValue placeholder="Who pays?" />
                </SelectTrigger>
                <SelectContent>
                  {group?.members?.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Frequency</Label>
              <Select
                value={newRecurring.frequency}
                onValueChange={(value) => setNewRecurring({ ...newRecurring, frequency: value })}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="recurring-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly (routine days)</SelectItem>
                  <SelectItem value="monthly">Monthly (1st of month)</SelectItem>
                  <SelectItem value="custom_dates">Specific Dates</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newRecurring.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Days of Week</Label>
                <div className="flex flex-wrap gap-2">
                  {dayNames.map((day, index) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDayOfWeek(index)}
                      data-testid={`day-${index}`}
                      className={`px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                        newRecurring.days_of_week.includes(index)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card border-2 border-border hover:border-primary/50'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {newRecurring.frequency === 'custom_dates' && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Select Dates</Label>
                <div className="border-2 rounded-xl p-2 flex justify-center">
                  <CalendarPicker
                    mode="multiple"
                    selected={newRecurring.custom_dates}
                    onSelect={(dates) => setNewRecurring({ ...newRecurring, custom_dates: dates || [] })}
                    disabled={(date) => date < new Date()}
                    data-testid="custom-dates-calendar"
                    className="rounded-xl"
                  />
                </div>
                {newRecurring.custom_dates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="selected-dates-list">
                    {newRecurring.custom_dates.map((date, i) => (
                      <span key={i} className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary font-semibold">
                        {format(date, 'MMM d')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Split Among</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {group?.members?.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer hover:border-primary/50"
                    onClick={() => toggleMemberInSplit(member.id, true)}
                  >
                    <Checkbox
                      checked={newRecurring.split_among.includes(member.id)}
                      onCheckedChange={() => toggleMemberInSplit(member.id, true)}
                      data-testid={`recurring-split-member-${member.id}`}
                    />
                    <span className="font-medium">{member.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-recurring-btn"
              className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
            >
              {submitting ? 'Creating...' : 'Create Recurring'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Member Modal */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="sm:max-w-md" data-testid="add-member-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add Member</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            {/* iter255 — quick-pick registered users */}
            {quickPickUsers.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">
                  Quick add — already on SplitEasy
                </Label>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="quick-pick-list">
                  {quickPickUsers.map((u) => {
                    const sel = selectedQuickIds.has(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setSelectedQuickIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(u.id)) next.delete(u.id); else next.add(u.id);
                          return next;
                        })}
                        data-testid={`quick-pick-${u.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${
                          sel
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                        }`}
                      >
                        {sel ? '✓ ' : ''}{u.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Member Phone{'(s)'}</Label>
              <Input
                type="tel"
                value={newMemberPhone}
                onChange={(e) => setNewMemberPhone(e.target.value)}
                placeholder="9820030046, 9876543210, …"
                data-testid="new-member-phone-input"
                className="h-12 border-2 rounded-xl"
              />
              {/* iter128 — live lookup feedback */}
              {phoneLookup.status === 'loading' && (
                <p className="text-xs text-muted-foreground" data-testid="phone-lookup-loading">
                  Looking up…
                </p>
              )}
              {phoneLookup.status === 'found' && phoneLookup.data?.user && (
                <p
                  className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1"
                  data-testid="phone-lookup-found"
                >
                  <span aria-hidden>✓</span> {phoneLookup.data.user.name} will be added
                </p>
              )}
              {phoneLookup.status === 'already_member' && (
                <p
                  className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1"
                  data-testid="phone-lookup-already"
                >
                  <span aria-hidden>•</span> {phoneLookup.data?.user?.name || 'They'} are already in this group
                </p>
              )}
              {phoneLookup.status === 'not_found' && (
                <div
                  className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 space-y-2.5"
                  data-testid="phone-lookup-not-found"
                >
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                    No SplitEasy account for this number yet.
                  </p>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-foreground/70">
                      Ghost name (no account needed)
                    </Label>
                    <Input
                      type="text"
                      value={shadowName}
                      onChange={(e) => setShadowName(e.target.value)}
                      placeholder="e.g. Sanjay (best guess works)"
                      data-testid="shadow-member-name-input"
                      className="h-10 border-2 rounded-lg bg-background"
                      autoComplete="off"
                    />
                    <p className="text-xs text-muted-foreground leading-snug">
                      Split expenses with them right away. When they sign up with this number later, their balances auto-merge.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      onClick={handleAddShadowMember}
                      disabled={submitting || !shadowName.trim()}
                      data-testid="add-as-ghost-btn"
                      className="rounded-full h-9 text-xs font-bold flex-1 min-w-[140px]"
                    >
                      {submitting ? 'Adding…' : 'Add as ghost'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        // iter248 — send from SplitEasy's Meta WhatsApp number first;
                        // fall back to the personal-WhatsApp share sheet if unavailable.
                        const phone = newMemberPhone;
                        try {
                          await sendSystemWaInvite(group.id, phone, shadowName.trim());
                          toast.success(`Invite sent to ${phone} from SplitEasy's WhatsApp ✓`);
                          setShowAddMember(false);
                          setNewMemberPhone('');
                          setShadowName('');
                          setPhoneLookup({ status: 'idle', data: null });
                        } catch (e) {
                          toast.info('System WhatsApp unavailable — opening share options instead');
                          setShowAddMember(false);
                          setNewMemberPhone('');
                          setShadowName('');
                          setPhoneLookup({ status: 'idle', data: null });
                          invite.openShareMenu(group);
                        }
                      }}
                      data-testid="phone-lookup-send-invite-btn"
                      className="rounded-full h-9 text-xs font-bold"
                    >
                      WhatsApp invite
                    </Button>
                  </div>
                </div>
              )}
              {phoneLookup.status === 'idle' && (
                <p className="text-xs text-muted-foreground">
                  Type several numbers separated by commas to add in bulk. Unknown numbers become Pending invites.
                </p>
              )}
            </div>

            {phoneLookup.status !== 'not_found' && (() => {
              const typedCount = (newMemberPhone || '').split(/[,;\s]+/).filter((p) => p.trim().length >= 4).length;
              const total = typedCount + selectedQuickIds.size;
              return (
              <Button
                onClick={handleAddMember}
                disabled={submitting || phoneLookup.status === 'loading' || total === 0}
                data-testid="submit-add-member-btn"
                className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
              >
                {submitting ? 'Adding...' : total > 1 ? `Add ${total} Members` : 'Add Member'}
              </Button>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settle Modal */}
      <Dialog open={showSettleModal} onOpenChange={setShowSettleModal}>
        <DialogContent className="sm:max-w-md" data-testid="settle-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Confirm Settlement</DialogTitle>
          </DialogHeader>
          
          {selectedSettlement && (
            <div className="space-y-5 mt-4">
              <Card className="p-4 border-2 rounded-xl">
                <div className="text-center">
                  <p className="text-muted-foreground">
                    <span className="font-semibold text-foreground">{selectedSettlement.from_name}</span>
                    {' '}pays{' '}
                    <span className="font-semibold text-foreground">{selectedSettlement.to_name}</span>
                  </p>
                  <p className="font-display text-3xl font-bold text-primary mt-2 amount-display">
                    {formatCurrency(selectedSettlement.amount)}
                  </p>
                </div>
              </Card>

              <Button
                onClick={handleSettle}
                disabled={submitting}
                data-testid="confirm-settle-btn"
                className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
              >
                {submitting ? 'Recording...' : 'Mark as Settled'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete Group?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the group and all its expenses. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroup}
              data-testid="confirm-delete-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Expense Modal */}
      <Dialog open={showEditExpense} onOpenChange={setShowEditExpense}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-expense-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit Expense
            </DialogTitle>
          </DialogHeader>
          
          {editingExpense && (
            <form onSubmit={handleEditExpense} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Amount</Label>
                <Input
                  type="number"
                  value={editingExpense.amount}
                  onChange={(e) => setEditingExpense({ ...editingExpense, amount: e.target.value })}
                  data-testid="edit-expense-amount"
                  className="h-14 text-2xl font-bold border-2 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Description</Label>
                <Input
                  value={editingExpense.description}
                  onChange={(e) => setEditingExpense({ ...editingExpense, description: e.target.value })}
                  data-testid="edit-expense-description"
                  className="h-12 border-2 rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Category</Label>
                <Select
                  value={editingExpense.category}
                  onValueChange={(value) => setEditingExpense({ ...editingExpense, category: value })}
                >
                  <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="edit-expense-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryIcons).map(([key, Icon]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span className="capitalize">{key}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Paid By</Label>
                <Select
                  value={editingExpense.paid_by}
                  onValueChange={(value) => setEditingExpense({ ...editingExpense, paid_by: value })}
                >
                  <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="edit-expense-paidby">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {group?.members?.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Split Among</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {group?.members?.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-3 p-3 border-2 rounded-xl"
                    >
                      <Checkbox
                        checked={editingExpense.split_among.includes(member.id)}
                        onCheckedChange={() => {
                          setEditingExpense(prev => ({
                            ...prev,
                            split_among: prev.split_among.includes(member.id)
                              ? prev.split_among.filter(id => id !== member.id)
                              : [...prev.split_among, member.id],
                          }));
                        }}
                        data-testid={`edit-split-member-${member.id}`}
                      />
                      <span className="font-medium">{member.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-edit-expense-btn"
                className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite Link Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-md" data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Invite Members
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            {!inviteCode ? (
              <div className="text-center space-y-4">
                <p className="text-muted-foreground text-sm">
                  Generate a shareable invite link. Anyone with this link can join the group.
                </p>
                <Button
                  onClick={handleGenerateInvite}
                  disabled={inviteLoading}
                  data-testid="generate-invite-btn"
                  className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
                >
                  {inviteLoading ? 'Generating...' : 'Generate Invite Link'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 border-2 rounded-xl bg-muted/50" data-testid="invite-code-display">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Invite Code</p>
                  <p className="font-display text-2xl font-bold text-primary tracking-widest">{inviteCode}</p>
                </div>

                {/* Rich preview thumbnail — what recipients will see in WhatsApp */}
                <div className="rounded-xl overflow-hidden border-2 border-border shadow-sm" data-testid="invite-og-preview">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/invites/${inviteCode}/og.png`}
                    alt="Invite preview"
                    className="w-full h-auto block"
                    loading="lazy"
                  />
                  <div className="px-3 py-2 bg-muted/30 text-xs text-muted-foreground text-center font-semibold uppercase tracking-wider">
                    Preview · How recipients see your invite
                  </div>
                </div>

                <Button
                  onClick={shareInviteRich}
                  data-testid="share-invite-rich-btn"
                  className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Share with rich preview
                </Button>

                <Button
                  onClick={copyInviteLink}
                  data-testid="copy-invite-btn"
                  variant="outline"
                  className="w-full h-11 rounded-full font-bold uppercase tracking-wider border-2"
                >
                  <Copy className="w-5 h-5 mr-2" />
                  Copy plain link
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Link expires in 7 days. Max 50 uses.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog open={!!showComments} onOpenChange={(open) => { if (!open) { setShowComments(null); setComments([]); setNewComment(''); } }}>
        <DialogContent className="sm:max-w-md max-h-[80vh] flex flex-col" data-testid="comments-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              Comments
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-3 mt-2 min-h-[120px]">
            {commentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-pulse text-muted-foreground text-sm">Loading...</div>
              </div>
            ) : comments.length === 0 ? (
              <EmptyState
                illustration={EmptyComments}
                title="No comments yet"
                description="Start the discussion."
                card={false}
                size="sm"
                testId="no-comments"
              />
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="flex gap-3 group" data-testid={`comment-${comment.id}`}>
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-primary">
                      {comment.user_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{comment.user_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                      {comment.user_id === user?.id && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive/80 transition-opacity"
                          data-testid={`delete-comment-${comment.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{comment.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex gap-2 mt-3 pt-3 border-t">
            <Input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              data-testid="comment-input"
              className="h-10 border-2 rounded-xl flex-1"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(); }}
            />
            <Button
              size="sm"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              data-testid="send-comment-btn"
              className="rounded-xl h-10 px-3"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fund Setup Modal — iter216: renamed for clarity vs "Adjust ledger" */}
      <Dialog open={showFundModal} onOpenChange={setShowFundModal}>
        <DialogContent className="sm:max-w-md" data-testid="fund-setup-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Add cash to fund
            </DialogTitle>
            <p className="text-xs text-muted-foreground -mt-1">
              Someone paid real money into the group kitty. Fund balance goes up.
            </p>
          </DialogHeader>
          <form onSubmit={handleSetupFund} className="space-y-4 mt-4">
            {/* iter191 — Trip multi-currency: which currency the top-up is in */}
            {group?.trip_currency && group?.trip_rate && (
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Top-up in</Label>
                <div className="grid grid-cols-2 gap-1.5 rounded-xl border-2 border-border p-1 bg-muted/30">
                  {[
                    { code: group.currency || 'INR', sym: CURRENCY_SYMBOLS[group.currency || 'INR'] || '₹' },
                    { code: group.trip_currency, sym: CURRENCY_SYMBOLS[group.trip_currency] || group.trip_currency },
                  ].map((opt) => {
                    const active = fundEntryCurrency === opt.code;
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => setFundEntryCurrency(opt.code)}
                        data-testid={`fund-currency-${opt.code}`}
                        className={`rounded-lg px-3 py-2 text-sm font-bold tabular-nums transition ${active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-accent'}`}
                      >
                        {opt.sym} {opt.code}
                      </button>
                    );
                  })}
                </div>
                {Number(fundAmount) > 0 && fundEntryCurrency && fundEntryCurrency !== (group.currency || 'INR') && (
                  <p className="text-xs text-muted-foreground tabular-nums" data-testid="fund-conversion-preview">
                    ≈ {formatNXC(Number(fundAmount) * Number(group.trip_rate || 1), group.currency || 'INR')} at the locked rate
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Total Amount</Label>
              <Input
                type="number"
                value={fundAmount}
                onChange={(e) => setFundAmount(e.target.value)}
                placeholder="e.g. 50000"
                data-testid="fund-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
              {fundAmount && fundContributorId === 'split' && group?.member_ids?.length > 0 && (
                <p className="text-sm text-muted-foreground" data-testid="per-member-amount">
                  {formatCurrency(parseFloat(fundAmount) / group.member_ids.length)} per member ({group.member_ids.length} members)
                </p>
              )}
              {fundAmount && fundContributorId === 'sponsor' && (
                <p className="text-sm text-muted-foreground">Fund-only credit — no member money affected.</p>
              )}
              {fundAmount && fundContributorId !== 'split' && fundContributorId !== 'sponsor' && (
                <p className="text-sm text-muted-foreground">
                  Full amount credited to {group?.members?.find(m => m.id === fundContributorId)?.name || 'selected member'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Contributor</Label>
              <select
                value={fundContributorId}
                onChange={(e) => {
                  const val = e.target.value;
                  // iter215/216 — Warn on External-Donation selection to prevent misclick that skips member coin credit
                  if (val === 'sponsor') {
                    const ok = window.confirm(
                      '⚠️ External Donation mode\n\n' +
                      'This is for anonymous donations only (e.g. from an outsider or brand). ' +
                      'It will credit ONLY the group fund — no member\'s NexaCoin balance will be updated.\n\n' +
                      'If a MEMBER is actually paying, cancel and pick their name from the "Individual contribution by…" list instead.\n\n' +
                      'Continue as External Donation?'
                    );
                    if (!ok) return; // keep previous value
                  }
                  setFundContributorId(val);
                }}
                data-testid="fund-contributor-select"
                className="w-full h-12 border-2 rounded-xl bg-background px-3 text-sm"
              >
                {/* iter215 + iter216 — Reordered: individual members first (most common), split middle, sponsor last (renamed for clarity) */}
                <optgroup label="Individual contribution by…">
                  {group?.members?.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </optgroup>
                <option value="split">Split equally across all members</option>
                {group?.allow_external_donations && (
                  <option value="sponsor">⚠️ External donation (no member involved — fund-only)</option>
                )}
              </select>
              {fundContributorId === 'sponsor' && (
                <div
                  data-testid="fund-sponsor-warning"
                  className="rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-500 p-3 text-xs"
                >
                  <p className="font-bold text-amber-800 dark:text-amber-200">You picked EXTERNAL DONATION</p>
                  <p className="mt-1 text-amber-700 dark:text-amber-300">
                    Fund will be credited, but NO member&apos;s balance will change. If a member is actually paying,
                    cancel and pick their name from the list above.
                  </p>
                </div>
              )}
            </div>

            {/* Received vs Payment Pending — only relevant when there's a real contributor */}
            {fundContributorId !== 'sponsor' && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Cash status</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="fund-status-toggle">
                  <button
                    type="button"
                    onClick={() => setFundStatus('received')}
                    data-testid="fund-status-received"
                    className={`h-12 rounded-xl border-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
                      fundStatus === 'received'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    Received
                    <span className="block text-xs font-normal normal-case mt-0.5 opacity-80">Cash already in hand</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFundStatus('pledged')}
                    data-testid="fund-status-pledged"
                    className={`h-12 rounded-xl border-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
                      fundStatus === 'pledged'
                        ? 'border-amber-500 bg-amber-500/10 text-amber-700'
                        : 'border-border bg-background text-muted-foreground hover:bg-accent'
                    }`}
                  >
                    Payment Pending
                    <span className="block text-xs font-normal normal-case mt-0.5 opacity-80">Promised, collect later</span>
                  </button>
                </div>
                {fundStatus === 'pledged' && (
                  <p className="text-xs text-amber-700/90 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                    Tracked as Payment Pending — fund balance and member money are NOT updated until you mark received.
                  </p>
                )}
              </div>
            )}

            {/* Stage-2 Loan flag — only valid for individual + received contributions */}
            {fundContributorId !== 'sponsor' && fundContributorId !== 'split' && fundStatus === 'received' && (
              <div className="space-y-2" data-testid="fund-loan-toggle-wrap">
                <div className="flex items-start gap-3 p-3 rounded-xl border-2 border-border bg-background">
                  <input
                    type="checkbox"
                    id="fund-is-loan"
                    checked={fundIsLoan}
                    onChange={(e) => setFundIsLoan(e.target.checked)}
                    data-testid="fund-is-loan-checkbox"
                    className="w-5 h-5 mt-0.5 accent-primary cursor-pointer"
                  />
                  <label htmlFor="fund-is-loan" className="flex-1 cursor-pointer">
                    <div className="text-sm font-semibold">Track as a loan to the group</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Member fronts cash, group will repay later. Adds Repay Now button on the fund tab.
                    </div>
                  </label>
                </div>
                {fundIsLoan && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Expected repayment date (optional)</Label>
                    <Input
                      type="date"
                      value={fundLoanDueDate}
                      onChange={(e) => setFundLoanDueDate(e.target.value)}
                      data-testid="fund-loan-due-date"
                      className="h-12 border-2 rounded-xl"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Note (optional)</Label>
              <Input
                value={fundNote}
                onChange={(e) => setFundNote(e.target.value)}
                placeholder="e.g. Monthly court fee collection"
                data-testid="fund-note-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <Button type="submit" disabled={submitting} data-testid="submit-fund-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {submitting ? 'Adding...' : (
                fundContributorId !== 'sponsor' && fundStatus === 'pledged' ? 'Record Payment Pending'
                : fundIsLoan && fundContributorId !== 'sponsor' && fundContributorId !== 'split' && fundStatus === 'received' ? 'Record Loan to Group'
                : 'Add to Fund'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Guest Contribution Modal */}
      <Dialog open={showGuestContribModal} onOpenChange={setShowGuestContribModal}>
        <DialogContent className="sm:max-w-md" data-testid="guest-contrib-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Guest Contribution
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Cash paid to the group fund by an external (non-member) guest. Adds to fund balance — does NOT affect any member's money.
            </p>
          </DialogHeader>
          <form onSubmit={handleAddGuestContribution} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Guest Name</Label>
              <Input
                value={guestContribForm.guest_name}
                onChange={(e) => setGuestContribForm((p) => ({ ...p, guest_name: e.target.value }))}
                placeholder="e.g. Rohit (Sanjay's friend)"
                data-testid="guest-contrib-name-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Amount (₹)</Label>
              <Input
                type="number"
                value={guestContribForm.amount}
                onChange={(e) => setGuestContribForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="e.g. 500"
                data-testid="guest-contrib-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Cash status</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="guest-contrib-status-toggle">
                <button
                  type="button"
                  onClick={() => setGuestContribForm((p) => ({ ...p, status: 'received' }))}
                  data-testid="guest-contrib-status-received"
                  className={`h-12 rounded-xl border-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
                    guestContribForm.status === 'received'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:bg-accent'
                  }`}
                >
                  Received
                </button>
                <button
                  type="button"
                  onClick={() => setGuestContribForm((p) => ({ ...p, status: 'pledged' }))}
                  data-testid="guest-contrib-status-pledged"
                  className={`h-12 rounded-xl border-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
                    guestContribForm.status === 'pledged'
                      ? 'border-amber-500 bg-amber-500/10 text-amber-700'
                      : 'border-border bg-background text-muted-foreground hover:bg-accent'
                  }`}
                >
                  Payment Pending
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Note (optional)</Label>
              <Input
                value={guestContribForm.note}
                onChange={(e) => setGuestContribForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. Played as guest on Saturday"
                data-testid="guest-contrib-note-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <Button type="submit" disabled={submitting} data-testid="submit-guest-contrib-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {submitting ? 'Recording...' : (guestContribForm.status === 'pledged' ? 'Record Payment Pending' : 'Add to Fund')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Loan to Member Modal */}
      <Dialog open={showLoanToMemberModal} onOpenChange={setShowLoanToMemberModal}>
        <DialogContent className="sm:max-w-md" data-testid="loan-to-member-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <NXCIcon className="w-5 h-5 text-amber-600" />
              Loan to Member
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Group fund lends money to a member. Fund balance decreases; member's balance decreases (they owe the group). Repayment cleared via Repay Now.
            </p>
          </DialogHeader>
          <form onSubmit={handleLoanToMember} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Borrower</Label>
              <select
                value={loanToMemberForm.member_id}
                onChange={(e) => setLoanToMemberForm((p) => ({ ...p, member_id: e.target.value }))}
                data-testid="loan-borrower-select"
                className="w-full h-12 border-2 rounded-xl bg-background px-3 text-sm"
              >
                <option value="">Select a member…</option>
                {group?.members?.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Amount (₹)</Label>
              <Input
                type="number"
                value={loanToMemberForm.amount}
                onChange={(e) => setLoanToMemberForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="e.g. 1000"
                data-testid="loan-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
              <p className="text-xs text-muted-foreground">Available fund: {formatNXC(fundData?.fund_balance ?? 0)}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Expected repayment date (optional)</Label>
              <Input
                type="date"
                value={loanToMemberForm.expected_repayment_date}
                onChange={(e) => setLoanToMemberForm((p) => ({ ...p, expected_repayment_date: e.target.value }))}
                data-testid="loan-due-date-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Note (optional)</Label>
              <Input
                value={loanToMemberForm.note}
                onChange={(e) => setLoanToMemberForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. Advance for next month's gear"
                data-testid="loan-note-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <Button type="submit" disabled={submitting} data-testid="submit-loan-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {submitting ? 'Recording...' : 'Issue Loan'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fine Modal */}
      <Dialog open={showFineModal} onOpenChange={setShowFineModal}>
        <DialogContent className="sm:max-w-md" data-testid="fine-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Issue Fine
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleIssueFine} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Member</Label>
              <Select value={fineData.user_id} onValueChange={(value) => setFineData({ ...fineData, user_id: value })}>
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="fine-member-select">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {group?.members?.map((member) => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Fine Type</Label>
              <Select value={fineData.fine_type} onValueChange={(value) => {
                const defaultAmount = fineDefaults[value] || '';
                setFineData({ ...fineData, fine_type: value, amount: String(defaultAmount) });
              }}>
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="fine-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="late_10">Late ≤{fineDefaults.late_threshold_minutes ?? 10}min ({formatCurrency(fineDefaults.late_10)})</SelectItem>
                  <SelectItem value="late_above_10">Late &gt;{fineDefaults.late_threshold_minutes ?? 10}min ({formatCurrency(fineDefaults.late_above_10)})</SelectItem>
                  <SelectItem value="cancellation">Cancellation ({formatCurrency(fineDefaults.cancellation)})</SelectItem>
                  <SelectItem value="no_show">No Show ({formatCurrency(fineDefaults.no_show)})</SelectItem>
                  <SelectItem value="custom">Custom Fine</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Amount</Label>
              <Input
                type="number"
                value={fineData.amount}
                onChange={(e) => setFineData({ ...fineData, amount: e.target.value })}
                placeholder="e.g. 200"
                data-testid="fine-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Reason (optional)</Label>
              <Input
                value={fineData.reason}
                onChange={(e) => setFineData({ ...fineData, reason: e.target.value })}
                placeholder="e.g. Late to court session"
                data-testid="fine-reason-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <Button type="submit" disabled={submitting} data-testid="submit-fine-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {submitting ? 'Issuing...' : 'Issue Fine'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Session Modal */}
      <Dialog open={showCreateSession} onOpenChange={setShowCreateSession}>
        <DialogContent className="sm:max-w-md" data-testid="create-session-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-primary" />
              New Session
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSession} className="space-y-4 mt-4">
            {/* Quick templates */}
            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={() => applySessionTemplate('defaults_morning_730')} data-testid="tmpl-defaults" className="p-2 border-2 rounded-xl text-xs font-semibold hover:bg-muted">
                <span className="block">Defaults</span>
                <span className="text-xs text-muted-foreground">{sessionDefaults.start_time} · {sessionDefaults.num_courts}×</span>
              </button>
              <button type="button" onClick={() => applySessionTemplate('morning_7')} data-testid="tmpl-morning7" className="p-2 border-2 rounded-xl text-xs font-semibold hover:bg-muted">
                <span className="block">7:00 AM</span>
                <span className="text-xs text-muted-foreground">Early slot</span>
              </button>
              <button type="button" onClick={() => applySessionTemplate('saturday_random')} data-testid="tmpl-saturday" className="p-2 border-2 rounded-xl text-xs font-semibold hover:bg-muted">
                <span className="block">Saturday</span>
                <span className="text-xs text-muted-foreground">Random one-off</span>
              </button>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Session Title</Label>
              <Input
                value={newSession.title}
                onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                placeholder="e.g. Tuesday Court Session"
                data-testid="session-title-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Date</Label>
                <Input
                  type="date"
                  value={newSession.date}
                  onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                  data-testid="session-date-input"
                  className="h-12 border-2 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Start Time</Label>
                <Input
                  type="time"
                  value={newSession.start_time}
                  onChange={(e) => setNewSession({ ...newSession, start_time: e.target.value })}
                  data-testid="session-start-time-input"
                  className="h-12 border-2 rounded-xl"
                />
              </div>
            </div>

            {/* Num courts */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Number of courts</Label>
              <div className="flex gap-2">
                {[1, 2, 3].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNewSession({ ...newSession, num_courts: n })}
                    data-testid={`num-courts-${n}`}
                    className={`flex-1 h-12 rounded-xl border-2 font-display font-bold transition-colors ${
                      newSession.num_courts === n ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'
                    }`}
                  >
                    {n} {n === 1 ? 'Court' : 'Courts'}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Players: min {newSession.num_courts * 5} &middot; max {newSession.num_courts * 6}
              </p>
            </div>

            {/* iter309 — per-session fee (blank = use the group default) */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide">Fee per player (₹)</Label>
              <Input
                type="number"
                min="0"
                value={newSession.session_fee ?? ''}
                placeholder={`${Math.round(parseFloat(fineDefaults.session_fee) || 0)} (group default)`}
                onChange={(e) => setNewSession({ ...newSession, session_fee: e.target.value })}
                data-testid="session-fee-input"
                className="h-11 border-2 rounded-xl"
              />
              <p className="text-xs text-muted-foreground">Leave blank to use the group default. Charged when you tap “Charge fee” after attendance.</p>
            </div>

            {/* iter323 — who fronts the court cash */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide">Who pays the court?</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="venue-payer-picker">
                {[
                  { id: 'rotation', label: 'Rotation / ask later' },
                  { id: 'group_fund', label: 'Group fund' },
                ].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`venue-payer-${o.id}`}
                    onClick={() => setNewSession({ ...newSession, venue_payer: o.id })}
                    className={`h-9 px-3 rounded-full border text-xs font-bold transition-colors ${(newSession.venue_payer || 'rotation') === o.id ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}
                  >
                    {o.label}
                  </button>
                ))}
                {(group?.members || []).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`venue-payer-${m.id}`}
                    onClick={() => setNewSession({ ...newSession, venue_payer: m.id })}
                    className={`h-9 px-3 rounded-full border text-xs font-semibold transition-colors ${newSession.venue_payer === m.id ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}
                  >
                    {m.name?.split(' ')[0]}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Group fund settles it straight away — no “Who paid?” prompt later. A person gets reimbursed from the fund once you confirm.
              </p>
            </div>

            {/* RSVP toggle */}
            <div className="flex items-center justify-between p-3 border-2 rounded-xl">
              <div>
                <Label className="font-semibold">Enable RSVP (IN/OUT)</Label>
                <p className="text-xs text-muted-foreground">Members tap IN to reserve a spot. First {newSession.num_courts * 6} qualify.</p>
              </div>
              <Switch
                checked={newSession.rsvp_enabled}
                onCheckedChange={(v) => setNewSession({ ...newSession, rsvp_enabled: v })}
                data-testid="rsvp-enabled-switch"
              />
            </div>

            {/* RSVP preset cards */}
            {newSession.rsvp_enabled && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">RSVP window</Label>
                <div className="space-y-2">
                  {[
                    { v: 'open_now_close_when_full', title: 'Open now', sub: `Closes when first ${newSession.num_courts * 6} qualify (or at session time)` },
                    { v: 'open_now_close_1h_before', title: 'Open now, close 1h before session', sub: 'Admin-preferred for last-minute drop-outs' },
                    { v: 'custom', title: 'Custom window', sub: 'Pick exact open & close times' },
                  ].map((p) => (
                    <button
                      key={p.v}
                      type="button"
                      onClick={() => setNewSession({ ...newSession, rsvp_preset: p.v })}
                      data-testid={`rsvp-preset-${p.v}`}
                      className={`w-full p-3 rounded-xl border-2 text-left transition-colors ${
                        newSession.rsvp_preset === p.v ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                      }`}
                    >
                      <p className="font-semibold text-sm">{p.title}</p>
                      <p className="text-xs text-muted-foreground">{p.sub}</p>
                    </button>
                  ))}
                </div>
                {newSession.rsvp_preset === 'custom' && (
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <div>
                      <Label className="text-xs">Opens at</Label>
                      <Input
                        type="datetime-local"
                        value={newSession.rsvp_opens_at}
                        onChange={(e) => setNewSession({ ...newSession, rsvp_opens_at: e.target.value })}
                        data-testid="rsvp-opens-input"
                        className="h-10 border-2 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Closes at</Label>
                      <Input
                        type="datetime-local"
                        value={newSession.rsvp_closes_at}
                        onChange={(e) => setNewSession({ ...newSession, rsvp_closes_at: e.target.value })}
                        data-testid="rsvp-closes-input"
                        className="h-10 border-2 rounded-xl"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <Button type="submit" disabled={submitting} data-testid="submit-session-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {submitting ? 'Creating...' : 'Create Session'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Session Defaults Modal */}
      <Dialog open={showSessionDefaults} onOpenChange={setShowSessionDefaults}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="session-defaults-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Session Defaults
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSessionDefaults} className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">Save your usual session settings so creating a new session is 1 tap.</p>

            {/* iter226 — Venue library shortcut */}
            <div className="flex items-center justify-between bg-muted/30 rounded-xl border-2 p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold">Venue Library</p>
                <p className="text-xs text-muted-foreground">Predefine venues + fees for auto-filled reimbursements.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setShowSessionDefaults(false); setShowVenueManager(true); }}
                data-testid="open-venue-manager-btn"
              >
                Manage Venues
              </Button>
            </div>

            {/* iter228 — Payment rotation shortcut */}
            <div className="flex items-center justify-between bg-muted/30 rounded-xl border-2 p-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold">Payment Rotation</p>
                <p className="text-xs text-muted-foreground">Auto-pre-fill who pays each month or round-robin.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setShowSessionDefaults(false); setShowPaymentRotation(true); }}
                data-testid="open-payment-rotation-btn"
              >
                Configure
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide">Default start time</Label>
                <Input
                  type="time"
                  value={sessionDefaults.start_time}
                  onChange={(e) => setSessionDefaults({ ...sessionDefaults, start_time: e.target.value })}
                  data-testid="defaults-start-time-input"
                  className="h-11 border-2 rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide">Courts</Label>
                <div className="flex gap-1">
                  {[1, 2, 3].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setSessionDefaults({ ...sessionDefaults, num_courts: n })}
                      data-testid={`defaults-num-courts-${n}`}
                      className={`flex-1 h-11 rounded-xl border-2 font-display font-bold text-sm transition-colors ${sessionDefaults.num_courts === n ? 'border-primary bg-primary/10 text-primary' : 'border-border'}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* iter289 — session fee lives here too (same value as Fine Defaults) */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide">Session fee per attendee (₹)</Label>
              <Input
                type="number"
                min="0"
                value={fineDefaults.session_fee ?? 0}
                onChange={(e) => setFineDefaults({ ...fineDefaults, session_fee: parseFloat(e.target.value) || 0 })}
                data-testid="defaults-session-fee-input"
                className="h-11 border-2 rounded-xl font-bold"
              />
              <p className="text-xs text-muted-foreground">What each present member is charged when you tap &ldquo;Charge fee&rdquo; on a completed session. Set 0 to disable charging.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide">Session title template</Label>
              <Input
                value={sessionDefaults.title_template}
                onChange={(e) => setSessionDefaults({ ...sessionDefaults, title_template: e.target.value })}
                data-testid="defaults-title-template-input"
                placeholder="Pickle — {day}"
                className="h-11 border-2 rounded-xl"
              />
              <p className="text-xs text-muted-foreground">{'{day}'} gets replaced with Mon/Tue/etc on auto-created sessions.</p>
            </div>

            {/* Auto-create cron */}
            <div className="p-3 border-2 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="font-semibold">Auto-create sessions</Label>
                  <p className="text-xs text-muted-foreground">System creates sessions at 6 AM IST on the days you pick.</p>
                </div>
                <Switch
                  checked={sessionDefaults.auto_create_enabled}
                  onCheckedChange={(v) => setSessionDefaults({ ...sessionDefaults, auto_create_enabled: v })}
                  data-testid="defaults-auto-create-switch"
                />
              </div>

              {sessionDefaults.auto_create_enabled && (
                <>
                  <div className="flex gap-1 flex-wrap">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => {
                      const selected = (sessionDefaults.days_of_week || []).includes(i);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => {
                            const set = new Set(sessionDefaults.days_of_week || []);
                            if (set.has(i)) set.delete(i); else set.add(i);
                            setSessionDefaults({ ...sessionDefaults, days_of_week: [...set].sort() });
                          }}
                          data-testid={`defaults-day-${i}`}
                          className={`px-3 py-2 rounded-full border-2 text-xs font-semibold transition-colors ${selected ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Open RSVP X hrs before</Label>
                      <Input
                        type="number"
                        min="1"
                        max="72"
                        value={sessionDefaults.rsvp_open_hours_before}
                        onChange={(e) => setSessionDefaults({ ...sessionDefaults, rsvp_open_hours_before: parseInt(e.target.value) || 1 })}
                        data-testid="defaults-rsvp-open-hours-input"
                        className="h-11 border-2 rounded-xl"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Close X hrs before</Label>
                      <Input
                        type="number"
                        min="0"
                        value={sessionDefaults.rsvp_close_hours_before}
                        onChange={(e) => setSessionDefaults({ ...sessionDefaults, rsvp_close_hours_before: parseInt(e.target.value) || 0 })}
                        data-testid="defaults-rsvp-close-hours-input"
                        className="h-11 border-2 rounded-xl"
                      />
                    </div>
                  </div>
                  {/* iter172 — Manual nudge cooldown (group-level, persisted via PUT /groups/{id}). */}
                  <div className="space-y-1 mt-2 pt-3 border-t border-border">
                    <Label className="text-xs flex items-center justify-between">
                      <span>Manual Nudge Cooldown (min)</span>
                      <span className="text-xs text-muted-foreground font-normal">0 = no limit</span>
                    </Label>
                    <Input
                      type="number" min="0" max="1440"
                      value={group?.rsvp_nudge_cooldown_minutes ?? 30}
                      onChange={async (e) => {
                        const v = Math.max(0, Math.min(1440, parseInt(e.target.value, 10) || 0));
                        try {
                          await api.put(`/groups/${groupId}`, { rsvp_nudge_cooldown_minutes: v });
                          fetchGroup();
                        } catch { /* silent */ }
                      }}
                      data-testid="rsvp-nudge-cooldown-input"
                      className="h-11 border-2 rounded-xl"
                    />
                    <p className="text-xs text-muted-foreground leading-snug">
                      Minimum gap between manual "Nudge pending" actions per session. AI Co-Admin's automatic T-2h nudge ignores this.
                    </p>
                  </div>
                  {/* Live preview */}
                  {sessionDefaults.days_of_week?.length > 0 && (() => {
                    const dayNames = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
                    const firstDay = sessionDefaults.days_of_week[0];
                    const [hh, mm] = (sessionDefaults.start_time || '07:30').split(':').map(n => parseInt(n) || 0);

                    // Work in IST by converting current UTC to an IST wallclock timestamp
                    // via Intl, then iterate by day. Display via Intl for accuracy.
                    const IST_FMT = new Intl.DateTimeFormat('en-US', {
                      timeZone: 'Asia/Kolkata', weekday: 'short',
                      hour: '2-digit', minute: '2-digit', hour12: false,
                      year: 'numeric', month: '2-digit', day: '2-digit',
                    });
                    // Build target: next occurrence of firstDay at hh:mm IST.
                    // Algorithm: today's IST date at hh:mm, bump by 1 day until weekday matches + is in future.
                    const istPartsNow = IST_FMT.formatToParts(new Date()).reduce((acc, p) => (acc[p.type] = p.value, acc), {});
                    const istYear = parseInt(istPartsNow.year);
                    const istMonth = parseInt(istPartsNow.month);
                    const istDay = parseInt(istPartsNow.day);
                    // target = IST midnight of today (UTC-5:30 behind, so IST midnight = previous-day 18:30 UTC)
                    // Simpler: construct a UTC Date corresponding to today-IST hh:mm; then iterate.
                    // IST is UTC+5:30 → to get UTC of "today 07:00 IST" = today 01:30 UTC.
                    let target = new Date(Date.UTC(istYear, istMonth - 1, istDay, hh - 5, mm - 30));
                    // Normalize negative minutes
                    if (target.getUTCMinutes() < 0 || target.getUTCHours() < 0) {
                      target = new Date(Date.UTC(istYear, istMonth - 1, istDay, hh, mm) - (5 * 60 + 30) * 60000);
                    }
                    const nowUtc = new Date();
                    let tries = 0;
                    // Weekday in IST: derive from formatted parts
                    const istWd = (d) => {
                      const wdStr = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata', weekday: 'short' }).format(d);
                      return ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].indexOf(wdStr);
                    };
                    while ((istWd(target) !== firstDay || target <= nowUtc) && tries < 10) {
                      target = new Date(target.getTime() + 24 * 3600 * 1000);
                      tries++;
                    }
                    const openAt = new Date(target.getTime() - (sessionDefaults.rsvp_open_hours_before || 12) * 3600 * 1000);
                    const closeAt = new Date(target.getTime() - (sessionDefaults.rsvp_close_hours_before || 1) * 3600 * 1000);
                    const fmtIST = (d) => {
                      const parts = new Intl.DateTimeFormat('en-US', {
                        timeZone: 'Asia/Kolkata', weekday: 'short',
                        hour: '2-digit', minute: '2-digit', hour12: false,
                      }).formatToParts(d).reduce((acc, p) => (acc[p.type] = p.value, acc), {});
                      return `${parts.weekday} ${parts.hour}:${parts.minute}`;
                    };
                    return (
                      <div className="p-3 rounded-xl bg-primary/5 border border-primary/20" data-testid="defaults-preview">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Next session preview</p>
                        <p className="text-sm">
                          <b>{fmtIST(target)}</b> IST start
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          RSVP opens <b>{fmtIST(openAt)}</b> → closes <b>{fmtIST(closeAt)}</b>
                        </p>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Live indicator style + session duration */}
            <div className="p-3 border-2 rounded-xl space-y-3" data-testid="live-style-section">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide">Session duration</Label>
                  <Input
                    type="number"
                    min="15"
                    step="15"
                    value={sessionDefaults.duration_minutes}
                    onChange={(e) => setSessionDefaults({ ...sessionDefaults, duration_minutes: parseInt(e.target.value) || 120 })}
                    data-testid="defaults-duration-input"
                    className="h-11 border-2 rounded-xl"
                    placeholder="120"
                  />
                  <p className="text-xs text-muted-foreground">Minutes. Used to show "LIVE NOW" highlight while a session is in progress.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide">Live highlight style</Label>
                  <div className="space-y-1">
                    {[
                      { id: 'red_pulse', label: '🔴 Red pulsing border (loud)' },
                      { id: 'green_glow', label: '🟢 Green glow (sporty)' },
                      { id: 'gradient_banner', label: '🌈 Gradient banner (premium)' },
                      { id: 'minimal', label: '⚪ Minimal — badge only' },
                    ].map(({ id, label }) => {
                      const selected = sessionDefaults.live_indicator_style === id;
                      return (
                        <button
                          type="button"
                          key={id}
                          onClick={() => setSessionDefaults({ ...sessionDefaults, live_indicator_style: id })}
                          data-testid={`defaults-live-style-${id}`}
                          className={`w-full text-left px-3 py-2 rounded-lg border-2 text-xs font-medium transition-colors ${selected ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" disabled={savingSessionDefaults} data-testid="save-session-defaults-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {savingSessionDefaults ? 'Saving…' : 'Save Defaults'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Session Modal */}
      <Dialog open={!!duplicateSource} onOpenChange={(o) => { if (!o) { setDuplicateSource(null); setDuplicateDate(''); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="duplicate-session-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Copy className="w-5 h-5 text-primary" />
              Duplicate session
            </DialogTitle>
          </DialogHeader>
          {duplicateSource && (
            <form onSubmit={handleDuplicateSession} className="space-y-4 mt-2">
              <div className="p-3 rounded-xl bg-muted/50 border">
                <p className="font-semibold text-sm">{duplicateSource.title}</p>
                <p className="text-xs text-muted-foreground">
                  {duplicateSource.start_time || '07:30'} · {duplicateSource.num_courts || 1} court{(duplicateSource.num_courts || 1) === 1 ? '' : 's'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">New date</Label>
                <Input
                  type="date"
                  value={duplicateDate}
                  onChange={(e) => setDuplicateDate(e.target.value)}
                  required
                  data-testid="duplicate-session-date-input"
                  className="h-12 border-2 rounded-xl"
                />
              </div>
              <Button type="submit" disabled={submitting || !duplicateDate} data-testid="confirm-duplicate-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
                {submitting ? 'Cloning…' : 'Clone session'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Session Modal (iter43) */}
      <Dialog open={!!editSession} onOpenChange={(o) => !o && setEditSession(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-session-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit session
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Changes notify all RSVP'd members. Reducing capacity moves last-in to waitlist.
            </DialogDescription>
          </DialogHeader>
          {editSession && (
            <form onSubmit={handleSaveEditSession} className="space-y-3 mt-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Title</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  data-testid="edit-session-title-input"
                  className="h-11 rounded-xl border-2 mt-1"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Date</Label>
                  <Input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    data-testid="edit-session-date-input"
                    className="h-11 rounded-xl border-2 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Start time</Label>
                  <Input
                    type="time"
                    value={editForm.start_time}
                    onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                    data-testid="edit-session-start-time-input"
                    className="h-11 rounded-xl border-2 mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Courts</Label>
                <div className="flex gap-2 mt-1">
                  {[1, 2, 3, 4].map((n) => {
                    // iter223 — sport-aware default sizing.
                    const sportMeta = (sportsCatalog.find((s) => s.key === group?.sport)) ||
                      { min_per_court: 5, max_per_court: 6, unit: 'court' };
                    return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setEditForm({
                        ...editForm,
                        num_courts: n,
                        max_players: n * sportMeta.max_per_court,
                        min_players: n * sportMeta.min_per_court,
                      })}
                      data-testid={`edit-session-courts-${n}`}
                      className={`flex-1 h-11 rounded-xl border-2 font-bold transition ${
                        editForm.num_courts === n ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-muted hover:border-primary'
                      }`}
                    >
                      {n}
                    </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {(() => {
                    const s = sportsCatalog.find((x) => x.key === group?.sport);
                    if (s) return `${s.emoji} ${s.label} — auto-sizes to ${s.min_per_court}–${s.max_per_court} per ${s.unit}. Override below if needed.`;
                    return 'Tapping auto-sizes min/max (5–6 per court). Override below if needed.';
                  })()}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Min players</Label>
                  <Input
                    type="number" min="1"
                    value={editForm.min_players}
                    onChange={(e) => setEditForm({ ...editForm, min_players: parseInt(e.target.value) || 1 })}
                    data-testid="edit-session-min-input"
                    className="h-11 rounded-xl border-2 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Max players</Label>
                  <Input
                    type="number" min="1"
                    value={editForm.max_players}
                    onChange={(e) => setEditForm({ ...editForm, max_players: parseInt(e.target.value) || 1 })}
                    data-testid="edit-session-max-input"
                    className="h-11 rounded-xl border-2 mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Fee per player (₹)</Label>
                <Input
                  type="number" min="0"
                  value={editForm.session_fee}
                  placeholder={`${Math.round(parseFloat(fineDefaults.session_fee) || 0)} (group default)`}
                  onChange={(e) => setEditForm({ ...editForm, session_fee: e.target.value })}
                  data-testid="edit-session-fee-input"
                  className="h-11 rounded-xl border-2 mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">Blank = group default. Applies when you charge fees for this session.</p>
              </div>

              {/* iter311 — explicit RSVP open/close date & time */}
              <div className="pt-3 border-t border-border">
                <Label className="text-xs font-bold uppercase tracking-wider">RSVP window</Label>
                <div className="grid grid-cols-1 gap-2 mt-1.5">
                  <div>
                    <span className="text-xs font-semibold text-muted-foreground">Opens at</span>
                    <Input
                      type="datetime-local"
                      value={editForm.rsvp_opens_at || ''}
                      onChange={(e) => setEditForm({ ...editForm, rsvp_opens_at: e.target.value })}
                      data-testid="edit-session-rsvp-opens-input"
                      className="h-11 rounded-xl border-2 mt-1"
                    />
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-muted-foreground">Closes at</span>
                    <Input
                      type="datetime-local"
                      value={editForm.rsvp_closes_at || ''}
                      onChange={(e) => setEditForm({ ...editForm, rsvp_closes_at: e.target.value })}
                      data-testid="edit-session-rsvp-closes-input"
                      className="h-11 rounded-xl border-2 mt-1"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {[
                    { label: 'Open now', mins: 0, field: 'rsvp_opens_at' },
                    { label: 'Close 1h before start', mins: 60, field: 'rsvp_closes_at' },
                    { label: 'Close 12h before start', mins: 720, field: 'rsvp_closes_at' },
                  ].map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      data-testid={`edit-session-rsvp-preset-${p.field === 'rsvp_opens_at' ? 'open-now' : `close-${p.mins}`}`}
                      onClick={() => {
                        if (p.field === 'rsvp_opens_at') {
                          setEditForm({ ...editForm, rsvp_opens_at: isoToLocalInput(new Date().toISOString()) });
                          return;
                        }
                        const [hh, mm] = String(editForm.start_time || '07:30').split(':').map((x) => parseInt(x, 10) || 0);
                        const [yy, mo, dd] = String(editForm.date || '').split('-').map((x) => parseInt(x, 10) || 0);
                        if (!yy) return;
                        const target = new Date(yy, mo - 1, dd, hh, mm - p.mins);
                        setEditForm({ ...editForm, rsvp_closes_at: isoToLocalInput(target.toISOString()) });
                      }}
                      className="px-2.5 h-7 rounded-full border-2 border-border text-xs font-semibold hover:border-primary hover:text-primary transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Everyone invited is notified on WhatsApp when you move these.
                </p>
              </div>
              {editSession.confirmed_ids?.length > 0 && editForm.max_players < editSession.confirmed_ids.length && (
                <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 text-xs">
                  ⚠️ Currently {editSession.confirmed_ids.length} confirmed. Reducing max to {editForm.max_players} will move {editSession.confirmed_ids.length - editForm.max_players} member(s) to waitlist (they'll be notified).
                </div>
              )}

              {/* iter175 — Session notes + venue/court info (admin/co-admin only). */}
              <div className="pt-3 border-t border-border space-y-3">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Court / venue info</Label>
                  <Input
                    value={editForm.court_info || ''}
                    onChange={(e) => setEditForm({ ...editForm, court_info: e.target.value })}
                    data-testid="edit-session-court-input"
                    maxLength={200}
                    placeholder='e.g. "Court 3, Phoenix Marketcity, 2nd floor"'
                    className="h-11 rounded-xl border-2 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Location URL (Google Maps)</Label>
                  <Input
                    type="url"
                    value={editForm.location_url || ''}
                    onChange={(e) => setEditForm({ ...editForm, location_url: e.target.value })}
                    data-testid="edit-session-location-url-input"
                    placeholder="https://maps.google.com/?q=..."
                    className="h-11 rounded-xl border-2 mt-1 text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Notes</Label>
                  <textarea
                    value={editForm.notes || ''}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    data-testid="edit-session-notes-input"
                    maxLength={1000}
                    rows={3}
                    placeholder="Bring own paddle. Light snacks after."
                    className="w-full px-3 py-2 rounded-xl border-2 mt-1 text-sm bg-background resize-y"
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {(editForm.notes || '').length}/1000
                  </p>
                </div>
              </div>

              <Button type="submit" disabled={editBusy} data-testid="save-edit-session-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
                {editBusy ? 'Saving…' : 'Save changes'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Attendance Modal */}
      <Dialog open={!!activeSession} onOpenChange={(open) => { if (!open) setActiveSession(null); }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="attendance-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-primary" />
              Mark Attendance
            </DialogTitle>
          </DialogHeader>
          {activeSession && (
            <div className="space-y-4 mt-4">
              <div className="p-3 border-2 rounded-xl bg-muted/50">
                <p className="font-semibold">{activeSession.title}</p>
                <p className="text-sm text-muted-foreground">{new Date(activeSession.date).toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>

              <p className="text-sm text-muted-foreground">
                Tap a status for each member. Late arrivals will be auto-fined when you save.
              </p>

              {(() => {
                const fineDef = group?.fine_defaults || fineDefaults || {};
                const threshold = parseInt(fineDef.late_threshold_minutes ?? 10) || 10;
                const late10Amt = Math.round(fineDef.late_10 ?? 100);
                const lateAboveAmt = Math.round(fineDef.late_above_10 ?? 200);
                const lateCancelAmt = Math.round(fineDef.late_cancel ?? 300);
                // For RSVP sessions, only committed members (confirmed + guests) are fineable/chargeable
                const rsvpPool = activeSession.confirmed_ids || [];
                const guests = activeSession.external_guests || [];
                const memberMap = Object.fromEntries((group?.members || []).map(m => [m.id, m]));
                const attendees = rsvpPool.length > 0
                  ? [
                      ...rsvpPool.map(id => memberMap[id]).filter(Boolean),
                      ...guests.map(g => ({ id: g.id, name: g.name + ' (guest)', is_guest: true }))
                    ]
                  : (group?.members || []);
                return (
                  <>
                    {rsvpPool.length > 0 && (
                      <p className="text-xs px-3 py-2 rounded-lg bg-blue-500/10 text-blue-700" data-testid="attendance-rsvp-scope">
                        Showing {attendees.length} confirmed attendees only. Non-committed members won't be charged or fined.
                      </p>
                    )}
                    <div className="space-y-3">
                      {attendees.map((member) => {
                        const state = getAttendanceState(member.id);
                        const isGuest = !!member.is_guest;
                        const guestDisabled = (s) => isGuest && (s === 'late_under' || s === 'late_above' || s === 'late_cancel');
                        const stateBtn = (s, label, color) => (
                          <button
                            type="button"
                            onClick={() => !guestDisabled(s) ? setAttendanceState(member.id, s) : null}
                            disabled={guestDisabled(s)}
                            data-testid={`attend-${s}-${member.id}`}
                            className={`flex-1 px-1.5 py-1.5 rounded-md text-xs font-bold uppercase tracking-tight border-2 transition ${
                              state === s ? color : 'border-transparent text-muted-foreground hover:border-muted'
                            } ${guestDisabled(s) ? 'opacity-30 cursor-not-allowed' : ''}`}
                          >
                            {label}
                          </button>
                        );
                        return (
                          <div
                            key={member.id}
                            data-testid={`attendance-row-${member.id}`}
                            className="border-2 rounded-xl p-2.5"
                          >
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <span className="font-medium text-sm truncate">{member.name}</span>
                              <span className={`text-xs font-bold uppercase ${
                                state === 'ontime' ? 'text-green-600' :
                                state === 'late_under' ? 'text-amber-600' :
                                state === 'late_above' ? 'text-orange-600' :
                                state === 'late_cancel' ? 'text-purple-600' : 'text-red-500'
                              }`}>
                                {state === 'ontime' ? 'On time'
                                  : state === 'late_under' ? `Late ≤${threshold}m`
                                  : state === 'late_above' ? `Late >${threshold}m`
                                  : state === 'late_cancel' ? `Late cancel ₹${lateCancelAmt}`
                                  : 'Absent'}
                              </span>
                            </div>
                            <div className="space-y-1.5">
                              {/* Row 1 — played states */}
                              <div className="grid grid-cols-3 gap-1.5">
                                {stateBtn('ontime', 'On time', 'border-green-500 bg-green-500/10 text-green-700')}
                                {stateBtn('late_under', `≤${threshold}m ₹${late10Amt}`, 'border-amber-500 bg-amber-500/10 text-amber-700')}
                                {stateBtn('late_above', `>${threshold}m ₹${lateAboveAmt}`, 'border-orange-500 bg-orange-500/10 text-orange-700')}
                              </div>
                              {/* Row 2 — didn't-play states */}
                              <div className="grid grid-cols-2 gap-1.5">
                                {stateBtn('late_cancel', `Late cancel ₹${lateCancelAmt}`, 'border-purple-500 bg-purple-500/10 text-purple-700')}
                                {stateBtn('absent', `Absent (₹500 if fined)`, 'border-red-500 bg-red-500/10 text-red-600')}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* iter325 — someone dropped out: bring in a replacement right here */}
                    {(() => {
                      if (!attendanceTouched) return null;
                      const droppedCount = attendees.filter((m) => {
                        const st = getAttendanceState(m.id);
                        return st === 'absent' || st === 'late_cancel';
                      }).length;
                      if (!droppedCount) return null;
                      const inSession = new Set(attendees.map((m) => m.id));
                      const bench = (group?.members || []).filter((m) => !inSession.has(m.id));
                      return (
                        <div className="p-3 rounded-xl border-2 border-dashed space-y-2.5" data-testid="replacement-block">
                          <p className="text-xs font-bold">
                            {droppedCount} player{droppedCount > 1 ? 's' : ''} out — bring someone in?
                          </p>
                          {bench.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {bench.map((m) => (
                                <button
                                  key={m.id}
                                  type="button"
                                  disabled={replacementBusy === m.name}
                                  onClick={() => addReplacement({ user_id: m.id }, m.name)}
                                  data-testid={`replacement-member-${m.id}`}
                                  className="h-8 px-3 rounded-full border text-xs font-semibold hover:bg-muted disabled:opacity-50"
                                >
                                  + {m.name?.split(' ')[0]}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              value={replacementGuest}
                              onChange={(e) => setReplacementGuest(e.target.value)}
                              placeholder="Guest name or mobile"
                              data-testid="replacement-guest-input"
                              className="flex-1 h-9 px-3 rounded-xl border-2 text-xs bg-background"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full h-9 text-xs shrink-0"
                              disabled={!replacementGuest.trim() || replacementBusy === 'guest'}
                              data-testid="replacement-add-guest-btn"
                              onClick={() => {
                                const v = replacementGuest.trim();
                                const isPhone = /^[0-9+\s-]{6,}$/.test(v);
                                addReplacement(isPhone ? { phone: v } : { name: v }, 'guest');
                              }}
                            >
                              Add guest
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Members join as players; a guest pays the guest fee in cash. Tap their status above once they&apos;re in.
                          </p>
                        </div>
                      );
                    })()}

                    <div className="flex gap-3 pt-2">
                      <Button variant="outline" onClick={() => { setPresentIds(attendees.map(m => m.id)); setLateUnderIds([]); setLateAboveIds([]); setLateCancelIds([]); }} className="flex-1 rounded-full" data-testid="mark-all-present-btn">
                        All On Time
                      </Button>
                      <Button variant="outline" onClick={() => { setAttendanceTouched(true); setPresentIds([]); setLateUnderIds([]); setLateAboveIds([]); setLateCancelIds([]); }} className="flex-1 rounded-full" data-testid="mark-all-absent-btn">
                        All Absent
                      </Button>
                    </div>

                    {(lateUnderIds.length + lateAboveIds.length + lateCancelIds.length) > 0 && (
                      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-800 text-xs" data-testid="attendance-late-preview">
                        {lateUnderIds.length > 0 && <>{lateUnderIds.length} member(s) will be fined <b>₹{late10Amt}</b> each (Late ≤{threshold}m).<br/></>}
                        {lateAboveIds.length > 0 && <>{lateAboveIds.length} member(s) will be fined <b>₹{lateAboveAmt}</b> each (Late &gt;{threshold}m).<br/></>}
                        {lateCancelIds.length > 0 && <>{lateCancelIds.length} member(s) will be fined <b>₹{lateCancelAmt}</b> each (Late cancel — won&apos;t also get no-show ₹500).<br/></>}
                        Total: <b>₹{lateUnderIds.length * late10Amt + lateAboveIds.length * lateAboveAmt + lateCancelIds.length * lateCancelAmt}</b>
                      </div>
                    )}

                    <Button onClick={handleSaveAttendance} disabled={submitting} data-testid="save-attendance-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
                      {submitting ? 'Saving...' : `Save (${presentIds.length}/${attendees.length} Present, ${lateUnderIds.length + lateAboveIds.length} Late, ${lateCancelIds.length} Cxl)`}
                    </Button>
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* iter209 — Promote waitlist person dialog */}
      <Dialog open={!!promotePrompt} onOpenChange={(open) => { if (!open) setPromotePrompt(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="promote-waitlist-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-500" />
              Promote from waitlist?
            </DialogTitle>
          </DialogHeader>
          {promotePrompt && (
            <div className="space-y-4 mt-2">
              <div className="rounded-xl border-2 p-3 bg-amber-500/5 text-sm leading-snug">
                <p>
                  You marked <span className="font-semibold">{promotePrompt.absentee_name}</span> as absent.
                </p>
                <p className="mt-1.5">
                  Promote <span className="font-semibold text-emerald-700 dark:text-emerald-400">{promotePrompt.waitlist_user_name}</span> from the waitlist to fill the slot?
                </p>
                <p className="mt-2 text-xs text-muted-foreground">
                  They&apos;ll appear in this Mark Attendance modal — you can then tap their actual status (On time / Late / Absent). WhatsApp goes out only if &gt; 30 min before kickoff.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPromotePrompt(null)}
                  disabled={promoting}
                  className="flex-1 rounded-full"
                  data-testid="promote-waitlist-skip-btn"
                >
                  Skip — play with {(activeSession?.confirmed_ids?.length || 0) - 1}
                </Button>
                <Button
                  onClick={handlePromoteWaitlist}
                  disabled={promoting}
                  className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="promote-waitlist-confirm-btn"
                >
                  {promoting ? 'Promoting…' : 'Promote'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* iter209 — Demote promoted waitlist person dialog */}
      <Dialog open={!!demotePrompt} onOpenChange={(open) => { if (!open) setDemotePrompt(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="demote-promoted-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-amber-500" />
              Demote replacement back?
            </DialogTitle>
          </DialogHeader>
          {demotePrompt && (
            <div className="space-y-4 mt-2">
              <div className="rounded-xl border-2 p-3 bg-amber-500/5 text-sm leading-snug">
                <p>
                  <span className="font-semibold">{demotePrompt.absentee_name}</span> is back to present.
                </p>
                <p className="mt-1.5">
                  Want to send <span className="font-semibold">{demotePrompt.promoted_user_name}</span> back to the waitlist? Or keep them in (session will play with more than capacity)?
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDemotePrompt(null)}
                  disabled={promoting}
                  className="flex-1 rounded-full"
                  data-testid="demote-promoted-keep-btn"
                >
                  Keep both in
                </Button>
                <Button
                  onClick={handleDemotePromoted}
                  disabled={promoting}
                  className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700"
                  data-testid="demote-promoted-confirm-btn"
                >
                  {promoting ? 'Demoting…' : 'Demote'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Settle Modal */}
      <Dialog open={showQuickSettle} onOpenChange={setShowQuickSettle}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="quick-settle-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" /> Quick Settle
            </DialogTitle>
          </DialogHeader>
          {loadingQuickSettle ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Calculating optimal transfers…</div>
          ) : quickSettle ? (
            <div className="space-y-4">
              {/* Totals */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border bg-red-500/5 p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Debt</div>
                  <div className="text-xl font-bold text-red-500">−{formatCurrency(quickSettle.totals.total_debt)}</div>
                  <div className="text-xs text-muted-foreground">{quickSettle.totals.num_negatives} members</div>
                </div>
                <div className="rounded-xl border bg-green-500/5 p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Credit</div>
                  <div className="text-xl font-bold text-green-600 dark:text-green-400">+{formatCurrency(quickSettle.totals.total_credit)}</div>
                  <div className="text-xs text-muted-foreground">{quickSettle.totals.num_positives} members</div>
                </div>
              </div>

              {/* Description */}
              <p className="text-xs text-muted-foreground">
                Suggested transfers to zero out negative balances. Each transfer still needs recipient approval after the sender initiates it — click <span className="font-semibold">Nudge Sender</span> to notify them.
              </p>

              {/* Suggestions */}
              {quickSettle.suggestions.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  <Check className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  All balances are zero-or-positive. Nothing to settle!
                </div>
              ) : (
                <div className="space-y-2" data-testid="quick-settle-suggestions">
                  {quickSettle.suggestions.map((s, idx) => (
                    <div key={idx} className="rounded-xl border-2 p-3 space-y-2" data-testid={`settle-pair-${idx}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap text-sm">
                          <span className="font-semibold">{s.from_name}</span>
                          <span className="text-xs text-green-600 dark:text-green-400 font-mono">(+{s.from_current})</span>
                          <ArrowUpRight className="w-4 h-4 text-primary" />
                          <span className="font-semibold">{s.to_name}</span>
                          <span className="text-xs text-red-500 font-mono">({s.to_current})</span>
                        </div>
                        <div className="text-lg font-bold">{formatNXC(s.amount, group?.currency)}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleQuickSettleNudge(s.from_user_id, s.to_user_id, s.amount)}
                          className="rounded-full text-xs flex-1 h-8"
                          data-testid={`nudge-sender-${idx}`}
                        >
                          <Send className="w-3 h-3 mr-1" /> Nudge {s.from_name} to transfer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Money Transfer Modal — Send / Request Peer / Request from Group Fund */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-md" data-testid="transfer-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              {transferMode === 'send' ? (
                <><ArrowUpRight className="w-5 h-5 text-primary" /> Send Money</>
              ) : (
                <><ArrowDownLeft className="w-5 h-5 text-primary" /> Request Money</>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-muted mt-3">
            <button
              type="button"
              onClick={() => { setTransferMode('send'); setTransferData({ ...transferData, to_user_id: '' }); }}
              data-testid="mode-send-btn"
              className={`h-9 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                transferMode === 'send' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground'
              }`}
            >
              Send
            </button>
            <button
              type="button"
              onClick={() => { setTransferMode((m) => (m === 'send' ? 'request_peer' : m)); setTransferData({ ...transferData, to_user_id: '' }); }}
              data-testid="mode-request-btn"
              className={`h-9 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                transferMode !== 'send' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground'
              }`}
            >
              Request
            </button>
          </div>

          <form onSubmit={handleCreateTransfer} className="space-y-4 mt-4">
            <p className="text-xs text-muted-foreground">
              {transferMode === 'send' && 'money move from you → recipient once they approve. Group fund is not affected.'}
              {transferMode === 'request_peer' && 'A peer reimburses you. money move from them → you once they approve.'}
              {transferMode === 'request_fund' && 'Reimburse from the group fund. Any group admin can approve. Fund balance decreases.'}
            </p>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">
                {transferMode === 'send' ? 'To Member' : 'Request from'}
              </Label>
              <select
                value={transferData.to_user_id}
                onChange={(e) => {
                  const v = e.target.value;
                  setTransferData({ ...transferData, to_user_id: v });
                  // Track whether user picked the fund vs a peer when in request mode
                  if (transferMode !== 'send') {
                    setTransferMode(v === 'group_fund' ? 'request_fund' : 'request_peer');
                  }
                }}
                data-testid="transfer-recipient-select"
                className="w-full h-12 border-2 rounded-xl bg-background px-3 text-sm"
              >
                <option value="">{transferMode === 'send' ? 'Select recipient…' : 'Select who pays…'}</option>
                {transferMode !== 'send' && (
                  <option value="group_fund">💰 Group Fund (admin-approved)</option>
                )}
                <optgroup label="Members">
                  {group?.members?.filter((m) => m.id !== user?.id).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">money</Label>
              <Input
                type="number"
                value={transferData.amount}
                onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                placeholder="e.g. 500"
                data-testid="transfer-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Note (optional)</Label>
              <Input
                value={transferData.note}
                onChange={(e) => setTransferData({ ...transferData, note: e.target.value })}
                placeholder={transferMode === 'send' ? 'e.g. dinner settlement' : 'e.g. cones I bought for Tuesday session'}
                data-testid="transfer-note-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <Button type="submit" disabled={submitting} data-testid="submit-transfer-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {submitting ? 'Sending…' : (transferMode === 'send' ? 'Send Transfer Request' : 'Send Request')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Allot Money Modal — iter216: renamed to "Adjust ledger balance" for clarity */}
      <Dialog open={showAllotCoins} onOpenChange={setShowAllotCoins}>
        <DialogContent className="sm:max-w-md" data-testid="allot-coins-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <NXCIcon className="w-5 h-5 text-primary" />
              Adjust member balance
            </DialogTitle>
            <p className="text-xs text-muted-foreground -mt-1">
              Ledger fix only — the group fund is NOT touched. Use this for
              opening balances, cash-outside-app credits, or ledger corrections.
            </p>
          </DialogHeader>
          <form onSubmit={handleAllotCoins} className="space-y-4 mt-4">
            <div className="rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-3 text-xs leading-snug text-foreground/80">
              <p className="font-semibold text-foreground">When to use this:</p>
              <ul className="mt-1 ml-4 list-disc space-y-0.5 text-muted-foreground">
                <li>Member paid you cash outside the app → credit their ledger.</li>
                <li>Setting an opening balance for a new member.</li>
                <li>Correcting a ledger drift after a reversal.</li>
              </ul>
              <p className="mt-2 text-amber-700 dark:text-amber-300 font-semibold">
                If cash is being ADDED to the fund, use &quot;Add cash to fund&quot; instead.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Member</Label>
              <Select value={allotData.user_id} onValueChange={(value) => setAllotData({ ...allotData, user_id: value })}>
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="allot-member-select">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {group?.members?.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex items-center justify-between w-full gap-4">
                        <span>{member.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatNXC(fundData?.coin_balances?.[member.id] || 0, group?.currency)}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Amount</Label>
              <Input
                type="number"
                value={allotData.amount}
                onChange={(e) => setAllotData({ ...allotData, amount: e.target.value })}
                placeholder="e.g. 5000"
                data-testid="allot-amount-input"
                className="h-14 text-2xl font-bold border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Note (optional)</Label>
              <Input
                value={allotData.note}
                onChange={(e) => setAllotData({ ...allotData, note: e.target.value })}
                placeholder="e.g. Cash payment received"
                data-testid="allot-note-input"
                className="h-12 border-2 rounded-xl"
              />
            </div>
            <Button type="submit" disabled={submitting} data-testid="submit-allot-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {submitting ? 'Adjusting...' : 'Adjust balance'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fine Defaults Modal */}
      <Dialog open={showFineDefaultsModal} onOpenChange={setShowFineDefaultsModal}>
        <DialogContent className="sm:max-w-sm overflow-y-auto" data-testid="fine-defaults-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Fine Defaults
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveFineDefaults} className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">Set default fine amounts for quick one-tap fines.</p>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Late threshold (minutes)</Label>
              <Input
                type="number"
                min="1"
                value={fineDefaults.late_threshold_minutes ?? 10}
                onChange={(e) => setFineDefaults({ ...fineDefaults, late_threshold_minutes: parseInt(e.target.value) || 10 })}
                data-testid="default-late-threshold-input"
                className="h-12 text-lg font-bold border-2 rounded-xl"
              />
              <p className="text-xs text-muted-foreground">Cut-off for the two late fine tiers (e.g. 10, 30, 60).</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Late ≤{fineDefaults.late_threshold_minutes ?? 10} min</Label>
              <Input
                type="number"
                value={fineDefaults.late_10}
                onChange={(e) => setFineDefaults({ ...fineDefaults, late_10: parseFloat(e.target.value) || 0 })}
                data-testid="default-late10-input"
                className="h-12 text-lg font-bold border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Late &gt;{fineDefaults.late_threshold_minutes ?? 10} min</Label>
              <Input
                type="number"
                value={fineDefaults.late_above_10}
                onChange={(e) => setFineDefaults({ ...fineDefaults, late_above_10: parseFloat(e.target.value) || 0 })}
                data-testid="default-late-above10-input"
                className="h-12 text-lg font-bold border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Cancellation (after 4 PM)</Label>
              <Input
                type="number"
                value={fineDefaults.cancellation}
                onChange={(e) => setFineDefaults({ ...fineDefaults, cancellation: parseFloat(e.target.value) || 0 })}
                data-testid="default-cancel-input"
                className="h-12 text-lg font-bold border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">No Show</Label>
              <Input
                type="number"
                value={fineDefaults.no_show}
                onChange={(e) => setFineDefaults({ ...fineDefaults, no_show: parseFloat(e.target.value) || 0 })}
                data-testid="default-noshow-input"
                className="h-12 text-lg font-bold border-2 rounded-xl"
              />
            </div>
            <div className="space-y-2 pt-2 border-t border-border">
              <Label className="text-sm font-semibold uppercase tracking-wide">Session fee per attendee</Label>
              <Input
                type="number"
                min="0"
                value={fineDefaults.session_fee ?? 0}
                onChange={(e) => setFineDefaults({ ...fineDefaults, session_fee: parseFloat(e.target.value) || 0 })}
                data-testid="default-session-fee-input"
                className="h-12 text-lg font-bold border-2 rounded-xl"
              />
              <p className="text-xs text-muted-foreground">Charged to each present member when admin runs &ldquo;Charge attendees&rdquo; on a completed session. Set 0 to disable.</p>
            </div>
            <div className="space-y-2 pt-2 border-t border-border">
              <Label className="text-sm font-semibold uppercase tracking-wide">Late RSVP Cancel</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Cut-off (hours before session)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={fineDefaults.late_cancel_hours ?? 15}
                    onChange={(e) => setFineDefaults({ ...fineDefaults, late_cancel_hours: parseInt(e.target.value) || 0 })}
                    data-testid="default-late-cancel-hours-input"
                    className="h-11 text-base font-bold border-2 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs">Fine amount (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={fineDefaults.late_cancel ?? 300}
                    onChange={(e) => setFineDefaults({ ...fineDefaults, late_cancel: parseFloat(e.target.value) || 0 })}
                    data-testid="default-late-cancel-amount-input"
                    className="h-11 text-base font-bold border-2 rounded-xl"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">If a confirmed member drops their RSVP less than <b>{fineDefaults.late_cancel_hours ?? 15}h</b> before session start, they're charged <b>{formatNXC(fineDefaults.late_cancel ?? 300)}</b>. Set fine to 0 to disable late-cancel charges.</p>
            </div>
            <Button type="submit" data-testid="save-defaults-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              Save Defaults
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Balance Policy Modal */}
      <Dialog open={showBalancePolicyModal} onOpenChange={setShowBalancePolicyModal}>
        <DialogContent className="sm:max-w-sm overflow-y-auto" data-testid="balance-policy-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-primary" />
              Balance Limits
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBalancePolicy} className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">Controls how far <span className="font-semibold">members</span> and the <span className="font-semibold">Group Fund</span> can go into negative balance.</p>

            <div className="flex items-center justify-between p-3 border-2 rounded-xl">
              <div>
                <Label className="font-medium">Allow Negative Balance</Label>
                <p className="text-xs text-muted-foreground">If off, fines/splits/reimbursements that push below 0 are blocked</p>
              </div>
              <Switch
                checked={balancePolicy.allow_negative}
                onCheckedChange={(val) => setBalancePolicy({ ...balancePolicy, allow_negative: val })}
                data-testid="allow-negative-toggle"
              />
            </div>

            {balancePolicy.allow_negative && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Minimum Balance Limit</Label>
                <Input
                  type="number"
                  value={balancePolicy.min_balance ?? ''}
                  onChange={(e) => setBalancePolicy({ ...balancePolicy, min_balance: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="No limit (leave empty)"
                  data-testid="min-balance-input"
                  className="h-12 text-lg font-bold border-2 rounded-xl"
                />
                <p className="text-xs text-muted-foreground">E.g. -2000 means nobody can go below -2000</p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Warning Threshold</Label>
              <Input
                type="number"
                value={balancePolicy.warning_threshold ?? ''}
                onChange={(e) => setBalancePolicy({ ...balancePolicy, warning_threshold: e.target.value ? parseFloat(e.target.value) : null })}
                placeholder="-500"
                data-testid="warning-threshold-input"
                className="h-12 text-lg font-bold border-2 rounded-xl"
              />
              <p className="text-xs text-muted-foreground">Admin gets alerted when a member crosses this balance</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Alert Method</Label>
              <Select
                value={balancePolicy.alert_method}
                onValueChange={(val) => setBalancePolicy({ ...balancePolicy, alert_method: val })}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="alert-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_app">In-App Only</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp Only</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" data-testid="save-balance-policy-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              Save Policy
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Fines Preview Modal */}
      <Dialog open={!!importPreview} onOpenChange={(open) => { if (!open && importLoading !== 'confirm') setImportPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-fines-preview-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-wide flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" /> Review Fine Import
            </DialogTitle>
          </DialogHeader>

          {importPreview && (
            <div className="space-y-4">
              {/* Warning banner */}
              {importPreview.warning && (
                <div className="rounded-xl border-2 border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3" data-testid="import-warning-banner">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-bold text-red-600 dark:text-red-400 mb-1">Existing balances detected</div>
                    <div className="text-foreground/80">{importPreview.warning}</div>
                    <div className="text-xs text-muted-foreground mt-2">Tip: cancel this import, click <span className="font-semibold">Reset</span> to clear existing data, then re-import.</div>
                  </div>
                </div>
              )}

              {/* Policy violations banner */}
              {(importPreview.policy_violations?.length > 0 || importPreview.server_violations?.length > 0) && (
                <div className="rounded-xl border-2 border-orange-500/50 bg-orange-500/10 p-4" data-testid="policy-violations-banner">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                    <div className="text-sm flex-1">
                      <div className="font-bold text-orange-600 dark:text-orange-400 mb-1">Balance policy violations</div>
                      <div className="text-foreground/80 mb-2">
                        {importPreview.server_violation_msg || `${(importPreview.policy_violations || importPreview.server_violations).length} member(s) will be pushed below the group's balance limit.`}
                      </div>
                      <div className="space-y-1">
                        {(importPreview.server_violations || importPreview.policy_violations).map((v, idx) => (
                          <div key={idx} className="text-xs font-mono flex justify-between">
                            <span className="font-semibold">{v.name}</span>
                            <span className="text-orange-600 dark:text-orange-400">→ {v.projected} (limit: {v.limit})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI-detected mapping */}
              <div className="rounded-xl border bg-muted/30 p-4 space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">AI-Detected Format</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Name column:</span> <span className="font-semibold">Col {(importPreview.mapping?.name_column ?? 0) + 1}</span></div>
                  <div><span className="text-muted-foreground">Date columns:</span> <span className="font-semibold">{importPreview.mapping?.date_columns}</span></div>
                  <div className="col-span-2"><span className="text-muted-foreground">Total date columns:</span> <span className="font-semibold">{importPreview.mapping?.total_date_columns}</span></div>
                  {importPreview.mapping?.dates_found?.length > 0 && (
                    <div className="col-span-2"><span className="text-muted-foreground">Sample dates:</span> <span className="font-mono text-xs">{importPreview.mapping.dates_found.join(', ')}</span></div>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-3" data-testid="import-summary-total">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Fines</div>
                  <div className="text-2xl font-bold">{formatNXC(importPreview.summary?.total_fines || 0)}</div>
                  <div className="text-xs text-muted-foreground">{importPreview.summary?.total_entries} fine entries</div>
                </div>
                <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-3">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Members</div>
                  <div className="text-2xl font-bold">{importPreview.summary?.members_found}</div>
                  <div className="text-xs text-muted-foreground">{importPreview.summary?.members_not_found > 0 && <span className="text-red-500">{importPreview.summary.members_not_found} not found</span>}</div>
                </div>
              </div>

              {/* Fund before/after */}
              <div className="rounded-xl border bg-card p-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Group Fund</div>
                  <div className="font-mono">{formatCurrency(importPreview.fund_before)} <span className="text-muted-foreground">→</span> <span className="font-bold text-green-600 dark:text-green-400">{formatCurrency(importPreview.fund_after)}</span></div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-green-500" />
              </div>

              {/* Members preview list */}
              <div className="rounded-xl border overflow-hidden">
                <div className="bg-muted/50 px-3 py-2 text-xs uppercase tracking-wider font-semibold flex justify-between">
                  <span>Member</span>
                  <span>Fines → Projected Balance</span>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y" data-testid="import-members-list">
                  {importPreview.members?.length === 0 && (
                    <div className="p-4 text-sm text-muted-foreground text-center">No fines parsed.</div>
                  )}
                  {importPreview.members?.map((m, idx) => (
                    <div key={idx} className={`flex items-center justify-between px-3 py-2 text-sm ${!m.found ? 'bg-red-500/5' : ''}`}>
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {m.name}
                          {!m.found && <span className="text-xs uppercase text-red-500 font-bold">Not found</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">{m.fine_count} fines</div>
                      </div>
                      <div className="text-right font-mono text-xs">
                        <div className="text-red-500">−{formatCurrency(m.total_fines)}</div>
                        <div className="text-muted-foreground">{m.current_balance} → <span className={m.projected_balance < 0 ? 'text-red-500 font-bold' : 'text-foreground'}>{m.projected_balance}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setImportPreview(null)}
                  disabled={importLoading === 'confirm'}
                  className="flex-1 h-12 rounded-full"
                  data-testid="import-cancel-btn"
                >
                  Cancel
                </Button>
                {(importPreview.server_violations?.length > 0) ? (
                  <Button
                    onClick={() => confirmFineImport(true)}
                    disabled={importLoading === 'confirm'}
                    variant="destructive"
                    className="flex-1 h-12 rounded-full font-bold uppercase tracking-wider"
                    data-testid="import-force-btn"
                  >
                    {importLoading === 'confirm' ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
                    ) : (
                      <>Override & Import</>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => confirmFineImport(false)}
                    disabled={importLoading === 'confirm' || importPreview.summary?.total_entries === 0}
                    className="flex-1 h-12 rounded-full font-bold uppercase tracking-wider"
                    data-testid="import-confirm-btn"
                  >
                    {importLoading === 'confirm' ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
                    ) : (
                      <>Confirm Import</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Member Ledger Drawer — iter186: full statement view with Dr/Cr/Running Balance,
          Opening + Closing rows, "Charged on" pill for batched session fees. */}
      {/* iter222 — pair-wise ledger sheet */}
      <PairLedgerSheet
        open={!!pairLedgerTarget}
        onOpenChange={(o) => !o && setPairLedgerTarget(null)}
        groupId={groupId}
        otherUserId={pairLedgerTarget?.id}
        otherName={pairLedgerTarget?.name}
      />


      <Drawer open={!!ledgerForMember} onOpenChange={(o) => !o && setLedgerForMember(null)}>
        <DrawerContent data-testid="member-ledger-drawer">
          <DrawerHeader>
            <DrawerTitle className="font-display text-xl flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              {ledgerForMember?.name}'s Ledger
            </DrawerTitle>
            {ledgerData && (
              <DrawerDescription className="flex items-center justify-between flex-wrap gap-2 text-sm" data-testid="ledger-summary">
                <span>
                  Closing balance:{' '}
                  <span className={`font-bold ${ledgerData.current_balance < 0 ? 'text-red-500' : 'text-green-600'}`} data-testid="ledger-closing-balance">
                    {formatCoinAmount(ledgerData.current_balance)}
                  </span>
                </span>
                <span>
                  Opening:{' '}
                  <span className={`font-bold ${(ledgerData.opening_balance ?? 0) < 0 ? 'text-red-500' : 'text-green-600'}`} data-testid="ledger-opening-balance">
                    {formatCoinAmount(ledgerData.opening_balance ?? 0)}
                  </span>
                  {' · '}
                  {ledgerData.total_entries} entries
                </span>
              </DrawerDescription>
            )}
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[68vh] overflow-y-auto space-y-2">
            {ledgerForMember && (
              <div className="flex flex-wrap gap-2 mb-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadBlob(`/api/exports/member-ledger/${groupId}/${ledgerForMember.id}?format=excel&days=90`, `${ledgerForMember.name}_ledger.xlsx`)}
                  data-testid="download-ledger-excel-btn"
                  className="rounded-full text-xs flex-1"
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> Excel
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadBlob(`/api/exports/member-ledger/${groupId}/${ledgerForMember.id}?format=pdf&days=90`, `${ledgerForMember.name}_ledger.pdf`)}
                  data-testid="download-ledger-pdf-btn"
                  className="rounded-full text-xs flex-1"
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
                {/* iter232 — Cash Out Credit: only for my own ledger with positive credit, non-super-admin. */}
                {ledgerForMember.id === user?.id
                  && !user?.is_super_admin
                  && (ledgerData?.current_balance ?? 0) > 0 && (
                  <Button
                    size="sm"
                    onClick={() => setShowCashOutRequest(true)}
                    data-testid="open-cash-out-request-btn"
                    className="rounded-full text-xs flex-[2] bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    💵 Cash Out ₹{Math.floor(ledgerData.current_balance).toLocaleString('en-IN')}
                  </Button>
                )}
              </div>
            )}
            {ledgerLoading && (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
              </div>
            )}
            {/* iter207 — Pending guest fees inherited from silent-add days.
                These rows live OUTSIDE the running coin_balance math, so
                we surface them as a sticky banner above the timeline. */}
            {!ledgerLoading && ledgerData?.pending_guest_fees?.length > 0 && (
              <div
                className="rounded-2xl border-2 border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-3 mb-3"
                data-testid="pending-guest-fees-banner"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
                    <span className="inline-flex w-5 h-5 rounded-full bg-amber-500/25 text-amber-700 dark:text-amber-300 items-center justify-center text-xs font-bold">!</span>
                    Pending dues from past guest sessions
                  </p>
                  <span className="text-base font-bold text-amber-800 dark:text-amber-200 tabular-nums" data-testid="pending-guest-fees-total">
                    ₹{Math.round(ledgerData.pending_guest_fees_total || 0).toLocaleString('en-IN')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  These dues were carried over when this member was promoted from a guest. Settle via cash or app.
                </p>
                <div className="space-y-1">
                  {ledgerData.pending_guest_fees.map((p) => (
                    <div key={p.txn_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`pending-guest-fee-${p.txn_id}`}>
                      <span className="truncate text-foreground/80">
                        {p.session_title || 'Session'}
                        {p.session_date ? ` · ${p.session_date}` : ''}
                      </span>
                      <span className="font-semibold tabular-nums shrink-0">
                        ₹{Math.round(p.amount).toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!ledgerLoading && ledgerData?.entries?.length === 0 && (!ledgerData?.pending_guest_fees?.length) && (
              <p className="text-center text-muted-foreground py-8">No transactions yet for this member.</p>
            )}

            {/* Desktop / wide-screen table (md+) */}
            {!ledgerLoading && ledgerData?.entries?.length > 0 && (
              <div className="hidden md:block">
                <table className="w-full text-sm" data-testid="ledger-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                      <th className="py-2 pr-2 font-semibold">Date</th>
                      <th className="py-2 pr-2 font-semibold">Type</th>
                      <th className="py-2 pr-2 font-semibold">Description</th>
                      <th className="py-2 pr-2 font-semibold text-right">Dr</th>
                      <th className="py-2 pr-2 font-semibold text-right">Cr</th>
                      <th className="py-2 font-semibold text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerData.entries.map((e, i) => {
                      const isSession = e.kind === 'session_fee';
                      const chargedOn = e.date ? e.date.slice(0, 10) : null;
                      const sessionDate = e.session_date || null;
                      const showChargedPill = isSession && sessionDate && chargedOn && sessionDate !== chargedOn;
                      const displayDate = isSession && sessionDate ? sessionDate : (e.date ? e.date.slice(0, 10) : '');
                      return (
                        <tr key={`${e.ref_id}-${i}`} className="border-b last:border-0 hover:bg-muted/30" data-testid={`ledger-entry-${i}`}>
                          <td className="py-2 pr-2 whitespace-nowrap text-xs">{displayDate}</td>
                          <td className="py-2 pr-2 text-xs capitalize">{e.kind.replaceAll('_', ' ')}</td>
                          <td className="py-2 pr-2">
                            <div className="text-sm flex items-center gap-1.5">
                              <span>{e.note || e.kind}</span>
                              {e.explainer && (
                                <FineExplainerPopover explainer={e.explainer} testId={`explainer-${i}`} />
                              )}
                            </div>
                            {showChargedPill && (
                              <span className="inline-block mt-0.5 px-1.5 py-0.5 text-xs rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 font-medium">
                                Charged on {chargedOn}
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-2 text-right tabular-nums text-red-500 text-sm">
                            {e.dr ? Math.round(e.dr).toLocaleString('en-IN') : ''}
                          </td>
                          <td className="py-2 pr-2 text-right tabular-nums text-green-600 text-sm">
                            {e.cr ? Math.round(e.cr).toLocaleString('en-IN') : ''}
                          </td>
                          <td className={`py-2 text-right font-semibold tabular-nums text-sm ${(e.balance ?? 0) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                            {Math.round(e.balance ?? 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Synthetic Opening Balance row at the bottom (since newest-first) */}
                    <tr className="border-t-2 border-dashed border-muted-foreground/30 bg-muted/20 italic" data-testid="ledger-opening-row">
                      <td className="py-2 pr-2 text-xs text-muted-foreground">—</td>
                      <td className="py-2 pr-2 text-xs text-muted-foreground">Opening Balance</td>
                      <td className="py-2 pr-2 text-xs text-muted-foreground">Carried forward (before window)</td>
                      <td className="py-2 pr-2"></td>
                      <td className="py-2 pr-2"></td>
                      <td className={`py-2 text-right font-bold tabular-nums text-sm ${(ledgerData.opening_balance ?? 0) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                        {Math.round(ledgerData.opening_balance ?? 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile stacked rows */}
            {!ledgerLoading && ledgerData?.entries?.length > 0 && (
              <div className="md:hidden space-y-2">
                {ledgerData.entries.map((e, i) => {
                  const isSession = e.kind === 'session_fee';
                  const chargedOn = e.date ? e.date.slice(0, 10) : null;
                  const sessionDate = e.session_date || null;
                  const showChargedPill = isSession && sessionDate && chargedOn && sessionDate !== chargedOn;
                  const displayDate = isSession && sessionDate ? sessionDate : (e.date ? e.date.slice(0, 10) : '');
                  return (
                    <Card key={`${e.ref_id}-${i}`} className="p-3 border rounded-xl" data-testid={`ledger-entry-mobile-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold flex items-center gap-1.5">
                            <span className="truncate">{e.note || e.kind}</span>
                            {e.explainer && (
                              <FineExplainerPopover explainer={e.explainer} testId={`explainer-mobile-${i}`} />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            <span className="capitalize">{e.kind.replaceAll('_', ' ')}</span>
                            {displayDate ? ` · ${displayDate}` : ''}
                          </p>
                          {showChargedPill && (
                            <span className="inline-block mt-1 px-1.5 py-0.5 text-xs rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 font-medium">
                              Charged on {chargedOn}
                            </span>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`font-display font-bold whitespace-nowrap text-sm ${e.delta >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {e.delta >= 0 ? '+' : ''}{Math.round(e.delta)}
                          </div>
                          <div className={`text-xs font-semibold tabular-nums ${(e.balance ?? 0) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                            Bal: {Math.round(e.balance ?? 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
                {/* Opening Balance pseudo-row */}
                <Card className="p-3 border-2 border-dashed border-muted-foreground/30 bg-muted/20 rounded-xl italic" data-testid="ledger-opening-row-mobile">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-muted-foreground">Opening Balance</p>
                      <p className="text-xs text-muted-foreground">Carried forward (before window)</p>
                    </div>
                    <span className={`font-display font-bold ${(ledgerData.opening_balance ?? 0) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                      {Math.round(ledgerData.opening_balance ?? 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </Card>
              </div>
            )}
          </div>
          <DrawerClose asChild>
            <Button variant="outline" className="mx-4 mb-4 rounded-full" data-testid="close-ledger-btn">Close</Button>
          </DrawerClose>
        </DrawerContent>
      </Drawer>

      {/* Audit Logs Drawer */}
      <Drawer open={showAuditLogs} onOpenChange={setShowAuditLogs}>
        <DrawerContent data-testid="audit-logs-drawer">
          <DrawerHeader>
            <DrawerTitle className="font-display text-xl flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-primary" />
              Audit Log
            </DrawerTitle>
            <p className="text-sm text-muted-foreground">Last 100 admin actions in this group.</p>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[65vh] overflow-y-auto space-y-2">
            {auditLoading && (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}
              </div>
            )}
            {!auditLoading && auditLogs.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No actions logged yet.</p>
            )}
            {!auditLoading && auditLogs.map((log, i) => (
              <Card key={log.id} className="p-3 border rounded-xl" data-testid={`audit-log-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{log.actor_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{log.action.replaceAll('_', ' ')}</p>
                    <p className="text-sm mt-1">{log.details}</p>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {log.created_at ? new Date(log.created_at).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </div>
              </Card>
            ))}
          </div>
          <DrawerClose asChild>
            <Button variant="outline" className="mx-4 mb-4 rounded-full" data-testid="close-audit-btn">Close</Button>
          </DrawerClose>
        </DrawerContent>
      </Drawer>

      {/* AI Supervisor Observations Drawer (iter37) */}
      <Drawer open={showAiObsDrawer} onOpenChange={setShowAiObsDrawer}>
        <DrawerContent data-testid="ai-observations-drawer">
          <DrawerHeader>
            <DrawerTitle className="font-display text-xl flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              AI Supervisor Flags
            </DrawerTitle>
            <p className="text-sm text-muted-foreground">
              The AI watches for anomalies, duplicates, stale approvals, and risk patterns — never acts on its own.
            </p>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[65vh] overflow-y-auto space-y-2">
            <div className="flex justify-end gap-2 mb-2">
              <Button size="sm" variant="outline" onClick={handleRunAiScan} disabled={aiObsLoading} data-testid="run-ai-scan-btn" className="rounded-full text-xs">
                {aiObsLoading ? 'Scanning…' : '🔄 Rescan now'}
              </Button>
            </div>
            {aiObservations.length === 0 && (
              <p className="text-center text-muted-foreground py-8">All clear! No flags right now.</p>
            )}
            {aiObservations.filter(o => !o.dismissed).map((o) => {
              const colors = {
                high: 'border-red-500/40 bg-red-500/5',
                medium: 'border-amber-500/40 bg-amber-500/5',
                low: 'border-blue-500/30 bg-blue-500/5',
              };
              return (
                <Card key={o.id} className={`p-3 border-2 rounded-xl ${colors[o.severity] || ''}`} data-testid={`ai-obs-${o.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold uppercase ${
                          o.severity === 'high' ? 'bg-red-500 text-white' : o.severity === 'medium' ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'
                        }`}>
                          {o.severity}
                        </span>
                        <span className="text-xs font-semibold text-muted-foreground capitalize">
                          {o.kind.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm mt-1">{o.reason}</p>
                      {o.suggested_action && (
                        <p className="text-xs text-muted-foreground mt-1 italic">→ {o.suggested_action}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {o.created_at ? new Date(o.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const reason = window.prompt('Why dismiss? (optional, helps AI learn)') || '';
                        handleDismissObservation(o.id, reason);
                      }}
                      data-testid={`dismiss-obs-${o.id}`}
                      className="rounded-full text-xs ml-auto text-muted-foreground hover:text-foreground"
                    >
                      Dismiss
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
          <DrawerClose asChild>
            <Button variant="outline" className="mx-4 mb-4 rounded-full" data-testid="close-ai-obs-btn">Close</Button>
          </DrawerClose>
        </DrawerContent>
      </Drawer>

      {/* AI Co-Admin Actions Drawer (iter38) */}
      <Drawer open={showCoAdminDrawer} onOpenChange={(o) => { setShowCoAdminDrawer(o); if (o) fetchCoAdminActions(); }}>
        <DrawerContent data-testid="co-admin-actions-drawer">
          <DrawerHeader>
            <DrawerTitle className="font-display text-xl flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              AI Co-Admin Actions
            </DrawerTitle>
            <p className="text-sm text-muted-foreground">
              Every AI-taken action stays reversible for the veto window. After that, it becomes permanent.
            </p>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[65vh] overflow-y-auto space-y-2">
            {!coAdminSettings.enabled && (
              <div className="bg-muted/40 rounded-xl p-4 text-sm text-muted-foreground">
                AI Co-Admin is currently <strong>paused</strong>. Enable it from Group Settings to let it auto-handle low-stakes items.
              </div>
            )}
            {coAdminActions.length === 0 && (
              <p className="text-center text-muted-foreground py-8" data-testid="co-admin-empty">No AI actions yet.</p>
            )}
            {coAdminActions.map((a) => {
              const deadline = a.veto_deadline ? new Date(a.veto_deadline) : null;
              const now = new Date();
              const hoursLeft = deadline ? Math.max(0, Math.round((deadline - now) / 36e5 * 10) / 10) : 0;
              const state = a.vetoed ? 'vetoed' : (a.in_window ? 'active' : 'expired');
              const tint = {
                active: 'border-primary/40 bg-primary/5',
                vetoed: 'border-muted bg-muted/30 opacity-70',
                expired: 'border-muted/60 bg-muted/10 opacity-80',
              }[state];
              const label = {
                expense_auto_approved: 'Auto-approved expense',
                late_cancel_fine: 'Late-cancel fine',
              }[a.kind] || a.kind;

              return (
                <Card key={a.id} className={`p-3 border-2 rounded-xl ${tint}`} data-testid={`co-admin-action-${a.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-bold uppercase bg-primary text-primary-foreground">
                          {label}
                        </span>
                        {(a.meta && typeof a.meta.confidence === 'number') && (
                          <span
                            data-testid={`confidence-${a.id}`}
                            className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                              a.meta.confidence >= 80
                                ? 'bg-emerald-500/20 text-emerald-700'
                                : a.meta.confidence >= 60
                                ? 'bg-amber-500/20 text-amber-700'
                                : 'bg-red-500/20 text-red-700'
                            }`}
                            title={(() => {
                              const b = a.meta.confidence_basis || {};
                              const bits = [];
                              if (typeof b.similar_count === 'number') bits.push(`${b.similar_count} past ${b.category || 'expenses'}`);
                              if (typeof b.median_ratio === 'number') bits.push(`${b.median_ratio}× median`);
                              if (b.trust_tier && b.trust_tier !== 'normal') bits.push(`${b.trust_tier.replace('_', ' ')} payer`);
                              if (b.clean === false) bits.push('flagged');
                              return bits.join(' · ') || 'baseline confidence';
                            })()}
                          >
                            {a.meta.confidence}% sure
                          </span>
                        )}
                        {state === 'active' && (
                          <span className="text-xs font-semibold text-primary">~{hoursLeft}h left</span>
                        )}
                        {state === 'vetoed' && (
                          <span className="text-xs font-semibold text-muted-foreground uppercase">Vetoed</span>
                        )}
                        {state === 'expired' && (
                          <span className="text-xs font-semibold text-muted-foreground uppercase">Locked in</span>
                        )}
                      </div>
                      <p className="text-sm mt-1">{a.reason}</p>
                      {(a.meta && a.meta.confidence_basis && typeof a.meta.confidence_basis.similar_count === 'number' && a.meta.confidence_basis.similar_count > 0) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Similar to <span className="font-semibold">{a.meta.confidence_basis.similar_count}</span> past{' '}
                          {(a.meta.confidence_basis.category || 'expenses').replace(/_/g, ' ')} expenses
                          {a.meta.confidence_basis.median_ratio ? ` · ${a.meta.confidence_basis.median_ratio}× usual` : ''}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {a.created_at ? new Date(a.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                      {a.vetoed_reason && (
                        <p className="text-xs italic text-muted-foreground mt-1">Veto reason: {a.vetoed_reason}</p>
                      )}
                    </div>
                    {state === 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleVetoAction(a.id)}
                        disabled={vetoingIds.has(a.id)}
                        data-testid={`veto-action-${a.id}`}
                        className="rounded-full text-xs shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-40"
                      >
                        {vetoingIds.has(a.id) ? '…' : 'Veto'}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
          <DrawerClose asChild>
            <Button variant="outline" className="mx-4 mb-4 rounded-full" data-testid="close-co-admin-btn">Close</Button>
          </DrawerClose>
        </DrawerContent>
      </Drawer>

      {/* Trash Drawer (iter47) */}
      <Drawer open={showTrashDrawer} onOpenChange={(o) => { setShowTrashDrawer(o); if (o) fetchTrash(); }}>
        <DrawerContent data-testid="trash-drawer">
          <DrawerHeader>
            <DrawerTitle className="font-display text-xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-muted-foreground" />
              Trash
            </DrawerTitle>
            <p className="text-sm text-muted-foreground">
              Items here are auto-purged 30 days after deletion. Restore anytime within the window.
            </p>
          </DrawerHeader>
          <div className="px-4 pb-6 max-h-[65vh] overflow-y-auto space-y-2">
            {trashLoading && <p className="text-center text-muted-foreground py-6">Loading…</p>}
            {!trashLoading && trashItems.length === 0 && (
              <p className="text-center text-muted-foreground py-8" data-testid="trash-empty">Trash is empty.</p>
            )}
            {!trashLoading && trashItems.length > 0 && (
              <Button
                variant="outline"
                onClick={handleEmptyTrash}
                data-testid="empty-trash-btn"
                className="w-full rounded-full text-xs text-destructive border-destructive/30 hover:bg-destructive/10 mb-2"
              >
                Empty trash ({trashItems.length})
              </Button>
            )}
            {trashItems.map((item) => (
              <Card key={`${item.type}-${item.id}`} className="p-3 border-2 rounded-xl" data-testid={`trash-item-${item.id}`}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-bold uppercase bg-muted text-muted-foreground">
                        {item.type === 'session' ? 'Session' : 'Recurring'}
                      </span>
                      <span className={`text-xs font-semibold ${
                        item.days_until_purge <= 3 ? 'text-destructive' :
                        item.days_until_purge <= 7 ? 'text-amber-600' : 'text-muted-foreground'
                      }`}>
                        Purges in {item.days_until_purge}d
                      </span>
                    </div>
                    <p className="text-sm font-semibold mt-1 truncate">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.summary}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Deleted {item.deleted_at ? new Date(item.deleted_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleRestoreTrashItem(item)}
                      data-testid={`restore-${item.type}-${item.id}`}
                      className="rounded-full text-xs h-8 border-primary/40 text-primary hover:bg-primary/10"
                    >
                      Restore
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => handlePurgeTrashItem(item)}
                      data-testid={`purge-${item.type}-${item.id}`}
                      className="rounded-full text-xs h-7 text-destructive hover:bg-destructive/10"
                    >
                      Purge now
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <DrawerClose asChild>
            <Button variant="outline" className="mx-4 mb-4 rounded-full" data-testid="close-trash-btn">Close</Button>
          </DrawerClose>
        </DrawerContent>
      </Drawer>

      {/* Admin RSVP Member Picker Modal (iter41) */}
      <Dialog open={!!adminRsvpModal} onOpenChange={(o) => !o && setAdminRsvpModal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="admin-rsvp-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Add member to '{adminRsvpModal?.session_title}'
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Tap a member to RSVP them in on their behalf. They'll be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-1.5" data-testid="admin-rsvp-eligible-list">
            {(adminRsvpModal?.eligible || []).length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-6">All members are already RSVP'd.</p>
            )}
            {(adminRsvpModal?.eligible || []).map((m) => (
              <Button
                key={m.id}
                type="button"
                variant="outline"
                onClick={() => handleAdminRsvpAdd(m.id)}
                disabled={adminRsvpBusy}
                data-testid={`admin-rsvp-add-${m.id}`}
                className="w-full rounded-xl justify-start text-sm h-11"
              >
                <UserCheck className="w-4 h-4 mr-2 text-primary" /> {m.name}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setAdminRsvpModal(null)}
            data-testid="admin-rsvp-close-btn"
            className="w-full rounded-full mt-2"
          >
            Done
          </Button>
        </DialogContent>
      </Dialog>

      {/* Group Settings Modal (iter37) */}
      <Dialog open={showGroupSettings} onOpenChange={setShowGroupSettings}>
        <DialogContent
          className="sm:max-w-md overflow-y-auto p-4 sm:p-6"
          data-testid="group-settings-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Group Settings
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveGroupSettings} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Group Name</Label>
              <Input
                value={groupSettings.name}
                onChange={(e) => setGroupSettings({ ...groupSettings, name: e.target.value })}
                data-testid="group-name-input"
                className="h-12 border-2 rounded-xl font-semibold"
                required
                maxLength={80}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Description</Label>
              <textarea
                value={groupSettings.description}
                onChange={(e) => setGroupSettings({ ...groupSettings, description: e.target.value })}
                data-testid="group-description-input"
                className="w-full min-h-[80px] p-3 border-2 rounded-xl font-body resize-none"
                placeholder="What's this group for?"
                maxLength={300}
              />
              <p className="text-xs text-muted-foreground text-right">{groupSettings.description.length}/300</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Currency</Label>
              <select
                value={groupSettings.currency}
                onChange={(e) => setGroupSettings({ ...groupSettings, currency: e.target.value })}
                data-testid="group-currency-select"
                className="w-full h-12 px-3 border-2 rounded-xl font-semibold bg-background"
              >
                {(currencies.length ? currencies : [{ code: 'INR', symbol: '₹' }, { code: 'USD', symbol: '$' }]).map(c => (
                  <option key={c.code} value={c.code}>{`${c.symbol} ${c.code}`}</option>
                ))}
              </select>
            </div>

            {/* iter233 — group's own coin name */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Coin Name</Label>
              <Input
                value={groupSettings.currency_name}
                onChange={(e) => setGroupSettings({ ...groupSettings, currency_name: e.target.value })}
                data-testid="group-coin-name-input"
                className="h-12 border-2 rounded-xl font-semibold"
                placeholder="e.g. Pickle Points"
                maxLength={24}
              />
              <p className="text-xs text-muted-foreground">
                Give your group's currency a fun name — "Pickle Points", "TripBucks"…
              </p>
            </div>

            {/* iter245 — group emoji icon */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Group Emoji</Label>
              <GroupEmojiPicker
                value={groupSettings.emoji}
                onChange={(e) => setGroupSettings({ ...groupSettings, emoji: e })}
              />
              <p className="text-xs text-muted-foreground">
                Shows next to the group name everywhere — makes lists instantly scannable.
              </p>
            </div>

            {/* Group logo — header, dashboard, PDFs, Play cards */}
            <GroupLogoUploader group={group} onChanged={fetchGroup} />

            {/* iter233 — per-group cash holder */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Cash Holder</Label>
              <select
                value={groupSettings.treasurer_id}
                onChange={(e) => setGroupSettings({ ...groupSettings, treasurer_id: e.target.value })}
                data-testid="group-treasurer-select"
                className="w-full h-12 px-3 border-2 rounded-xl font-semibold bg-background"
              >
                <option value="">— Super Admin (default) —</option>
                {(group?.members || []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Who physically keeps this group's cash box. Changing this records a handover in the fund ledger.
              </p>
            </div>

            {/* iter240 — scheduled ledger PDFs on WhatsApp */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Auto Ledger PDF (WhatsApp)</Label>
              <div className="flex gap-2">
                <select
                  value={groupSettings.ledger_pdf_freq}
                  onChange={(e) => setGroupSettings({ ...groupSettings, ledger_pdf_freq: e.target.value })}
                  data-testid="group-ledger-pdf-freq"
                  className="flex-1 h-12 px-3 border-2 rounded-xl font-semibold bg-background"
                >
                  <option value="off">Off</option>
                  <option value="weekly">Weekly (on digest day)</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly (Jan/Apr/Jul/Oct)</option>
                </select>
                {(groupSettings.ledger_pdf_freq === 'monthly' || groupSettings.ledger_pdf_freq === 'quarterly') && (
                  <Input
                    type="number"
                    min={1}
                    max={28}
                    value={groupSettings.ledger_pdf_day}
                    onChange={(e) => setGroupSettings({ ...groupSettings, ledger_pdf_day: e.target.value })}
                    data-testid="group-ledger-pdf-day"
                    className="w-24 h-12 border-2 rounded-xl font-semibold text-center"
                    placeholder="Day"
                  />
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Each member automatically receives their own ledger PDF on WhatsApp — weekly (7 days), monthly (30) or quarterly (90). Pick the date of the month (1–28) for monthly/quarterly.
              </p>
            </div>

            {/* iter256 — external donation toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-xl border-2 p-3">
                <div className="pr-3">
                  <Label className="text-sm font-semibold uppercase tracking-wide">Allow external donations</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Shows the "External donation (fund-only)" option in fund top-ups — for outside money (sponsors, brands, non-members). Keep off if this group never has sponsors.
                  </p>
                </div>
                <Switch
                  checked={!!groupSettings.allow_external_donations}
                  onCheckedChange={(val) => setGroupSettings({ ...groupSettings, allow_external_donations: val })}
                  data-testid="group-allow-external-donations-toggle"
                />
              </div>
            </div>

            {/* iter223 — Sport picker (sports-type groups only) */}
            {group?.group_type === 'sports' && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wide">Sport</Label>
                <select
                  value={groupSettings.sport || ''}
                  onChange={(e) => setGroupSettings({ ...groupSettings, sport: e.target.value })}
                  data-testid="group-sport-select"
                  className="w-full h-12 px-3 border-2 rounded-xl font-semibold bg-background"
                >
                  <option value="">— Not set —</option>
                  {sportsCatalog.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.emoji}  {s.label}   ({s.min_per_court}–{s.max_per_court} per {s.unit})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Drives default players-per-court in the session editor. Admin can still override per session.
                </p>
              </div>
            )}

            {/* iter274 — per-group expense approval policy */}
            <div className="space-y-1.5" data-testid="approval-policy-setting">
              <Label className="text-sm font-semibold uppercase tracking-wide">Expense approval policy</Label>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Who approves multi-member peer-to-peer splits. Fund-touching expenses (reimburse / advance) always need an admin. 1-on-1 splits are always approved by the other person.
              </p>
              <Select
                value={groupSettings.expense_approval_policy || 'admin'}
                onValueChange={(v) => setGroupSettings((s) => ({ ...s, expense_approval_policy: v }))}
              >
                <SelectTrigger className="h-12 border-2 rounded-xl" data-testid="approval-policy-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="policy-admin">Admin approves (default)</SelectItem>
                  <SelectItem value="any_one" data-testid="policy-any-one">Any one split member approves</SelectItem>
                  <SelectItem value="majority" data-testid="policy-majority">Majority of split members approve</SelectItem>
                  <SelectItem value="all" data-testid="policy-all">All split members approve</SelectItem>
                </SelectContent>
              </Select>
              {groupSettings.expense_approval_policy && groupSettings.expense_approval_policy !== 'admin' && (
                <p className="text-xs text-sky-700 bg-sky-500/10 border border-sky-500/30 rounded-lg p-2">
                  If a vote gets stuck (someone inactive), an admin can always override-approve — it's logged in the audit trail.
                </p>
              )}
            </div>

            <Button type="submit" disabled={savingGroupSettings} data-testid="save-group-settings-btn" className="w-full h-12 rounded-full font-bold uppercase tracking-wider">
              {savingGroupSettings ? 'Saving…' : 'Save Changes'}
            </Button>

            {/* Customise Features (iter97) — admin override per group on top of type defaults */}
            {isAdmin && group?.features && group?.type_meta && (
              <details className="mt-3 group" data-testid="customise-features-details">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground py-2 list-none flex items-center justify-between">
                  <span>Customise features ({group.type_meta.label})</span>
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                </summary>
                <p className="text-xs text-muted-foreground mb-2 mt-1 leading-relaxed">
                  Hide or show features. Defaults are tailored for {group.type_meta.label} groups — you can override any of them here.
                </p>
                <div className="space-y-1.5">
                  {[
                    { key: 'sessions', label: 'Sessions & RSVP' },
                    { key: 'fines', label: 'Fines' },
                    { key: 'recurring', label: 'Recurring expenses' },
                    { key: 'loans', label: 'Loan tools' },
                    { key: 'currency_picker', label: 'Multi-currency picker' },
                    { key: 'trip_phase_banner', label: 'Trip phase banner' },
                    { key: 'event_budget_gauge', label: 'Event budget gauge' },
                    { key: 'monthly_view', label: 'Monthly view (Daily)' },
                    { key: 'match_day_mode', label: 'Match-day mode (Sports)' },
                  ].map(({ key, label }) => {
                    const enabled = group.features?.[key];
                    return (
                      <div key={key} className="flex items-center justify-between gap-3 py-1" data-testid={`feature-row-${key}`}>
                        <span className="text-xs flex-1 truncate">{label}</span>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            checked={!!enabled}
                            disabled={savingFeatures}
                            onChange={() => handleToggleFeature(key, enabled)}
                            data-testid={`feature-toggle-${key}`}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4"></div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </details>
            )}

            {/* iter169 — Trip-only: Party Composition + Per-Head split.
                 Admin-only. Renders inside Group Settings modal for Trip groups. */}
            {isAdmin && (group?.group_type === 'trip') && (group?.members?.length > 0) && (
              <details className="mt-6 pt-4 border-t-2 border-primary/20 group" open>
                <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-primary py-1 list-none flex items-center justify-between">
                  <span>Trip · Party Composition</span>
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                </summary>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Set how many adults & kids each member is bringing. When you pick <span className="font-semibold">Per Head</span> on an expense,
                  shares are weighted by these counts.
                </p>

                <div className="mt-3 space-y-2" data-testid="party-composition-list">
                  {(group?.members || []).map((m) => {
                    const draft = partyDraft[m.id] || { adults: 1, kids: 0 };
                    const heads = (parseInt(draft.adults, 10) || 0) + (parseInt(draft.kids, 10) || 0) * (parseFloat(kidWeightDraft) || 0);
                    return (
                      <div key={m.id} className="rounded-xl border border-border bg-card p-2.5" data-testid={`party-row-${m.id}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="flex-1 truncate text-sm font-semibold">{m.name}</span>
                          <span
                            className="text-xs tabular-nums font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5"
                            data-testid={`party-heads-${m.id}`}
                          >
                            {heads.toFixed(1)} hd
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Adults</Label>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              max="10"
                              value={draft.adults === 0 ? '' : draft.adults}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPartyDraft({ ...partyDraft, [m.id]: { ...draft, adults: v === '' ? 0 : Math.max(0, parseInt(v, 10) || 0) } });
                              }}
                              placeholder="0"
                              data-testid={`party-adults-${m.id}`}
                              className="h-10 text-center rounded-lg text-sm font-semibold mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Kids</Label>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              max="10"
                              value={draft.kids === 0 ? '' : draft.kids}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPartyDraft({ ...partyDraft, [m.id]: { ...draft, kids: v === '' ? 0 : Math.max(0, parseInt(v, 10) || 0) } });
                              }}
                              placeholder="0"
                              data-testid={`party-kids-${m.id}`}
                              className="h-10 text-center rounded-lg text-sm font-semibold mt-1"
                            />
                          </div>
                          {/* iter183 — Stay nights per member */}
                          <div>
                            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Nights</Label>
                            <Input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              max="365"
                              value={!draft.nights ? '' : draft.nights}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPartyDraft({ ...partyDraft, [m.id]: { ...draft, nights: v === '' ? 0 : Math.max(0, parseFloat(v) || 0) } });
                              }}
                              placeholder="0"
                              data-testid={`party-nights-${m.id}`}
                              className="h-10 text-center rounded-lg text-sm font-semibold mt-1"
                            />
                          </div>
                        </div>
                        {/* iter181 — Drinker toggle (default ON) */}
                        <label className="mt-2 flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={drinkerDraft[m.id] !== false}
                            onChange={(e) => setDrinkerDraft({ ...drinkerDraft, [m.id]: e.target.checked })}
                            data-testid={`drinker-toggle-${m.id}`}
                            className="w-4 h-4 accent-primary"
                          />
                          <span className="text-xs text-muted-foreground">
                            Drinker <span className="opacity-60">(included in Liquor/Bar splits)</span>
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 p-3 rounded-xl bg-primary/5 border border-primary/10 space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide flex items-center justify-between">
                    <span>Kid head weight</span>
                    <span className="tabular-nums font-bold text-primary">×{(parseFloat(kidWeightDraft) || 0).toFixed(2)}</span>
                  </Label>
                  <input
                    type="range" min="0" max="2" step="0.05"
                    value={kidWeightDraft}
                    onChange={(e) => setKidWeightDraft(parseFloat(e.target.value))}
                    data-testid="kid-head-weight-slider"
                    className="w-full accent-primary"
                  />
                  <p className="text-xs text-muted-foreground leading-snug">
                    1.00 = a kid counts as a full head (fairest for food). 0.50 = half (typical for buffet/hotel rooms). 0 = kids not counted.
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={handleSaveParty}
                  disabled={savingParty}
                  data-testid="save-party-btn"
                  className="w-full h-10 rounded-full text-xs font-bold uppercase tracking-wider mt-3"
                >
                  {savingParty ? 'Saving…' : 'Save Trip Party Settings'}
                </Button>
              </details>
            )}

            {/* iter181 — Trip-only: Hotel Rooms config (admin only) */}
            {isAdmin && (group?.group_type === 'trip') && (group?.members?.length > 0) && (
              <details className="mt-6 pt-4 border-t-2 border-primary/20 group" data-testid="trip-rooms-section">
                <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-primary py-1 list-none flex items-center justify-between">
                  <span>Trip · Hotel Rooms</span>
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                </summary>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Define rooms once, then pick a room when entering a Hotel expense to auto-split equally among its occupants.
                </p>

                <div className="mt-3 space-y-2" data-testid="rooms-list">
                  {roomsDraft.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-3">No rooms defined yet.</p>
                  )}
                  {roomsDraft.map((r, idx) => (
                    <div key={r.id} className="p-2.5 rounded-xl border border-border bg-card space-y-2" data-testid={`room-row-${r.id}`}>
                      <div className="flex items-center gap-2">
                        <Input
                          value={r.label}
                          onChange={(e) => renameRoom(r.id, e.target.value)}
                          placeholder={`Room ${idx + 1}`}
                          maxLength={50}
                          data-testid={`room-label-${r.id}`}
                          className="h-9 text-sm font-semibold rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeRoom(r.id)}
                          data-testid={`room-remove-${r.id}`}
                          className="w-9 h-9 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 flex items-center justify-center"
                          title="Remove room"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {(group?.members || []).map((m) => {
                          const checked = (r.occupant_user_ids || []).includes(m.id);
                          return (
                            <label key={m.id} className="flex items-center gap-1.5 cursor-pointer p-1.5 rounded-lg hover:bg-accent">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleRoomOccupant(r.id, m.id)}
                                data-testid={`room-occ-${r.id}-${m.id}`}
                                className="w-3.5 h-3.5 accent-primary"
                              />
                              <span className="text-xs truncate">{m.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  onClick={addRoom}
                  data-testid="add-room-btn"
                  variant="outline"
                  className="w-full h-9 rounded-full text-xs font-bold uppercase tracking-wider mt-2"
                >
                  + Add Room
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveRooms}
                  disabled={savingRooms}
                  data-testid="save-rooms-btn"
                  className="w-full h-10 rounded-full text-xs font-bold uppercase tracking-wider mt-2"
                >
                  {savingRooms ? 'Saving…' : 'Save Hotel Rooms'}
                </Button>
              </details>
            )}

            {/* iter171 — Trip-only: Trip Budget editor (admin only) */}
            {isAdmin && (group?.group_type === 'trip') && (
              <details className="mt-6 pt-4 border-t-2 border-primary/20 group">
                <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-primary py-1 list-none flex items-center justify-between">
                  <span>Trip · Budget</span>
                  <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                </summary>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Set how much you've earmarked for each category. Approved expenses are matched by their category and tallied against your budget on the Overview tab.
                </p>

                <div className="mt-3 space-y-2" data-testid="trip-budget-list">
                  {budgetDraft.map((b, idx) => (
                    <div key={idx} className="p-2.5 rounded-xl border border-border bg-card space-y-2" data-testid={`budget-row-${idx}`}>
                      <div className="flex items-center gap-2">
                        <Input
                          value={b.label}
                          onChange={(e) => setBudgetDraft(budgetDraft.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                          placeholder="Category label"
                          className="h-9 flex-1 text-xs rounded-lg"
                          data-testid={`budget-label-${idx}`}
                        />
                        <Input
                          type="number" min="0" step="100"
                          value={b.amount}
                          onChange={(e) => setBudgetDraft(budgetDraft.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                          placeholder="Amount"
                          className="h-9 w-24 text-xs tabular-nums text-right rounded-lg"
                          data-testid={`budget-amount-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => setBudgetDraft(budgetDraft.filter((_, i) => i !== idx))}
                          className="h-9 w-9 rounded-lg border border-border hover:bg-destructive/10 hover:border-destructive flex items-center justify-center text-muted-foreground hover:text-destructive transition"
                          data-testid={`budget-delete-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1" data-testid={`budget-cats-${idx}`}>
                        {['food', 'restaurant', 'groceries', 'transport', 'accommodation', 'entertainment', 'sports', 'nightlife', 'shopping', 'medical', 'utilities', 'miscellaneous'].map((cat) => {
                          const active = (b.expense_categories || []).includes(cat);
                          return (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => {
                                setBudgetDraft(budgetDraft.map((x, i) => {
                                  if (i !== idx) return x;
                                  const cats = active
                                    ? (x.expense_categories || []).filter((c) => c !== cat)
                                    : [...(x.expense_categories || []), cat];
                                  return { ...x, expense_categories: cats };
                                }));
                              }}
                              data-testid={`budget-cat-${idx}-${cat}`}
                              className={`text-xs px-2 py-0.5 rounded-full border transition ${active ? 'border-primary bg-primary/10 text-primary font-bold' : 'border-border text-muted-foreground hover:border-primary/40'}`}
                            >
                              {cat}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setBudgetDraft([...budgetDraft, { key: `custom_${budgetDraft.length}`, label: '', amount: 0, expense_categories: [] }])}
                  data-testid="add-budget-item-btn"
                  className="w-full h-9 mt-3 rounded-full text-xs font-bold"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add custom category
                </Button>

                <Button
                  type="button"
                  onClick={handleSaveBudget}
                  disabled={savingBudget}
                  data-testid="save-budget-btn"
                  className="w-full h-10 rounded-full text-xs font-bold uppercase tracking-wider mt-2"
                >
                  {savingBudget ? 'Saving…' : `Save Trip Budget (\u20b9${(budgetDraft.reduce((s, b) => s + (parseFloat(b.amount) || 0), 0)).toLocaleString('en-IN')})`}
                </Button>
              </details>
            )}

            {/* AI Co-Admin section (iter38) */}
            <div className="mt-6 pt-4 border-t-2 border-primary/20 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1">
                    <Bot className="w-3.5 h-3.5" /> AI Co-Admin
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Let the AI handle low-stakes approvals. Every action is reversible within the veto window.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={coAdminSettings.enabled}
                    onChange={(e) => setCoAdminSettings({ ...coAdminSettings, enabled: e.target.checked })}
                    data-testid="co-admin-enabled-switch"
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                </label>
              </div>

              {(
                <div className="space-y-3 bg-primary/5 rounded-xl p-3 border border-primary/10" data-testid="co-admin-details">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide">Auto-approve expenses up to (₹)</Label>
                    <Input
                      type="number" min="0"
                      value={coAdminSettings.auto_approve_max_amount}
                      onChange={(e) => setCoAdminSettings({ ...coAdminSettings, auto_approve_max_amount: parseFloat(e.target.value) || 0 })}
                      data-testid="co-admin-max-amount-input"
                      disabled={!coAdminSettings.enabled}
                      className="h-10 rounded-lg"
                    />
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={coAdminSettings.auto_approve_requires_clean}
                      onChange={(e) => setCoAdminSettings({ ...coAdminSettings, auto_approve_requires_clean: e.target.checked })}
                      data-testid="co-admin-requires-clean-switch"
                      disabled={!coAdminSettings.enabled}
                      className="mt-0.5"
                    />
                    <span className="text-xs">Skip auto-approve if AI Supervisor flagged the expense</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={coAdminSettings.auto_fine_late_cancel}
                      onChange={(e) => setCoAdminSettings({ ...coAdminSettings, auto_fine_late_cancel: e.target.checked })}
                      data-testid="co-admin-fine-late-cancel-switch"
                      disabled={!coAdminSettings.enabled}
                      className="mt-0.5"
                    />
                    <span className="text-xs">Auto-issue late-RSVP-cancel fines (vetoable)</span>
                  </label>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide">Veto window (hours)</Label>
                    <Input
                      type="number" min="1" max="168"
                      value={coAdminSettings.veto_window_hours}
                      onChange={(e) => setCoAdminSettings({ ...coAdminSettings, veto_window_hours: parseInt(e.target.value) || 24 })}
                      data-testid="co-admin-veto-hours-input"
                      disabled={!coAdminSettings.enabled}
                      className="h-10 rounded-lg"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleSaveCoAdmin}
                    disabled={savingCoAdmin}
                    data-testid="save-co-admin-btn"
                    className="w-full h-10 rounded-full text-xs font-bold uppercase tracking-wider"
                  >
                    {savingCoAdmin ? 'Saving…' : (coAdminSettings.enabled ? 'Save Co-Admin Settings' : 'Save (paused)')}
                  </Button>
                </div>
              )}

              {isAdmin && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setShowGroupSettings(false); setShowCoAdminDrawer(true); }}
                  data-testid="view-co-admin-actions-btn"
                  className="w-full h-10 rounded-full text-xs text-primary"
                >
                  View AI actions history →
                </Button>
              )}

              {isAdmin && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setShowGroupSettings(false); setShowTrashDrawer(true); fetchTrash(); }}
                  data-testid="view-trash-btn"
                  className="w-full h-10 rounded-full text-xs text-muted-foreground hover:text-foreground"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> View Trash (recoverable for 30 days) →
                </Button>
              )}

              {/* Member Trust tiers (iter40) */}
              {isAdmin && (
                <details className="mt-3 group" data-testid="member-trust-details">
                  <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground py-2 list-none flex items-center justify-between">
                    <span>Per-member trust ({Object.keys(coAdminSettings.member_trust || {}).length})</span>
                    <ChevronRight className="w-3.5 h-3.5 transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="text-xs text-muted-foreground mb-2 mt-1 leading-relaxed">
                    Multiplies the auto-approve threshold per payer. Strict 0.5× · Normal 1× · Trusted 1.5× · Very Trusted 2×.
                  </p>
                  <div className="space-y-1.5">
                    {(group?.members || []).map((m) => {
                      const tier = (coAdminSettings.member_trust || {})[m.id] || 'normal';
                      return (
                        <div key={m.id} className="flex items-center gap-2 py-1" data-testid={`trust-row-${m.id}`}>
                          <span className="text-xs flex-1 truncate">{m.name}</span>
                          <select
                            value={tier}
                            onChange={(e) => handleUpdateMemberTrust(m.id, e.target.value)}
                            data-testid={`trust-select-${m.id}`}
                            className="h-8 rounded-md border-2 text-xs px-2 bg-background"
                          >
                            <option value="strict">Strict 0.5×</option>
                            <option value="normal">Normal 1×</option>
                            <option value="trusted">Trusted 1.5×</option>
                            <option value="very_trusted">Very Trusted 2×</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}
            </div>

            {/* Danger zone */}
            <div className="mt-6 pt-4 border-t-2 border-destructive/20 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-destructive">Danger Zone</p>
              {group?.created_by !== user?.id && (
                <Button type="button" variant="outline" onClick={handleLeaveGroup} data-testid="leave-group-btn" className="w-full rounded-full border-destructive/40 text-destructive hover:bg-destructive/10">
                  Leave Group
                </Button>
              )}
              {group?.created_by === user?.id && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowGroupSettings(false); setShowDeleteConfirm(true); }}
                  data-testid="delete-group-from-settings-btn"
                  className="w-full rounded-full border-destructive/40 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Group
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter216 — Admin actions Sheet is now controlled by the "⚙️ Manage" chip in
          PrimaryActionBar. FAB (bottom-right "+") removed to avoid scroll-hunt. */}
      <AdminActionsFab
        isAdmin={isAdmin}
        fineDefaults={fineDefaults}
        importLoading={importLoading}
        open={showAdminSheet}
        onOpenChange={setShowAdminSheet}
        noFab
        ctx={{
          group,
          setShowFundModal,
          openFineWithType,
          setShowAllotCoins,
          setShowTransferModal,
          setShowGuestContribModal,
          setShowLoanToMemberModal,
          openQuickSettle,
          handleImportFile,
          handleImportFines,
          handleDownloadSample,
          setShowFineDefaultsModal,
          setShowBalancePolicyModal,
          setShowMembershipModal,
          handleResetGroup,
        }}
      />

      <MembershipFeeDialog
        open={showMembershipModal}
        onOpenChange={setShowMembershipModal}
        group={group}
        api={api}
        onSaved={() => { fetchGroup(); fetchFund(); }}
      />

      {/* iter94 — AI assistant Sheet (was a tab, now lives behind the header sparkle) */}
      <Sheet open={showAiSheet} onOpenChange={setShowAiSheet}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl px-0 pb-2 pt-3 max-h-[88vh] overflow-hidden flex flex-col"
          data-testid="group-ai-sheet"
        >
          <SheetHeader className="text-left px-5">
            <SheetTitle className="text-lg font-bold tracking-tight">AI assistant</SheetTitle>
            <p className="text-xs text-muted-foreground -mt-1">
              Ask anything about this group's expenses, fines, or balances.
            </p>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-2 pt-2">
            <AIChatPanel groupId={groupId} />
          </div>
        </SheetContent>
      </Sheet>

      {/* iter94 — Group menu Sheet (Analytics / Settings / Trash / Delete) */}
      <Sheet open={showGroupMenu} onOpenChange={setShowGroupMenu}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl px-5 pb-7 pt-4 max-h-[80vh] overflow-y-auto"
          data-testid="group-menu-sheet"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="text-lg font-bold tracking-tight">Group options</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-1 gap-2 mt-3">
            {/* iter189 — grouped sections: Share/Quick → Reports → Settings → Danger */}
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold pt-1 pb-0.5" data-testid="menu-section-quick">Quick actions</p>
            {/* iter127 — Share invite link as the first item — most-tapped utility */}
            <button
              data-testid="menu-share-invite"
              onClick={() => { setShowGroupMenu(false); invite.openShareMenu(group); }}
              className="w-full rounded-2xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
            >
              <UserPlus className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-semibold">Share invite link</p>
                <p className="text-xs text-muted-foreground">Anyone with the link can join</p>
              </div>
            </button>
            <button
              data-testid="menu-analytics"
              onClick={() => { setShowGroupMenu(false); navigate(`/groups/${groupId}/analytics`); }}
              className="w-full rounded-2xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
            >
              <BarChart3 className="w-5 h-5 text-primary" />
              <div>
                <p className="text-sm font-semibold">Analytics</p>
                <p className="text-xs text-muted-foreground">Charts, trends, top spenders</p>
              </div>
            </button>
            {(isAdmin || group?.group_type === 'trip') && (
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold pt-2 pb-0.5" data-testid="menu-section-reports">Reports &amp; admin</p>
            )}
            {isAdmin && (
              <button
                data-testid="menu-reconcile"
                onClick={() => { setShowGroupMenu(false); navigate(`/groups/${groupId}/reconcile`); }}
                className="w-full rounded-2xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <FileCheck className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold">Reconcile</p>
                  <p className="text-xs text-muted-foreground">Monthly close, drift checks</p>
                </div>
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="menu-audit-log"
                onClick={() => { setShowGroupMenu(false); openAuditLogs(); }}
                className="w-full rounded-2xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <ScrollText className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-sm font-semibold">Audit log</p>
                  <p className="text-xs text-muted-foreground">Every action, every actor</p>
                </div>
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="menu-settings"
                onClick={() => { setShowGroupMenu(false); openGroupSettings(); }}
                className="w-full rounded-2xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <Settings className="w-5 h-5 text-foreground/70" />
                <div>
                  <p className="text-sm font-semibold">Group settings</p>
                  <p className="text-xs text-muted-foreground">Name, members, branding, defaults</p>
                </div>
              </button>
            )}
            {(isAdmin && group?.group_type === 'trip') && (
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold pt-2 pb-0.5" data-testid="menu-section-trip">Trip tools</p>
            )}
            {group?.group_type === 'trip' && (
              <button
                data-testid="menu-trip-summary"
                onClick={() => {
                  setShowGroupMenu(false);
                  navigate(`/groups/${groupId}/trip-summary`);
                }}
                className="w-full rounded-2xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <BarChart3 className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-bold text-primary">Trip Summary 📊</p>
                  <p className="text-xs text-muted-foreground">All trip details in one glance · share via WhatsApp</p>
                </div>
              </button>
            )}
            {isAdmin && group?.group_type === 'trip' && (
              <button
                data-testid="menu-share-trip-view"
                onClick={() => {
                  setShowGroupMenu(false);
                  setShowTripViewLinkSheet(true);
                }}
                className="w-full rounded-2xl border-2 border-dashed border-violet-500/40 bg-violet-500/5 hover:bg-violet-500/10 active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <Link2 className="w-5 h-5 text-violet-500" />
                <div>
                  <p className="text-sm font-bold text-violet-700 dark:text-violet-300">Share read-only trip view</p>
                  <p className="text-xs text-muted-foreground">30-day magic link · no account needed</p>
                </div>
              </button>
            )}
            {isAdmin && group?.group_type === 'trip' && (
              <button
                data-testid="menu-export-trip-pdf"
                onClick={async () => {
                  setShowGroupMenu(false);
                  try {
                    const res = await api.get(`/groups/${groupId}/export/trip-pdf`, { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${(group?.name || 'trip').replace(/ /g, '_')}_trip_report.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('PDF report downloaded');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'PDF export failed');
                  }
                }}
                className="w-full rounded-2xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <Download className="w-5 h-5 text-foreground/70" />
                <div>
                  <p className="text-sm font-semibold">Export Trip Report (PDF)</p>
                  <p className="text-xs text-muted-foreground">Branded · budget, expenses, settlements</p>
                </div>
              </button>
            )}
            {isAdmin && group?.group_type === 'trip' && (
              <button
                data-testid="menu-export-trip-xlsx"
                onClick={async () => {
                  setShowGroupMenu(false);
                  try {
                    const res = await api.get(`/groups/${groupId}/export/trip-xlsx`, { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${(group?.name || 'trip').replace(/ /g, '_')}_trip_report.xlsx`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success('Excel report downloaded');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Excel export failed');
                  }
                }}
                className="w-full rounded-2xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <Download className="w-5 h-5 text-foreground/70" />
                <div>
                  <p className="text-sm font-semibold">Export Trip Report (Excel)</p>
                  <p className="text-xs text-muted-foreground">Multi-sheet · party, budget, members, settlements</p>
                </div>
              </button>
            )}
            {isAdmin && group?.group_type === 'trip' && (
              <button
                data-testid="menu-duplicate-trip"
                onClick={() => { setShowGroupMenu(false); openDuplicateModal(); }}
                className="w-full rounded-2xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <Copy className="w-5 h-5 text-foreground/70" />
                <div>
                  <p className="text-sm font-semibold">Duplicate this trip</p>
                  <p className="text-xs text-muted-foreground">New group for next trip — keeps members, party & rules; fresh fund</p>
                </div>
              </button>
            )}
            {isAdmin && (
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold pt-2 pb-0.5" data-testid="menu-section-danger">Danger zone</p>
            )}
            {/* iter193 — End-trip lives in Danger zone, but BEFORE Delete so
                admins reach for the safer (reversible) option first. Only
                shown on active trip groups. */}
            {isAdmin && group?.group_type === 'trip' && !group?.archived_at && (
              <button
                data-testid="menu-end-trip"
                onClick={() => { setShowGroupMenu(false); setShowEndTrip(true); }}
                className="w-full rounded-2xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-950/20 hover:bg-rose-100/60 dark:hover:bg-rose-900/30 active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <Flag className="w-5 h-5 text-rose-600" />
                <div>
                  <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">End trip</p>
                  <p className="text-xs text-rose-600/80 dark:text-rose-400/80">Freeze expenses · keep settlements open · auto-delete in 30 days</p>
                </div>
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="menu-trash"
                onClick={() => { setShowGroupMenu(false); setShowTrashDrawer(true); }}
                className="w-full rounded-2xl border border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left"
              >
                <Trash2 className="w-5 h-5 text-foreground/70" />
                <div>
                  <p className="text-sm font-semibold">Trash</p>
                  <p className="text-xs text-muted-foreground">Restore deleted items (30-day window)</p>
                </div>
              </button>
            )}
            {isAdmin && (
              <button
                data-testid="menu-delete-group"
                onClick={() => { setShowGroupMenu(false); setShowDeleteConfirm(true); }}
                className="w-full rounded-2xl border border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/10 active:scale-[0.99] transition px-4 py-3 flex items-center gap-3 text-left text-rose-600"
              >
                <Trash2 className="w-5 h-5" />
                <div>
                  <p className="text-sm font-semibold">Delete this group</p>
                  <p className="text-xs opacity-80">Permanent. All data wiped.</p>
                </div>
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* iter170 — Duplicate Trip Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="duplicate-trip-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Copy className="w-5 h-5 text-primary" />
              Duplicate this trip
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDuplicateGroup} className="space-y-4 mt-2">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Creates a new Trip group with the same members, party composition, and rules — but a fresh fund, zero balances, and no expenses.
            </p>
            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide">Which trip is this for?</Label>
              <Input
                value={duplicateForm.name}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, name: e.target.value })}
                data-testid="duplicate-name-input"
                className="h-12 border-2 rounded-xl font-semibold"
                placeholder="e.g. Manali 2027, Goa Reunion"
                maxLength={80}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-wide">Start date</Label>
                <Input
                  type="date"
                  value={duplicateForm.trip_start_date}
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, trip_start_date: e.target.value })}
                  data-testid="duplicate-start-input"
                  className="h-10 border-2 rounded-xl text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-wide">End date</Label>
                <Input
                  type="date"
                  value={duplicateForm.trip_end_date}
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, trip_end_date: e.target.value })}
                  data-testid="duplicate-end-input"
                  className="h-10 border-2 rounded-xl text-xs"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Dates are optional — you can set them later in Group Settings.</p>

            <div className="space-y-2">
              <Label className="text-sm font-semibold uppercase tracking-wide flex items-center justify-between">
                <span>Members to carry over</span>
                <span className="text-xs text-muted-foreground font-normal">
                  {duplicateForm.included_member_ids.length} of {group?.members?.length || 0}
                </span>
              </Label>
              <div className="space-y-1.5 max-h-48 overflow-y-auto p-1" data-testid="duplicate-member-list">
                {(group?.members || []).map((m) => {
                  const checked = duplicateForm.included_member_ids.includes(m.id);
                  const isYou = m.id === user?.id;
                  return (
                    <label
                      key={m.id}
                      className={`flex items-center gap-3 p-2.5 rounded-xl border-2 cursor-pointer transition ${checked ? 'border-primary/40 bg-primary/5' : 'border-border hover:border-primary/20'}`}
                      data-testid={`duplicate-member-row-${m.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={isYou}
                        onCheckedChange={() => {
                          if (isYou) return;
                          setDuplicateForm((f) => ({
                            ...f,
                            included_member_ids: checked
                              ? f.included_member_ids.filter((x) => x !== m.id)
                              : [...f.included_member_ids, m.id],
                          }));
                        }}
                        data-testid={`duplicate-member-${m.id}`}
                      />
                      <span className="flex-1 font-medium text-sm truncate">
                        {m.name} {isYou && <span className="text-xs text-muted-foreground font-normal">(you)</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <Button
              type="submit"
              disabled={duplicateBusy}
              data-testid="duplicate-submit-btn"
              className="w-full h-12 rounded-full font-bold uppercase tracking-wider"
            >
              {duplicateBusy ? 'Duplicating…' : 'Create Duplicate Trip'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter127 — universal Share Invite sheet (from header pill + ⋯ menu) */}
      <ShareInviteSheet
        open={invite.open}
        onOpenChange={invite.setOpen}
        group={invite.groupCtx}
        code={invite.code}
        loading={invite.loading}
      />

      {/* iter226 — Venue library manager */}
      <VenueManagerDialog
        open={showVenueManager}
        groupId={groupId}
        isAdmin={isAdmin}
        onClose={() => setShowVenueManager(false)}
        onChanged={() => { /* venues are re-fetched by AttendanceTab on next open */ }}
      />

      {/* iter228 — Payment rotation config */}
      <PaymentRotationDialog
        open={showPaymentRotation}
        groupId={groupId}
        group={group}
        isAdmin={isAdmin}
        onClose={() => setShowPaymentRotation(false)}
      />

      {/* iter231 — Contribution Request dialog for non-super-admins */}
      <ContributionRequestDialog
        open={showContribRequest}
        group={group}
        onClose={() => setShowContribRequest(false)}
        onSaved={() => { fetchGroup(); fetchFund(); }}
      />

      {/* iter232 — Cash Out Request dialog (member draws down their credit) */}
      <CashOutRequestDialog
        open={showCashOutRequest}
        group={group}
        maxAmount={ledgerData?.current_balance ?? 0}
        onClose={() => setShowCashOutRequest(false)}
        onSaved={() => {
          fetchGroup();
          fetchFund();
          // Refresh the currently-open member ledger too so the pending request is visible.
          if (ledgerForMember) openMemberLedger(ledgerForMember);
        }}
      />
    </div>
  );
}
